import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import JSZip from 'jszip';
import { marked } from "marked";
import DOMPurify from "dompurify";

import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { NOTE_CATEGORIES, DEFAULT_NOTE_CATEGORY, getNoteCategoryBadgeClasses } from "../../lib/common/noteCategories";
import {
    getSingleAssignment,
    addJobNote,
    updateJobNote,
    deleteJobNote,
    setImportantJobNote,
    getAvailableDesigners,
    getAvailableEstimators,
    getAvailableDrivers,
    assignDesigner,
    removeDesigner,
    assignEstimator,
    removeEstimator,
    assignDriver,
    removeDriver,
    confirmPackageReceipt,
    uploadDeliveryPhoto,
    deleteDeliveryPhoto,
    completeDelivery,
    completeDesign,
    completeEstimation,
    subscribeToJobUpdates,
    toggleQuoteSent,
    createNewDelivery,
    deleteJob,
    cancelJob,
    restoreCancelledJob,
    removeSelfAssignment,
    uploadJobFile
} from "../../lib/supabase/single-assignment";

import {
    FiArrowLeft, FiEdit3, FiDownload, FiCalendar, FiUser, FiPhone,
    FiMapPin, FiCheckCircle, FiClock, FiAlertCircle, FiFileText,
    FiHome, FiLayers, FiTool, FiDollarSign, FiFlag, FiUpload,
    FiCheck, FiX, FiEye, FiMail, FiTruck,
    FiPenTool, FiMap, FiSend, FiTrash2,
    FiDelete, FiPackage, FiActivity, FiBriefcase,
    FiFolder,
    FiFolderMinus,
    FiChevronDown,
    FiPlus,
    FiFolderPlus,
    FiMessageSquare,
    FiCopy
} from "react-icons/fi";



import { BsExclamationTriangle } from "react-icons/bs";

import { FiImage, FiFile, FiFilePdf } from "react-icons/fi";
import { Home, BrickWall, Code, FileArchive, FileAudio, FileSpreadsheet, FileVideo, Grid2X2, Loader, PanelsTopLeft, Info, SquareStack, PencilRuler, Receipt, Calendar, NotebookPen, User, Users, UserCog, DollarSign, Square, PartyPopper, PauseCircle } from "lucide-react";
import { FaRegFileWord, FaRegFilePdf } from "react-icons/fa6";

import DragDropUploader from "../common/DragDropUploader";
import FileViewer from '../common/FileViewer';
import { checkPermissions, hasPermission } from "../../lib/permissions";
import { sendInTransitNotificationSMS, sendDeliveredNotificationSMS, sendDriverSMS } from "../../lib/supabase/sms";
import { createNotification, createNotificationWithFallback, NOTIFICATION_TYPES, NOTIFICATION_PRIORITIES } from "../../lib/supabase/notifications";
import { notifyPricingEntered } from '../../lib/supabase/pricingNotifications';
import { BackButtonIconOnly } from "../common/BackButton";
import Toast from "../common/Toast";
import { getJobStatusSummary, getJobStatusSummarySingleQuote } from "../../lib/common/jobStatusText";
import EditAssignment from "../assignments/EditAssignment";
import Modal from "../popup/Modal";

import { GiWoodBeam, GiWoodPile } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import DropdownSection from "../common/DropdownSection";
import JobFilesEmailSender from "../common/JobFilesEmailSender";
import JobBookedDatesEmailSender from "../common/JobBookedDatesEmailSender";
import AssignmentConversation from "../assignments/AssignmentConversation";
import { getCustomerConversations, subscribeToCustomerConversations } from "../../lib/supabase/customerConversations";

import { getInvoiceSourceIcon, getInvoiceStatusSource, getInvoiceStatusText } from "../../lib/common/invoiceDisplay"
import XeroInvoicePdfModal from '../modals/XeroInvoicePdfModal';
import { uuidToShortId } from "../../lib/common/shortId";
import TextTooltip from "../common/TextTooltip";
import { useHolidays } from "../../hooks/useHolidays";
import EstimatorPricingForm from "../assignments/EstimatorPricingForm";
import PricingReviewDropdown from "../assignments/PricingReviewDropdown";
import FileSearchBar from "../common/FileSearchBar";
import FileOnlyDepartmentSection from "../assignments/FileOnlyDepartmentSection";
import {
    FILE_ONLY_QUOTE_TYPES,
    isFileOnlyQuoteType,
    getQuoteLabel,
    QUOTE_ORDER as SHARED_QUOTE_ORDER,
} from "../../lib/quoteTypes";
import ActivityLogsTable from "../assignments/ActivityLogsTable";





const normalizeConversationFiles = (files) => {
    if (!files) return [];

    const parsed = typeof files === 'string'
        ? (() => {
            try {
                return JSON.parse(files);
            } catch {
                return [];
            }
        })()
        : files;

    if (!Array.isArray(parsed)) return [];

    return parsed
        .filter(Boolean)
        .map((file, index) => ({
            ...file,
            id: file.id || `${file.filename || file.url || 'file'}-${index}`,
            filename: file.filename || file.url,
            original_name: file.original_name || file.filename || file.url || 'Attachment',
            uploaded_at: file.uploaded_at || file.created_at || null,
        }))
        .filter(file => !!file.filename);
};

const renderMarkdown = (text) => {
    const html = marked.parse(text || "", {
        breaks: true,
        gfm: true,
    });

    return DOMPurify.sanitize(html);
};

const CUSTOMER_TYPE_META = {
    Account: {
        label: "Account",
        badge: "bg-blue-50 text-blue-700 border-blue-200",
        card: "bg-blue-50 border-blue-200",
        icon: "bg-blue-100 text-blue-700",
    },
    COD: {
        label: "COD",
        badge: "bg-amber-50 text-amber-700 border-amber-200",
        card: "bg-amber-50 border-amber-200",
        icon: "bg-amber-100 text-amber-700",
    },
};

const CustomerNotesBox = ({ assignment }) => {
    const [showOtherNotes, setShowOtherNotes] = useState(false);

    const allNotes = Array.isArray(assignment?.customer_notes)
        ? assignment.customer_notes.filter(n => n?.note?.trim())
        : [];

    const currentNoteText = assignment?.customer_note?.trim() || "";

    const otherNotes = allNotes.filter(n => {
        if (!currentNoteText) return true;
        return n.note?.trim() !== currentNoteText;
    });

    const getFromText = (note) => {
        if (note?.email?.trim()) return note.email;
        if (note?.phone?.trim()) return note.phone;
        return "other customer record";
    };

    if (!currentNoteText && otherNotes.length === 0) return null;

    return (
        <div className="w-full space-y-3">
            {currentNoteText && (
                <div
                    className="
    prose prose-sm max-w-none
    prose-p:my-0 prose-ul:my-1 prose-ol:my-1 prose-li:my-0
    prose-li:leading-snug prose-li:marker:text-amber-400
    text-amber-950 bg-amber-50 border border-amber-300
    rounded-lg px-3 py-2 shadow-sm
"
                    dangerouslySetInnerHTML={{
                        __html: renderMarkdown(currentNoteText)
                    }}
                />
            )}

            {otherNotes.length > 0 && (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowOtherNotes(prev => !prev)}
                        className="text-xs font-semibold text-zinc-700 hover:text-zinc-900 underline"
                    >
                        {showOtherNotes
                            ? "Hide other customer notes"
                            : `Show other customer notes (${otherNotes.length})`}
                    </button>

                    {showOtherNotes && (
                        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {otherNotes.map((note, index) => (
                                <div
                                    key={note.id || index}
                                    className="relative bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm h-24 flex flex-col"
                                >
                                    <div
                                        className="
                                        prose prose-sm max-w-none
    prose-p:my-0 prose-ul:my-1 prose-ol:my-1 prose-li:my-0
    prose-li:leading-snug prose-li:marker:text-amber-400
    text-amber-950 bg-amber-50 border border-amber-300
    rounded-lg px-3 py-2 shadow-sm pt-4
                                        "
                                        dangerouslySetInnerHTML={{
                                            __html: renderMarkdown(note.note)
                                        }}
                                    />

                                    <div className="absolute left-6 top-3 text-[10px] text-gray-400 font-medium">
                                        From: {getFromText(note)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Defined at module scope (not inside SingleAssignment) so typing here only
// re-renders this small form, not the entire ~7800-line page tree.
const NoteSubmitButton = ({ onClick, disabled, className, children }) => (
    disabled ? (
        <TextTooltip text="This job is cancelled">
            <span className="inline-flex cursor-not-allowed">
                <button disabled className={`${className} opacity-50 cursor-not-allowed pointer-events-none`}>
                    {children}
                </button>
            </span>
        </TextTooltip>
    ) : (
        <button onClick={onClick} className={className}>
            {children}
        </button>
    )
);

const AddNoteForm = ({ onAdd, isJobCancelled }) => {
    const [content, setContent] = useState("");
    const [category, setCategory] = useState(DEFAULT_NOTE_CATEGORY);

    const handleAdd = () => {
        if (!content.trim()) return;
        onAdd(content.trim(), category);
        setContent("");
        setCategory(DEFAULT_NOTE_CATEGORY);
    };

    return (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Add New Note</label>
                <select
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                >
                    {NOTE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
                <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Add a note to this assignment..."
                    rows="3"
                />
                {content.trim() && (
                    <NoteSubmitButton
                        onClick={handleAdd}
                        disabled={isJobCancelled}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                        Add Note
                    </NoteSubmitButton>
                )}
            </div>
        </div>
    );
};

const EditNoteForm = ({ note, onSave, onCancel, isJobCancelled }) => {
    const [content, setContent] = useState(note.content);
    const [category, setCategory] = useState(note.category || DEFAULT_NOTE_CATEGORY);

    return (
        <div className="space-y-3">
            <select
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
            >
                {NOTE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                ))}
            </select>
            <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows="3"
            />
            <div className="flex gap-2">
                <NoteSubmitButton
                    onClick={() => onSave(note.id, content, category)}
                    disabled={isJobCancelled}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                    Save Changes
                </NoteSubmitButton>
                <NoteSubmitButton
                    onClick={onCancel}
                    disabled={isJobCancelled}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                >
                    Cancel
                </NoteSubmitButton>
            </div>
        </div>
    );
};

const SingleAssignment = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const holidayMap = useHolidays(company?.state);
    const { companySlug, jobId } = useParams();

    const [editOpen, setEditOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [assignment, setAssignment] = useState(null);
    const [actionLogs, setActionLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);

    const [showDesignerModal, setShowDesignerModal] = useState(false);
    const [showEstimatorModal, setShowEstimatorModal] = useState(false);
    const [selectedQuoteType, setSelectedQuoteType] = useState(null);
    const [selectedChildQuoteId, setSelectedChildQuoteId] = useState(null);
    const [selectedChildQuoteName, setSelectedChildQuoteName] = useState(null);
    const [selectedDesignerId, setSelectedDesignerId] = useState('');
    const [selectedEstimatorId, setSelectedEstimatorId] = useState('');
    const [availableDesigners, setAvailableDesigners] = useState([]);
    const [availableEstimators, setAvailableEstimators] = useState([]);
    const [userAvatarMap, setUserAvatarMap] = useState({});

    const [uploadedFiles, setUploadedFiles] = useState([]);

    const [driverFilesToUpload, setDriverFilesToUpload] = useState([]);
    const [clearDriverFiles, setClearDriverFiles] = useState(false);
    const [isUploadingDriverFiles, setIsUploadingDriverFiles] = useState(false);

    const [isSubmittingCompletion, setIsSubmittingCompletion] = useState(false);

    const [isAssigningDesigner, setIsAssigningDesigner] = useState(false);
    const [clearPdfs, setClearPdfs] = useState(false);
    const [completionNotes, setCompletionNotes] = useState({
        roof: '',
        wall: '',
        floor: '',
        estimation: ''
    });
    const [quoteDeliveryNotes, setQuoteDeliveryNotes] = useState({
        roof: '',
        wall: '',
        floor: '',
        estimation: ''
    });

    // Driver delivery states - keyed by "quoteType-deliveryCount"
    const [uploadedDeliveryPhotos, setUploadedDeliveryPhotos] = useState({});
    const [clearDeliveryPhotos, setClearDeliveryPhotos] = useState({});
    const [deliveryNotes, setDeliveryNotes] = useState({});
    const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);

    const [showDriverModal, setShowDriverModal] = useState(false);
    const [selectedQuoteTypeForDriver, setSelectedQuoteTypeForDriver] = useState(null);
    const [selectedDeliveryCount, setSelectedDeliveryCount] = useState(0);
    const [selectedChildQuoteIdForDriver, setSelectedChildQuoteIdForDriver] = useState(null);
    const [selectedChildQuoteNameForDriver, setSelectedChildQuoteNameForDriver] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [availableDrivers, setAvailableDrivers] = useState([]);

    // File viewer states
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [viewerFiles, setViewerFiles] = useState([]);
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);

    const [fileSearch, setFileSearch] = useState("");
    const filterFiles = useCallback((files = []) => {
        const query = fileSearch.trim().toLowerCase();

        if (!query) return files;

        return files.filter(file => {
            const searchable = [
                file.original_name,
                file.filename,
                file.file_type,
                file.quote_type,
                file.description,
                file.folder_name,
                file.uploaded_by_name,
                file.uploaded_by_role,
                file.sent_by_name,
                file.sent_by_role,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return searchable.includes(query);
        });
    }, [fileSearch]);

    // Quote sent state
    const [isTogglingQuoteSent, setIsTogglingQuoteSent] = useState(false);

    // Delete Job Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingJob, setIsDeletingJob] = useState(false);
    const isNavigatingAway = useRef(false);

    // Cancel Job Modal states
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelConfirmText, setCancelConfirmText] = useState('');
    const [isCancellingJob, setIsCancellingJob] = useState(false);

    // New Delivery Modal states
    const [showNewDeliveryModal, setShowNewDeliveryModal] = useState(false);
    const [showConversationModal, setShowConversationModal] = useState(false);

    const [xeroPdfModal, setXeroPdfModal] = useState({
        open: false,
        loading: false,
        error: null,
        pdfUrl: null,
        title: '',
    });

    const [pricingValues, setPricingValues] = useState({});
    const [estimatorPricingEntries, setEstimatorPricingEntries] = useState([]);

    const [customerConversations, setCustomerConversations] = useState([]);
    const [newDeliveryQuoteType, setNewDeliveryQuoteType] = useState(null);
    const [newDeliveryNote, setNewDeliveryNote] = useState('');
    const [newDeliveryTimeSlot, setNewDeliveryTimeSlot] = useState(null);
    const [newDeliveryScheduledDate, setNewDeliveryScheduledDate] = useState('');
    const [newDeliverySelectedUnits, setNewDeliverySelectedUnits] = useState([]);

    const [openFolders, setOpenFolders] = useState({});
    const [openQuoteFolders, setOpenQuoteFolders] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);

    // Custom folder + per-department upload states
    const [showFolderInput, setShowFolderInput] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [folderUploading, setFolderUploading] = useState(null); // upload key while uploading
    const [dragOverFolder, setDragOverFolder] = useState(null);
    const fileUploadInputRef = useRef(null);
    const uploadContext = useRef({ fileType: null, quoteType: null, folderName: null, isCustom: false });

    // Tracks whether estimator pricing was already fully complete before the
    // current save — used by saveEstimationPricingEntries() below to detect
    // the false -> true completeness transition and fire notifyPricingEntered
    // exactly once. Recomputed (see effect near hasAllRequiredPricingValues)
    // each time a new assignment is loaded, establishing the pre-edit
    // baseline for that assignment/session.
    const wasPricingCompleteRef = useRef(false);

    const isTouchDevice = 'ontouchstart' in window;

    const QUOTE_ORDER = SHARED_QUOTE_ORDER;

    // Remove Designer confirmation modal
    const [removeDesignerModal, setRemoveDesignerModal] = useState({
        open: false,
        quoteType: null,
        designerId: null,
        childQuoteId: null,
        childQuoteName: null,
        designerName: '',
        isCompleted: false,
        keepCompleted: true,
    });

    // Confirm Remove Driver modal (replaces window.confirm — iOS suppresses window.confirm)
    const [confirmRemoveDriverModal, setConfirmRemoveDriverModal] = useState({
        open: false, quoteType: null, deliveryCount: 0, childQuoteId: null
    });

    // Confirm End Delivery modal (replaces window.confirm — iOS suppresses window.confirm)
    const [confirmEndDeliveryModal, setConfirmEndDeliveryModal] = useState({
        open: false, quoteType: null, deliveryCount: 0
    });

    const fetchAssignment = async () => {
        // Don't fetch if we're in the process of deleting or navigating away
        if (isDeletingJob || isNavigatingAway.current) return;

        try {
            const data = await getSingleAssignment(jobId, company.id);
            setAssignment(data);

            const sortedLogs = (data.job_action_logs || []).sort((a, b) =>
                new Date(b.timestamp) - new Date(a.timestamp)
            );
            setActionLogs(sortedLogs);
        } catch (error) {
            console.error("Error fetching assignment:", error);

            // Only show error and redirect if not currently deleting or navigating
            if (!isDeletingJob && !isNavigatingAway.current) {
                Toast.error("Assignment not found or has been deleted", {
                    title: 'Error',
                    position: 'bottom-right',
                    autoClose: 3000
                });
                navigate(`/${companySlug}/assignment/list`);
            }
        }
    };

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (company?.id && jobId && !isDeletingJob) {
            fetchAssignment();
        }
    }, [company, jobId]);

    useEffect(() => {
        if (!company?.id) return;
        supabase
            .from('users')
            .select('id, avatar_url')
            .eq('company_id', company.id)
            .then(({ data }) => {
                if (data) {
                    const map = {};
                    data.forEach(u => { map[u.id] = u.avatar_url || null; });
                    setUserAvatarMap(map);
                }
            });
    }, [company?.id]);

    // Real-time subscription
    useEffect(() => {
        if (!assignment?.id) return;

        const channel = subscribeToJobUpdates(assignment.id, (payload) => {
            fetchAssignment();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [assignment?.id]);

    useEffect(() => {
        if (!company?.id || !assignment?.id) return;

        let active = true;
        let reloadTimeout = null;

        const loadCustomerConversations = async () => {
            try {
                const data = await getCustomerConversations(company.id, assignment.id);

                if (!active) return;

                setCustomerConversations(data || []);
            } catch (error) {
                console.error("Error loading customer conversations:", error);
            }
        };

        const scheduleReload = () => {
            clearTimeout(reloadTimeout);
            reloadTimeout = setTimeout(loadCustomerConversations, 250);
        };

        loadCustomerConversations();

        const channel = supabase
            .channel(`single-assignment-customer-conversations-${assignment.id}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "customer_conversations",
                    filter: `job_id=eq.${assignment.id}`,
                },
                (payload) => {
                    const row = payload.new || payload.old;

                    if (row?.company_id !== company.id) return;

                    // Important: UPDATE catches files added after the inbound email is inserted
                    scheduleReload();
                }
            )
            .subscribe();

        return () => {
            active = false;
            clearTimeout(reloadTimeout);
            supabase.removeChannel(channel);
        };
    }, [company?.id, assignment?.id]);

    // Keyboard navigation for file viewer
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!fileViewerOpen) return;

            if (e.key === 'Escape') {
                setFileViewerOpen(false);
            } else if (e.key === 'ArrowLeft' && selectedFileIndex > 0) {
                setSelectedFileIndex(prev => prev - 1);
                setSelectedFile(viewerFiles[selectedFileIndex - 1]);
            } else if (e.key === 'ArrowRight' && selectedFileIndex < viewerFiles.length - 1) {
                setSelectedFileIndex(prev => prev + 1);
                setSelectedFile(viewerFiles[selectedFileIndex + 1]);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [fileViewerOpen, selectedFileIndex, viewerFiles]);


    useEffect(() => {
        if (!assignment?.id || !company?.id) return;

        const loadEstimatorPricingEntries = async () => {
            const { data, error } = await supabase
                .from("job_pricing_entries")
                .select("*")
                .eq("company_id", company.id)
                .eq("job_id", assignment.id)
                .eq("type", "estimation")
                .order("unit_number", { ascending: true });

            if (error) {
                console.error("Failed to load estimator pricing entries:", error);
                return;
            }

            setEstimatorPricingEntries(data || []);
        };

        loadEstimatorPricingEntries();
    }, [assignment?.id, company?.id]);


    const patchXeroQuoteUpdate = (payload) => {
        const row = payload?.new;

        if (!row?.id) return false;

        const xeroFields = [
            'xero_invoice_status',
            'xero_invoice_id',
            'xero_invoice_number',
            'xero_last_synced_at',
        ];

        const changedKeys = Object.keys(row || {});
        const isProbablyXeroUpdate = xeroFields.some((field) =>
            changedKeys.includes(field)
        );

        if (!isProbablyXeroUpdate) return false;

        setAssignment((prev) => {
            if (!prev?.job_quotes?.length) return prev;

            let changed = false;

            const nextQuotes = prev.job_quotes.map((quote) => {
                if (quote.id !== row.id) return quote;

                changed = true;

                return {
                    ...quote,
                    xero_invoice_status: row.xero_invoice_status,
                    xero_invoice_id: row.xero_invoice_id,
                    xero_invoice_number: row.xero_invoice_number,
                    xero_last_synced_at: row.xero_last_synced_at,
                };
            });

            if (!changed) return prev;

            return {
                ...prev,
                job_quotes: nextQuotes,
            };
        });

        return true;
    };

    const openXeroInvoicePdf = async (quote) => {
        if (!quote?.xero_invoice_id) return;

        try {
            setXeroPdfModal({
                open: true,
                loading: true,
                error: null,
                pdfUrl: null,
                title: quote?.xero_invoice_number || quote?.child_unique_id || 'Xero Invoice',
            });

            const res = await fetch(
                `https://xgezccwtibbfoplffzcr.supabase.co/functions/v1/xero-invoice-pdf?invoice_id=${quote.xero_invoice_id}&company_id=${company.id}`
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to load Xero invoice PDF');
            }

            const blob = await res.blob();
            const pdfUrl = URL.createObjectURL(blob);

            setXeroPdfModal(prev => ({
                ...prev,
                loading: false,
                pdfUrl,
            }));
        } catch (err) {
            setXeroPdfModal(prev => ({
                ...prev,
                loading: false,
                error: err.message || 'Failed to load invoice PDF',
            }));
        }
    };

    const closeXeroPdfModal = () => {
        if (xeroPdfModal.pdfUrl) {
            URL.revokeObjectURL(xeroPdfModal.pdfUrl);
        }

        setXeroPdfModal({
            open: false,
            loading: false,
            error: null,
            pdfUrl: null,
            title: '',
        });
    };

    const canUserModifyNote = (note) => {
        return note.added_by_user_id === userProfile?.id || userProfile?.role === 'admin';
    };

    const handleAddNote = async (content, category) => {
        if (!content?.trim()) return;

        try {
            await addJobNote(
                assignment.id,
                content.trim(),
                userProfile.id,
                userProfile.username || userProfile.full_name,
                userProfile.role,
                null,
                category
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error adding note:", error);
            alert("Error adding note");
        }
    };

    const startEditNote = (noteId) => {
        setEditingNoteId(noteId);
    };

    const cancelEditNote = () => {
        setEditingNoteId(null);
    };

    const [fileOnlyDepartmentFiles, setFileOnlyDepartmentFiles] = useState({});

    const handleAdditionalDepartmentFilesChange = useCallback((quoteType, files) => {
        setFileOnlyDepartmentFiles(prev => ({
            ...prev,
            [quoteType]: files,
        }));
    }, []);

    const handleDriverFilesChange = useCallback((files) => {
        const nextFiles = files || [];

        setDriverFilesToUpload(currentItems => {
            return nextFiles.map(file => {
                const existingItem = currentItems.find(item =>
                    item.file.name === file.name &&
                    item.file.size === file.size &&
                    item.file.lastModified === file.lastModified
                );

                return existingItem || {
                    file,
                    childId: "",
                };
            });
        });
    }, []);

    const handleDriverFileChildChange = useCallback((fileIndex, childId) => {
        setDriverFilesToUpload(currentItems =>
            currentItems.map((item, index) =>
                index === fileIndex
                    ? {
                        ...item,
                        childId,
                    }
                    : item
            )
        );
    }, []);

    const handleCompleteFileOnlyDepartment = async (quote, note = "") => {
        const files = fileOnlyDepartmentFiles[quote.quote_type] || [];

        if (!files.length) {
            alert(`Please upload at least one file for ${getQuoteLabel(quote.quote_type)}`);
            return;
        }

        if (!window.confirm(`Complete ${getQuoteLabel(quote.quote_type)} files?`)) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            for (const file of files) {
                await uploadJobFile(
                    assignment.id,
                    file,
                    "initial",
                    quote.quote_type,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug || company?.slug,
                    quote.id
                );
            }

            const { error } = await supabase
                .from("job_quotes")
                .update({
                    department_file_status: "completed",
                    department_files_completed_at: new Date().toISOString(),
                    department_files_completed_by: userProfile.id,
                    department_files_completed_by_name: userProfile.username || userProfile.full_name,
                    department_files_completion_note: note?.trim() || null,
                })
                .eq("id", quote.id);

            if (error) throw error;

            setFileOnlyDepartmentFiles(prev => ({
                ...prev,
                [quote.quote_type]: [],
            }));

            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            Toast.success(`${getQuoteLabel(quote.quote_type)} completed`, {
                title: "Completed",
                position: "bottom-right",
                autoClose: 3000,
            });

            fetchAssignment();
        } catch (error) {
            console.error("Error completing file-only department:", error);
            Toast.error(error.message || "Failed to complete department files");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handleSaveEditNote = async (noteId, content, category) => {
        if (!content?.trim()) return;

        try {
            await updateJobNote(noteId, content.trim(), userProfile.id, category);
            cancelEditNote();
            fetchAssignment();
        } catch (error) {
            console.error("Error updating note:", error);
            alert(error.message || "Error updating note");
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm("Are you sure you want to delete this note? This action cannot be undone.")) {
            return;
        }

        try {
            await deleteJobNote(noteId, userProfile.id, userProfile.role);
            fetchAssignment();
        } catch (error) {
            console.error("Error deleting note:", error);
            alert(error.message || "Error deleting note");
        }
    };

    const handleSetImportant = async (noteId, currentIsImportant) => {
        try {
            await setImportantJobNote(noteId, !currentIsImportant);
            fetchAssignment();
        } catch (error) {
            console.error("Error updating note importance:", error);
            alert(error.message || "Error updating note importance");
        }
    };

    const handleDeleteJob = async () => {
        if (deleteConfirmText !== assignment.unique_id) {
            return;
        }

        setIsDeletingJob(true);
        isNavigatingAway.current = true;

        try {
            await deleteJob(assignment.id, company.id, userProfile);

            // Close modal
            setShowDeleteModal(false);
            setDeleteConfirmText('');

            Toast.success('Job deleted successfully', {
                title: 'Deleted',
                position: 'bottom-right',
                autoClose: 3000
            });

            // Navigate to assignment list
            navigate(`/${companySlug}/assignment/list`);
        } catch (error) {
            console.error('Error deleting job:', error);
            Toast.error(error.message || 'Failed to delete job', {
                title: 'Error',
                position: 'bottom-right',
                autoClose: 5000
            });
            setIsDeletingJob(false);
        }
    };

    const handleCancelJob = async () => {
        if (cancelConfirmText !== assignment.unique_id) return;

        setIsCancellingJob(true);

        try {
            await cancelJob(assignment.id, company.id, userProfile);

            setShowCancelModal(false);
            setCancelConfirmText('');

            Toast.success('Job cancelled successfully', {
                title: 'Cancelled',
                position: 'bottom-right',
                autoClose: 3000
            });

            fetchAssignment();
        } catch (error) {
            console.error('Error cancelling job:', error);
            Toast.error(error.message || 'Failed to cancel job', {
                title: 'Error',
                position: 'bottom-right',
                autoClose: 5000
            });
        } finally {
            setIsCancellingJob(false);
        }
    };

    const handleRestoreCancelledJob = async () => {
        setIsCancellingJob(true);

        try {
            await restoreCancelledJob(assignment.id, company.id, userProfile);

            Toast.success('Job restored successfully', {
                title: 'Restored',
                position: 'bottom-right',
                autoClose: 3000
            });

            fetchAssignment();
        } catch (error) {
            console.error('Error restoring job:', error);
            Toast.error(error.message || 'Failed to restore job', {
                title: 'Error',
                position: 'bottom-right',
                autoClose: 5000
            });
        } finally {
            setIsCancellingJob(false);
        }
    };

    const openAssignDesignerModal = async (quoteType, childQuoteId = null, childQuoteName = null) => {
        setSelectedQuoteType(quoteType);
        setSelectedChildQuoteId(childQuoteId);
        setSelectedChildQuoteName(childQuoteName);
        try {
            const designers = await getAvailableDesigners(company.id);
            setAvailableDesigners(designers);
            setShowDesignerModal(true);
        } catch (error) {
            console.error("Error fetching designers:", error);
            alert("Error loading designers");
        }
    };

    const openAssignEstimatorModal = async () => {
        try {
            const allowAnyUserAsEstimator =
                company?.settings?.allow_any_user_to_act_as_estimator === true;


            const estimators = await getAvailableEstimators(company.id, allowAnyUserAsEstimator);
            setAvailableEstimators(estimators);
            setShowEstimatorModal(true);
        } catch (error) {
            console.error("Error fetching estimators:", error);
            alert("Error loading estimators");
        }
    };

    const openAssignDriverModal = async (quoteType, deliveryCount = 0, childQuoteId = null, childQuoteName = null) => {
        setSelectedQuoteTypeForDriver(quoteType);
        setSelectedDeliveryCount(deliveryCount);
        setSelectedChildQuoteIdForDriver(childQuoteId);
        setSelectedChildQuoteNameForDriver(childQuoteName);
        try {
            const drivers = await getAvailableDrivers(company.id);
            setAvailableDrivers(drivers);
            setShowDriverModal(true);
        } catch (error) {
            console.error("Error fetching drivers:", error);
            alert("Error loading drivers");
        }
    };

    const handleAssignDriver = async () => {
        if (!selectedDriverId || !selectedQuoteTypeForDriver) return;

        try {
            // Parse the selected driver ID (format: "id_name")
            const [driverId, driverName] = selectedDriverId.split('_');

            const assignedDelivery = await assignDriver(
                assignment.id,
                selectedQuoteTypeForDriver,
                driverId,
                driverName,
                selectedDeliveryCount,
                selectedChildQuoteIdForDriver,
            );

            // await sendDriverSMS(
            //     assignment,
            //     company,
            //     driverId,
            //     selectedQuoteTypeForDriver,
            //     assignedDelivery?.id
            // );

            const deliveryShortId = assignedDelivery?.id
                ? uuidToShortId(assignedDelivery.id)
                : null;

            const label = selectedChildQuoteNameForDriver || assignment.unique_id;
            await createNotificationWithFallback(
                {
                    user_id: driverId,
                    company_id: company.id,
                    type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                    title: "New Delivery Assignment",
                    message: `Assignment ${label} (${selectedQuoteTypeForDriver}) assigned to you by ${userProfile.full_name || userProfile.username}`,
                    priority: NOTIFICATION_PRIORITIES.NORMAL,
                    related_type: "delivery",
                    related_id: assignedDelivery?.id || assignment.id,
                    action_url: deliveryShortId
                        ? `/${companySlug}/assignment/${deliveryShortId}`
                        : `/${companySlug}/assignment/${assignment.id}`,
                    metadata: {
                        job_id: assignment.id,
                        delivery_id: assignedDelivery?.id || null,
                        delivery_short_id: deliveryShortId,
                        quote_type: selectedQuoteTypeForDriver,
                        quote_id: selectedChildQuoteIdForDriver,
                        delivery_count: selectedDeliveryCount,
                        quote_name: label
                    }
                },
                async () => {
                    await sendDriverSMS(
                        assignment,
                        company,
                        driverId,
                        selectedQuoteTypeForDriver,
                        assignedDelivery?.id
                    );
                }
            );
            // const notificationData = {
            //     user_id: driverId,
            //     company_id: company.id,
            //     type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
            //     title: "New Delivery Assignment",
            //     message: `Assignment ${label} (${selectedQuoteTypeForDriver}) assigned to you by ${userProfile.full_name || userProfile.username}`,
            //     priority: "normal",
            //     related_type: "assignment",
            //     related_id: assignment.id,
            //     action_url: `${companySlug}/assignment/${assignment.id}`
            // };

            // await createNotification(notificationData);

            setShowDriverModal(false);
            setSelectedDriverId('');
            setSelectedQuoteTypeForDriver(null);
            setSelectedChildQuoteIdForDriver(null);
            setSelectedChildQuoteNameForDriver(null);
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning driver:", error);
            alert(error.message || "Error assigning driver");
        }
    };

    // childQuoteId is the job_quotes UUID — null for old single-quote jobs
    // Opens modal instead of window.confirm (window.confirm is silently suppressed on iOS in-app browsers)
    const handleRemoveDriver = (quoteType, deliveryCount = 0, childQuoteId = null) => {
        setConfirmRemoveDriverModal({ open: true, quoteType, deliveryCount, childQuoteId });
    };

    const handleRemoveDriverConfirmed = async () => {
        const { quoteType, deliveryCount, childQuoteId } = confirmRemoveDriverModal;
        setConfirmRemoveDriverModal(prev => ({ ...prev, open: false }));
        try {
            await removeDriver(assignment.id, quoteType, deliveryCount, childQuoteId);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing driver:", error);
            Toast.error(error.message || "Error removing driver");
        }
    };

    const handleConfirmPackage = async (quoteType, deliveryCount = 0, childId = null) => {
        try {
            await confirmPackageReceipt(assignment.id, quoteType, deliveryCount, childId);
            // Get the expected delivery date for this specific quote
            const quote = assignment?.job_quotes?.find(q => q.quote_type === quoteType);
            const deliveryDate = quote?.expected_delivery_date || null;
            await sendInTransitNotificationSMS(assignment?.customer_number, assignment?.customer_name, assignment?.short_id, assignment?.id, deliveryDate, company, assignment?.unique_id) // phoneNumber, customerName, shortId, jobId, deliveryDate, company, jobName
            fetchAssignment();
        } catch (error) {
            console.error("Error confirming package:", error);
            alert(error.message || "Error confirming package receipt");
        }
    };

    // Opens modal instead of window.confirm (window.confirm is silently suppressed on iOS in-app browsers)
    const handleEndDelivery = (quoteType, deliveryCount = 0, childId = null) => {
        setConfirmEndDeliveryModal({ open: true, quoteType, deliveryCount, childId });
    };

    const handleEndDeliveryConfirmed = async () => {
        const { quoteType, deliveryCount, childId } = confirmEndDeliveryModal;
        setConfirmEndDeliveryModal(prev => ({ ...prev, open: false }));
        try {
            const deliveryKey = `${quoteType}-${deliveryCount}-${childId || ''}`;
            await completeDelivery(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                deliveryNotes[deliveryKey] || '',
                deliveryCount,
                childId || null
            );

            const quote = assignment?.job_quotes?.find(q => q.quote_type === quoteType);
            const deliveryDate = quote?.expected_delivery_date || null;

            await sendDeliveredNotificationSMS(assignment?.customer_number, assignment?.customer_name, assignment?.short_id, assignment?.id, deliveryDate, company, assignment?.unique_id);
            fetchAssignment();
        } catch (error) {
            console.error("Error completing delivery:", error);
            Toast.error(error.message || "Error completing delivery");
        }
    };

    const handleUploadDeliveryPhotos = async (quoteType, deliveryCount, childId = null) => {
        const deliveryKey = `${quoteType}-${deliveryCount}-${childId || ''}`;
        if (!uploadedDeliveryPhotos[deliveryKey] || uploadedDeliveryPhotos[deliveryKey].length === 0) {
            alert("Please select at least one photo to upload");
            return;
        }

        setIsSubmittingDelivery(true);

        try {
            const photoFiles = uploadedDeliveryPhotos[deliveryKey];
            const uploadedFileRecords = [];

            // Upload each photo
            for (const file of photoFiles) {
                const fileRecord = await uploadDeliveryPhoto(
                    assignment.id,
                    file,
                    quoteType,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug,
                    deliveryCount,
                    childId
                );
                uploadedFileRecords.push(fileRecord);
            }

            // Update delivery notes if provided
            if (deliveryNotes[deliveryKey]?.trim()) {
                let query = supabase
                    .from('job_deliveries')
                    .update({
                        delivery_notes: deliveryNotes[deliveryKey].trim(),
                        note_updated_at: new Date().toISOString(),
                        note_updated_by_user_id: userProfile.id,
                        note_updated_by_name: userProfile.username || userProfile.full_name
                    })
                    .eq('job_id', assignment.id)
                    .eq('quote_type', quoteType);

                if (childId) {
                    query = query.eq('child_unique_id', childId);
                } else {
                    query = query.is('child_unique_id', null);
                    if (deliveryCount === 0) {
                        query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
                    } else {
                        query = query.eq('delivery_count', deliveryCount);
                    }
                }

                const { error: notesError } = await query;
                if (notesError) throw notesError;
            }

            // Clear the upload state
            setUploadedDeliveryPhotos(prev => ({
                ...prev,
                [deliveryKey]: []
            }));

            setClearDeliveryPhotos(prev => ({
                ...prev,
                [deliveryKey]: true
            }));

            setTimeout(() => {
                setClearDeliveryPhotos(prev => ({
                    ...prev,
                    [deliveryKey]: false
                }));
            }, 100);

            alert(`Successfully uploaded ${uploadedFileRecords.length} photo(s)`);
            fetchAssignment();
        } catch (error) {
            console.error("Error uploading delivery photos:", error);
            alert(error.message || "Error uploading delivery photos");
        } finally {
            setIsSubmittingDelivery(false);
        }
    };

    // Stable reference via useCallback — prevents DragDropUploader's useEffect from
    // re-firing on every parent render (which would re-call onFilesChange and cause
    // uploadedDeliveryPhotos to flicker, momentarily disabling the Upload button)
    const handleDeleteDeliveryPhoto = async (fileId, filename) => {
        try {
            await deleteDeliveryPhoto(fileId, filename);
            // Remove from local assignment state so UI updates immediately
            setAssignment(prev => ({
                ...prev,
                job_files: (prev.job_files || []).filter(f => f.id !== fileId)
            }));
        } catch (error) {
            console.error('Error deleting photo:', error);
            Toast.error('Failed to delete photo');
        }
    };

    const handleDeliveryPhotosChange = useCallback((quoteType, deliveryCount, childId = null) => (files) => {
        const deliveryKey = `${quoteType}-${deliveryCount}-${childId || ''}`;
        setUploadedDeliveryPhotos(prev => ({
            ...prev,
            [deliveryKey]: files
        }));
    }, []);

    const handleAssignDesigner = async () => {
        if (!selectedDesignerId || !selectedQuoteType || isAssigningDesigner) return;

        setIsAssigningDesigner(true);
        try {
            const designer = availableDesigners.find(d => d.id === selectedDesignerId);
            await assignDesigner(
                assignment.id,
                selectedQuoteType,
                selectedDesignerId,
                designer.username || designer.full_name,
                selectedChildQuoteId || null,
                selectedChildQuoteName || null
            );

            let url = `${companySlug}/assignment/${assignment.id}`;
            if (selectedChildQuoteId) url += `/${selectedChildQuoteId}`;

            const notificationData = {
                user_id: selectedDesignerId,
                company_id: company.id,
                type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                title: "New Assignment",
                message: `Assignment ${selectedChildQuoteName || assignment.unique_id} (${selectedQuoteType}) assigned to you by ${userProfile.full_name || userProfile.username}`,
                priority: "normal",
                related_type: "assignment",
                related_id: assignment.id,
                action_url: url
            }

            await createNotification(notificationData)

            setShowDesignerModal(false);
            setSelectedDesignerId('');
            setSelectedQuoteType(null);
            setSelectedChildQuoteId(null);
            setSelectedChildQuoteName(null);
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning designer:", error);
            alert(error.message || "Error assigning designer");
        } finally {
            setIsAssigningDesigner(false);
        }
    };

    const handleSelfAssignDesigner = async (quoteType, childId, childName) => {
        if (isAssigningDesigner) return;
        setIsAssigningDesigner(true);
        try {
            await assignDesigner(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                childId || null,
                childName || null
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error self-assigning:", error);
            alert(error.message || "Error assigning yourself");
        } finally {
            setIsAssigningDesigner(false);
        }
    };

    // Open the remove-designer confirmation modal
    const openRemoveDesignerModal = (quoteType, designerId, childQuoteId, designerName, isCompleted, childQuoteName = null) => {
        setRemoveDesignerModal({
            open: true,
            quoteType,
            designerId,
            childQuoteId,
            childQuoteName,
            designerName,
            isCompleted,
            // Default: keep the completed state if the designer already finished
            keepCompleted: !!isCompleted,
        });
    };

    // childQuoteId is the job_quotes UUID — null for old single-quote jobs,
    // set for new multi-quote jobs so the backend can target the exact row.
    const handleRemoveDesigner = async () => {
        const { quoteType, designerId, childQuoteId, childQuoteName, keepCompleted } = removeDesignerModal;
        setRemoveDesignerModal(prev => ({ ...prev, open: false }));
        try {
            await removeDesigner(assignment.id, quoteType, designerId, childQuoteId, keepCompleted, childQuoteName);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing designer:", error);
            alert(error.message || "Error removing designer");
        }
    };

    const handleAssignEstimator = async () => {
        if (!selectedEstimatorId) return;

        try {
            const estimator = availableEstimators.find(e => e.id === selectedEstimatorId);
            await assignEstimator(
                assignment.id,
                selectedEstimatorId,
                estimator.username || estimator.full_name,
            );

            const notificationData = {
                user_id: selectedEstimatorId,
                company_id: company.id,
                type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                title: "New Assignment",
                message: `Assignment ${assignment.unique_id} assigned to you by ${userProfile.full_name || userProfile.username}`,
                priority: "normal",
                related_type: "assignment",
                related_id: assignment.id,
                action_url: `${companySlug}/assignment/${assignment.id}`
            }

            await createNotification(notificationData)

            setShowEstimatorModal(false);
            setSelectedEstimatorId('');
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning estimator:", error);
            alert(error.message || "Error assigning estimator");
        }
    };

    const handleSelfAssignEstimator = async () => {
        try {
            await assignEstimator(
                assignment.id,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error self-assigning estimator:", error);
            alert(error.message || "Error assigning yourself as estimator");
        }
    };

    const handleRemoveEstimator = async () => {
        if (!window.confirm("Are you sure you want to remove the estimator?")) return;

        try {
            await removeEstimator(assignment.id);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing estimator:", error);
            alert(error.message || "Error removing estimator");
        }
    };

    const handleCompleteDesign = async (quoteType, assignedDesigners, quoteId = null) => {

        // For new-system children, find the designer specific to this child (child_job_id = quoteId)
        // Fall back to type-level match for old-system
        let assignedDesigner = quoteId
            ? assignedDesigners.find(d => d.child_job_id === quoteId)
            : null;
        if (!assignedDesigner) {
            assignedDesigner = assignedDesigners.find(d => d.quote_type === quoteType);
        }

        // Resolve the job_quotes row for this specific child (needed by completeDesign backend)
        const department = quoteId
            ? (assignment.job_quotes?.find(q => q.id === quoteId) || null)
            : null;

        if (!quoteType || !uploadedFiles?.pdfFiles || uploadedFiles.pdfFiles.length === 0) {
            alert("Please upload at least one file before completing the design");
            return;
        }

        if (!window.confirm(`Are you sure you want to complete the ${quoteType} design? This action cannot be undone.`)) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            const result = await completeDesign(
                assignment.id,
                quoteType,
                assignedDesigner?.user_id || null,
                assignedDesigner?.name || null,
                completionNotes[quoteType] || '',
                uploadedFiles.pdfFiles,
                companySlug || company?.slug,
                userProfile,
                department
            );

            // Save delivery note — filter by specific child for new-system
            if (quoteDeliveryNotes[quoteType]?.trim()) {
                let noteQuery = supabase
                    .from('job_quotes')
                    .update({ delivery_note: quoteDeliveryNotes[quoteType].trim() })
                    .eq('job_id', assignment.id)
                    .eq('quote_type', quoteType);

                if (quoteId) {
                    noteQuery = noteQuery.eq('id', quoteId);
                } else {
                    noteQuery = noteQuery.or('delivery_count.is.null,delivery_count.eq.0');
                }

                const { error: noteError } = await noteQuery;
                if (noteError) {
                    console.error('Error saving delivery note:', noteError);
                    // Don't throw - design is already completed
                }
            }

            // Success feedback
            const uploadCount = result.uploaded_files?.length || 0;
            alert(`Design completed successfully! ${uploadCount} file(s) uploaded.`);

            // Reset form
            setUploadedFiles({ pdfFiles: [] });
            setCompletionNotes(prev => ({
                ...prev,
                [quoteType]: ''
            }));
            setQuoteDeliveryNotes(prev => ({
                ...prev,
                [quoteType]: ''
            }));
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            // Refresh assignment data
            fetchAssignment();
        } catch (error) {
            console.error("Error completing design:", error);
            alert(error.message || "Error completing design. Please try again.");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handleRemoveSelfAssignment = async (type, quoteType) => {
        if (!window.confirm("Are you sure you want to remove this assignment from yourself?")) return;

        try {
            await removeSelfAssignment(assignment.id, assignment.unique_id, userProfile, type, quoteType);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing designer:", error);
            alert(error.message || "Error removing designer");
        }
    }

    const handleCompleteEstimation = async (estimator) => {


        if (!uploadedFiles?.pdfFiles || uploadedFiles.pdfFiles.length === 0) {
            alert("Please upload at least one file before completing the estimation");
            return;
        }

        if (isEstimationPricingMissing()) {
            alert("Please complete all required pricing fields before completing the estimation.");
            return;
        }

        if (!window.confirm("Are you sure you want to complete the estimation? This action cannot be undone.")) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            await saveEstimationPricingEntries();
            const result = await completeEstimation(
                assignment.id,
                estimator?.user_id || null,
                estimator?.name || null,
                completionNotes.estimation || '',
                uploadedFiles.pdfFiles,
                companySlug || company?.slug,
                userProfile
            );

            // Success feedback
            const uploadCount = result.uploaded_files?.length || 0;
            alert(`Estimation completed successfully! ${uploadCount} file(s) uploaded.`);

            // Reset form
            setUploadedFiles({ pdfFiles: [] });
            setCompletionNotes(prev => ({
                ...prev,
                estimation: ''
            }));
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            // Refresh assignment data
            fetchAssignment();
        } catch (error) {
            console.error("Error completing estimation:", error);
            alert(error.message || "Error completing estimation. Please try again.");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handlePdfsChange = useCallback((pdfFiles) => {
        setUploadedFiles(prevState => ({ ...prevState, pdfFiles }));
    }, []);

    const handleToggleQuoteSent = async () => {
        try {
            setIsTogglingQuoteSent(true);
            const updatedAssignment = await toggleQuoteSent(assignment.id, assignment.quote_sent);
            setAssignment(prev => ({ ...prev, quote_sent: updatedAssignment.quote_sent }));
        } catch (error) {
            console.error('Error toggling quote sent status:', error);
            alert('Failed to update quote sent status');
        } finally {
            setIsTogglingQuoteSent(false);
        }
    };

    const handleOpenNewDeliveryModal = (quoteType) => {
        setNewDeliveryQuoteType(quoteType);
        setShowNewDeliveryModal(true);

        // Reset form fields
        setNewDeliveryNote('');
        setNewDeliveryTimeSlot(null);
        setNewDeliveryScheduledDate('');
        setNewDeliverySelectedUnits([]);
    };

    const handleCloseNewDeliveryModal = () => {
        setShowNewDeliveryModal(false);
        setNewDeliveryQuoteType(null);
        setNewDeliveryNote('');
        setNewDeliveryTimeSlot(null);
        setNewDeliveryScheduledDate('');
        setNewDeliverySelectedUnits([]);
    };

    const handleToggleUnit = (unitNumber) => {
        setNewDeliverySelectedUnits(prev => {
            if (prev.includes(unitNumber)) {
                return prev.filter(u => u !== unitNumber);
            } else {
                return [...prev, unitNumber];
            }
        });
    };

    const handleSubmitNewDelivery = async () => {
        try {
            if (!newDeliveryQuoteType) {
                alert('Quote type is required');
                return;
            }

            // Create the new delivery record
            await createNewDelivery(
                assignment.id,
                newDeliveryQuoteType,
                newDeliveryNote,
                newDeliveryTimeSlot,
                newDeliveryScheduledDate,
                newDeliverySelectedUnits
            );

            // Close modal and refresh assignment data
            handleCloseNewDeliveryModal();
            await fetchAssignment();

            alert('New delivery created successfully!');
        } catch (error) {
            console.error('Error creating new delivery:', error);
            alert('Failed to create new delivery. Please try again.');
        }
    };

    // not, inv, cod, paid

    const getStatusColor = (status) => {
        const statusColors = {
            pending_design: "bg-yellow-700 text-white border-yellow-700",
            waiting_assignment: "bg-yellow-700 text-white border-yellow-700",
            in_design: "bg-yellow-700 text-white border-yellow-700",
            in_progress: "bg-yellow-700 text-white border-yellow-700",
            design_completed: "bg-green-600 text-white border-green-700",
            pending_estimation: "bg-orange-100 text-orange-800 border-orange-200",
            in_estimation: "bg-yellow-700 text-white border-yellow-700",
            estimation_completed: "bg-green-600 text-white border-green-700",
            ready_for_delivery: "bg-emerald-100 text-emerald-800 border-emerald-200",
            completed: "bg-green-600 text-white border-green-700"
        };
        return statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200";
    };

    const getStatusTextColor = (status) => {
        const statusColors = {
            pending_design: "text-yellow-700",
            waiting_assignment: "text-gray-700",
            in_design: "text-yellow-700",
            in_progress: "text-yellow-700",
            design_completed: "text-green-600",
            pending_estimation: "text-orange-100",
            in_estimation: "text-yellow-700",
            estimation_completed: "text-green-600",
            ready_for_delivery: "text-emerald-100",
            completed: "text-green-600"
        };
        return statusColors[status] || "text-gray-500";
    };

    const getDepartmentBoxColor = (status) => {
        return getQuoteColor(status).bg;
    };

    const getDepartmentTextColor = (status) => {
        const colors = {
            roof: "text-blue-900",
            wall: "text-red-900",
            floor: "text-green-900",
            timber: "text-amber-900",
            beams: "text-orange-900",
            flooring_sheets: "text-violet-900",
        };

        return colors[status] || "text-gray-900";
    };

    const getRequiredSections = (job) => {
        const sections = [];
        job.job_quotes?.forEach(quote => {
            if (quote.is_required) {
                if (quote?.child_unique_id) {
                    sections.push(quote?.child_unique_id);
                } else {
                    sections.push(getQuoteLabel(quote.quote_type));
                }

            }
        });
        return sections;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDateDriver = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const copyToClipboard = async (text, type = "Text") => {
        if (!text?.trim()) return;

        await navigator.clipboard.writeText(text.trim());

        Toast.success(`${type} copied`, {
            title: "Copied",
            position: "bottom-right",
            autoClose: 1500
        });
    };

    const hasJobAddress = !!assignment?.customer_address?.trim();
    const hasEmail = !!assignment?.customer_mail?.trim();
    const hasPhoneNumber = !!assignment?.customer_number?.trim();

    const customerTypeMeta = assignment?.customer_type
        ? CUSTOMER_TYPE_META[assignment.customer_type] || {
            label: assignment.customer_type,
            badge: "bg-gray-50 text-gray-700 border-gray-200",
            card: "bg-gray-50 border-gray-200",
            icon: "bg-gray-100 text-gray-700",
        }
        : null;

    const downloadFile = async (file) => {
        try {
            // The file.filename already contains the full path: companySlug/jobId/type-timestamp-filename
            let filePath;

            // Handle different filename formats from database
            if (file?.filename.startsWith('http://') || file?.filename.startsWith('https://')) {
                // If it's a full URL, extract the path after the bucket name
                const urlParts = file?.filename.split('/storage/v1/object/public/job-files/');
                if (urlParts.length > 1) {
                    filePath = decodeURIComponent(urlParts[1]);
                } else {
                    throw new Error('Invalid file URL format');
                }
            } else if (file?.filename.includes(`${companySlug || company?.slug}/${jobId}`)) {
                // Already has the full path structure
                filePath = file?.filename;
            } else if (file?.filename.startsWith(`${companySlug || company?.slug}/`)) {
                // Has company but might be missing jobId
                filePath = file?.filename;
            } else {
                // Just a filename or timestamp-based name, construct full path
                filePath = `${companySlug || company?.slug}/${jobId}/${file?.filename}`;
            }

            const { data, error } = await supabase.storage
                .from('job-files')
                .download(filePath);

            if (error) {
                console.error('Download error:', error);
                throw error;
            }

            // Create a download link
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.original_name || filePath.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file: ' + (err.message || 'Unknown error'));
        }
    };

    const getFilesForQuoteType = useCallback((quoteType) => {
        return (assignment?.job_files || []).filter(file =>
            file.quote_type === quoteType &&
            file.filename !== "__folder_placeholder__"
        );
    }, [assignment?.job_files]);

    const isFileOnlyQuoteCompleted = useCallback((quote) => {
        if (!quote || !isFileOnlyQuoteType(quote.quote_type)) return false;

        return (
            quote.department_file_status === "completed" ||
            getFilesForQuoteType(quote.quote_type).length > 0
        );
    }, [getFilesForQuoteType]);

    const downloadAllFilesWithFolders = async (files, zipName = 'files.zip') => {
        if (isDownloading) return;
        setIsDownloading(true);

        const sanitizeName = (name = '') => {
            return name
                .replace(/[\\/:*?"<>|]/g, '_')   // replace invalid chars
                .replace(/\.+$/, '')             // remove trailing dots
                .replace(/\s+$/, '')             // remove trailing spaces
                .trim();
        };

        try {
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;

            const usedZipPaths = new Set();

            const getUniqueZipPath = (path) => {
                if (!usedZipPaths.has(path)) {
                    usedZipPaths.add(path);
                    return path;
                }

                const lastSlash = path.lastIndexOf('/');
                const folder = lastSlash >= 0 ? path.slice(0, lastSlash + 1) : '';
                const name = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;

                const dotIndex = name.lastIndexOf('.');
                const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
                const ext = dotIndex > 0 ? name.slice(dotIndex) : '';

                let counter = 2;
                let newPath;

                do {
                    newPath = `${folder}${base} (${counter})${ext}`;
                    counter++;
                } while (usedZipPaths.has(newPath));

                usedZipPaths.add(newPath);
                return newPath;
            };

            // Download and organize files
            for (const file of files) {
                // Skip folder placeholder records
                if (file?.filename === '__folder_placeholder__') continue;

                try {
                    let filePath;

                    // Handle different filename formats
                    if (file?.filename.startsWith('http://') || file?.filename.startsWith('https://')) {
                        const urlParts = file?.filename.split('/storage/v1/object/public/job-files/');
                        if (urlParts.length > 1) {
                            filePath = decodeURIComponent(urlParts[1]);
                        } else {
                            throw new Error('Invalid file URL format');
                        }
                    } else if (file?.filename.includes(`${companySlug || company?.slug}/${jobId}`)) {
                        filePath = file?.filename;
                    } else if (file?.filename.startsWith(`${companySlug || company?.slug}/`)) {
                        filePath = file?.filename;
                    } else {
                        filePath = `${companySlug || company?.slug}/${jobId}/${file?.filename}`;
                    }

                    const { data, error } = await supabase.storage
                        .from('job-files')
                        .download(filePath);

                    if (error) throw error;

                    const fileName = sanitizeName(file.original_name || filePath.split('/').pop());
                    const fileType = file.file_type || 'other';

                    // Create folder path based on file type
                    let folderPath;

                    if (fileType === 'design') {
                        // Design files: organize by quote type
                        const quoteType = file.quote_type || 'general';
                        const quoteTypeName = quoteType === 'general' ? 'General' :
                            quoteType.charAt(0).toUpperCase() + quoteType.slice(1);
                        folderPath = `Design/${quoteTypeName}/${fileName}`;
                    } else {
                        // Non-design files: only file type folder
                        const fileTypeName = fileType === 'initial' ? 'Job Files' :
                            fileType === 'estimation' ? 'Estimation' :
                                fileType === 'timber' ? 'Timber' :
                                    fileType === 'delivery' ? 'Delivery' :
                                        fileType === 'custom' ? `${file?.description ? file?.description : "Custom"}` :
                                            fileType.charAt(0).toUpperCase() + fileType.slice(1);
                        folderPath = `${fileTypeName}/${fileName}`;
                    }

                    zip.file(getUniqueZipPath(folderPath), data);
                    successCount++;

                } catch (err) {
                    console.error(`Error downloading file ${file.original_name}:`, err);
                    failCount++;
                }
            }

            if (successCount === 0) {
                alert('Failed to download any files');
                return;
            }

            // Add notes file
            const jobNotes = assignment?.job_notes || [];
            if (jobNotes.length > 0) {
                const notesText = jobNotes.map((note, index) => {
                    const dateStr = note.added_at ? new Date(note.added_at).toLocaleString() : '';
                    const editedStr = note.is_edited && note.edited_at ? `\n  (Edited: ${new Date(note.edited_at).toLocaleString()})` : '';
                    const jobRef = note.child_unique_id ? `[${note.child_unique_id}] ` : '';
                    return `[${index + 1}] ${note.added_by_name} (${note.added_by_role}) — ${dateStr}${editedStr}\n${jobRef}${note.content}`;
                }).join('\n\n---\n\n');
                zip.file('Notes.txt', notesText);
            }

            // Add empty customer name file
            const customerName = sanitizeName(assignment?.customer_name || 'Customer');
            if (customerName) {
                zip.file(`${customerName}.txt`, '');
            }

            const zipBlob = await zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: {
                    level: 6
                }
            });

            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = zipName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            if (failCount > 0) {
                alert(`Downloaded ${successCount} files. ${failCount} files failed.`);
            }

        } catch (err) {
            console.error('Error creating zip file:', err);
            alert('Failed to create zip file: ' + (err.message || 'Unknown error'));
        } finally {
            setIsDownloading(false);
        }
    };

    const seeFile = (file, allFiles = []) => {
        setSelectedFile(file);
        setViewerFiles(allFiles.length > 0 ? allFiles : [file]);
        setSelectedFileIndex(allFiles.findIndex(f => f.filename === file.filename));
        setFileViewerOpen(true);
    };

    const getDriverAssignedDeliveries = () => {
        if (userProfile?.role !== 'driver') return [];

        // Return all deliveries assigned to this driver (including multiple deliveries of same quote type)
        return assignment.job_deliveries?.filter(delivery =>
            delivery.assigned_driver_id === userProfile.id
        ).sort((a, b) => {
            // Sort by quote type first, then by delivery_count
            if (a.quote_type !== b.quote_type) {
                return a.quote_type.localeCompare(b.quote_type);
            }
            return (a.delivery_count ?? 0) - (b.delivery_count ?? 0);
        }) || [];
    };

    const canDesignerCompleteQuote = (quoteType) => {
        if (userProfile?.role !== 'designer') return false;

        const userDesigner = assignment.job_designers?.find(
            d => d.quote_type === quoteType && d.user_id === userProfile.id
        );
        return userDesigner && !userDesigner.is_completed;
    };

    const customerConversationFiles = customerConversations.flatMap((conversation) =>
        normalizeConversationFiles(conversation.files).map((file) => ({
            ...file,
            direction: conversation.direction,
            created_at: conversation.created_at,
            sent_by_name: conversation.sent_by_name,
            sent_by_role: conversation.sent_by_role,
        }))
    ).filter(file => file.direction === 'inbound');
    const jobFiles = assignment?.job_files || [];

    const driverFiles = useMemo(() => {
        return jobFiles.filter(file =>
            file.file_type === "driver" &&
            file.filename !== "__folder_placeholder__"
        );
    }, [jobFiles]);

    const filteredDriverFiles = useMemo(() => {
        return filterFiles(driverFiles);
    }, [driverFiles, filterFiles]);

    const filteredJobFiles = useMemo(() => {
        return filterFiles(
            jobFiles.filter(file => file.file_type !== "driver")
        );
    }, [jobFiles, filterFiles]);

    const driverFilesEnabled =
        company?.settings?.enable_driver_files === true;

    const shouldShowDriverFiles =
        driverFilesEnabled || driverFiles.length > 0;

    const driverFileChildOptions = useMemo(() => {
        return (assignment?.job_quotes || [])
            .filter(quote => quote.id && quote.child_unique_id)
            .map(quote => ({
                id: quote.id,
                childUniqueId: quote.child_unique_id,
                quoteType: quote.quote_type,
            }));
    }, [assignment?.job_quotes]);

    const filteredCustomerConversationFiles = useMemo(() => {
        return filterFiles(customerConversationFiles);
    }, [customerConversationFiles, filterFiles]);

    const hasFileSearch = fileSearch.trim().length > 0;

    const inboundConversationCount = customerConversations.filter(
        conversation => conversation.direction === 'inbound'
    ).length;

    // Status Badge Component
    const StatusBadge = ({ status, large = false }) => {
        const isTimber = assignment?.job_type === "Timber";
        const displayStatus = isTimber
            ? (status === 'ready_for_delivery' ? 'Ready for Delivery' :
                status === 'completed' ? 'Completed' : status.replace('_', ' '))
            : status.replace('_', ' ');

        if (assignment?.is_cancelled) {
            return (
                <span className={`px-3 py-1 rounded-full font-medium border ${large ? 'text-sm' : 'text-xs'} bg-red-100 text-red-800 border-red-300`}>
                    CANCELLED
                </span>
            );
        }

        return (
            <span className={`px-3 py-1 rounded-full font-medium border ${large ? 'text-sm' : 'text-xs'} ${getStatusColor(status)}`}>
                {displayStatus.toUpperCase()}
            </span>
        );
    };

    // Info Card Component
    const InfoCard = ({ title, children, icon: Icon, color = "blue", customClass = "" }) => (
        <div className={`bg-white border border-gray-200 rounded-lg p-4 space-y-2 ${customClass}`}>
            <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                <Icon className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
            </div>
            <div className="space-y-2">
                {children}
            </div>
        </div>
    );

    const isJobCancelled = !!assignment?.is_cancelled;

    const CancelledAction = ({ children, allow = false }) => {
        if (!isJobCancelled || allow) return children;

        return (
            <TextTooltip text="This job is cancelled">
                <span className="inline-flex cursor-not-allowed">
                    {React.cloneElement(children, {
                        disabled: true,
                        onClick: undefined,
                        className: `${children.props.className || ''} opacity-50 cursor-not-allowed pointer-events-none`
                    })}
                </span>
            </TextTooltip>
        );
    };

    // Detail Row Component
    const DetailRow = ({ label, value, icon: Icon, link, extraStyle = "", containerStyle = "", onCopy }) => {
        const labelNode = React.isValidElement(label) ? (
            <div className="text-xs text-gray-500">{label}</div>
        ) : (
            <p className="text-xs text-gray-500">{label}</p>
        );

        return (
            <div className={`flex items-start gap-2 ${containerStyle}`}>
                {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
                <div className="flex-1 min-w-0 mb-1">
                    {labelNode}

                    {link ? (
                        <div className="flex items-center gap-2">
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-gray-800 break-words hover:text-blue-600 flex"
                            >
                                {value || "Not specified"}
                            </a>

                            {onCopy && value && (
                                <button
                                    type="button"
                                    onClick={onCopy}
                                    className="p-1 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                    title="Copy"
                                >
                                    <FiCopy className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            {React.isValidElement(value) ? (
                                value
                            ) : (
                                <div className={`text-sm font-medium text-gray-800 break-words ${extraStyle}`}>
                                    {value || "Not specified"}
                                </div>
                            )}

                            {onCopy && value && (
                                <button
                                    type="button"
                                    onClick={onCopy}
                                    className="p-1 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                    title="Copy"
                                >
                                    <FiCopy className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Checkbox Display Component
    const CheckboxDisplay = ({ label, isChecked, keyText }) => (
        <div key={keyText || label} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isChecked ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                {isChecked && <FiCheck className="w-3 h-3 text-white" />}
            </div>
            <span className={`text-sm ${isChecked ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                {label}
            </span>
        </div>
    );

    const renderUserAvatar = (userId, name, sizeClass = 'w-7 h-7', bgClass = 'bg-gray-400', textClass = 'text-xs') => {
        const avatarUrl = userAvatarMap[userId];
        const initial = (name || '?').charAt(0).toUpperCase();
        return avatarUrl ? (
            <img
                src={avatarUrl}
                alt={name}
                className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
            />
        ) : (
            <div className={`${sizeClass} ${bgClass} text-white rounded-full flex items-center justify-center flex-shrink-0`}>
                <span className={`${textClass} font-bold`}>{initial}</span>
            </div>
        );
    };

    const getQuoteIcon = (quoteType, className = "w-4 h-4") => {
        if (quoteType === "roof") return <Home className={className} />;
        if (quoteType === "wall") return <PanelsTopLeft className={className} />;
        if (quoteType === "floor") return <HiOutlineSquare3Stack3D className={className} />;
        if (quoteType === "timber") return <GiWoodBeam className={className} />;
        if (quoteType === "beams") return <GiWoodBeam className={className} />;
        if (quoteType === "flooring_sheets") return <SquareStack className={className} />;

        return <FiLayers className={className} />;
    };

    const getQuoteColor = (quoteType) => {
        const colors = {
            roof: {
                bg: "bg-blue-50",
                border: "border-blue-200",
                iconBg: "bg-blue-500",
                avatarBg: "bg-blue-500",
                divider: "divide-blue-100",
            },
            wall: {
                bg: "bg-red-50",
                border: "border-red-200",
                iconBg: "bg-red-500",
                avatarBg: "bg-red-500",
                divider: "divide-red-100",
            },
            floor: {
                bg: "bg-green-50",
                border: "border-green-200",
                iconBg: "bg-green-500",
                avatarBg: "bg-green-500",
                divider: "divide-green-100",
            },
            timber: {
                bg: "bg-amber-50",
                border: "border-amber-200",
                iconBg: "bg-amber-500",
                avatarBg: "bg-amber-500",
                divider: "divide-amber-100",
            },
            beams: {
                bg: "bg-orange-50",
                border: "border-orange-200",
                iconBg: "bg-orange-500",
                avatarBg: "bg-orange-500",
                divider: "divide-orange-100",
            },
            flooring_sheets: {
                bg: "bg-violet-50",
                border: "border-violet-200",
                iconBg: "bg-violet-500",
                avatarBg: "bg-violet-500",
                divider: "divide-violet-100",
            },
        };

        return colors[quoteType] || {
            bg: "bg-gray-50",
            border: "border-gray-200",
            iconBg: "bg-gray-400",
            avatarBg: "bg-gray-400",
            divider: "divide-gray-100",
        };
    };


    const INVOICE_META = {
        not_invoiced: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },
        null: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },
        undefined: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },
        deleted: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },

        draft: {
            label: "DRAFT",
            short: "DRAFT",
            card: "bg-sky-50 border-sky-200",
            iconWrap: "bg-sky-100 text-sky-700",
            badge: "bg-sky-100 text-sky-700 border-sky-200",
            accent: "bg-sky-500",
            wrap: "bg-sky-50 border-sky-200",
        },
        submitted: {
            label: "Submitted",
            short: "Submitted",
            card: "bg-sky-50 border-sky-200",
            iconWrap: "bg-sky-100 text-sky-700",
            badge: "bg-sky-100 text-sky-700 border-sky-200",
            accent: "bg-sky-500",
            wrap: "bg-sky-50 border-sky-200",
        },

        invoiced: {
            label: "Invoiced",
            short: "Inv",
            card: "bg-blue-50 border-blue-200",
            iconWrap: "bg-blue-100 text-blue-700",
            badge: "bg-blue-100 text-blue-700 border-blue-200",
            accent: "bg-blue-500",
            wrap: "bg-blue-50 border-blue-200",
        },
        authorised: {
            label: "Authorised",
            short: "Authorised",
            card: "bg-blue-50 border-blue-200",
            iconWrap: "bg-blue-100 text-blue-700",
            badge: "bg-blue-100 text-blue-700 border-blue-200",
            accent: "bg-blue-500",
            wrap: "bg-blue-50 border-blue-200",
        },
        cod: {
            label: "COD",
            short: "COD",
            card: "bg-sky-50 border-sky-200",
            iconWrap: "bg-sky-100 text-sky-700",
            badge: "bg-sky-100 text-sky-700 border-sky-200",
            accent: "bg-sky-500",
            wrap: "bg-sky-50 border-sky-200",
        },
        paid: {
            label: "Paid",
            short: "Paid",
            card: "bg-green-50 border-green-200",
            iconWrap: "bg-green-100 text-green-700",
            badge: "bg-green-100 text-green-700 border-green-200",
            accent: "bg-green-500",
            wrap: "bg-green-50 border-green-200",
        },
        deposit: {
            label: "Deposit",
            short: "Deposit",
            card: "bg-amber-50 border-amber-200",
            iconWrap: "bg-amber-100 text-amber-700",
            badge: "bg-amber-100 text-amber-700 border-amber-200",
            accent: "bg-amber-500",
            wrap: "bg-amber-50 border-amber-200",
        },
        voided: {
            label: "Voided",
            short: "Voided",
            card: "bg-zinc-50 border-zinc-200",
            iconWrap: "bg-zinc-100 text-zinc-700",
            badge: "bg-zinc-100 text-zinc-700 border-zinc-200",
            accent: "bg-zinc-500",
            wrap: "bg-zinc-50 border-zinc-200",
        },
        cancelled: {
            label: "Cancelled",
            short: "Cancelled",
            card: "bg-zinc-50 border-zinc-200",
            iconWrap: "bg-zinc-100 text-zinc-700",
            badge: "bg-zinc-100 text-zinc-700 border-zinc-200",
            accent: "bg-zinc-500",
            wrap: "bg-zinc-50 border-zinc-200",

        },
    };

    const getSortedQuoteTypeEntries = (rows = []) => {
        const grouped = rows.reduce((acc, row) => {
            const type = row?.quote_type || "other";
            if (!acc[type]) acc[type] = [];
            acc[type].push(row);
            return acc;
        }, {});

        return Object.entries(grouped)
            .sort(([a], [b]) => (QUOTE_ORDER[a] ?? 99) - (QUOTE_ORDER[b] ?? 99))
            .map(([quoteType, items]) => ({
                quoteType,
                label: getQuoteLabel(quoteType),
                items: items.sort((a, b) => {
                    const childCompare = (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                    if (childCompare !== 0) return childCompare;
                    return (a.delivery_count || 0) - (b.delivery_count || 0);
                }),
            }));
    };

    const getInvoiceDisplayName = (quote) => {
        if (quote?.child_unique_id) return quote.child_unique_id;
        return quote?.quote_type || "Unknown";
    };


    const StatusPill = ({ label, color = "neutral" }) => {

        const base =
            "mt-1 px-1 py-[1px] rounded-md text-xs font-semibold border transition-all";

        const variants = {
            neutral: "bg-gray-800 text-white border-gray-800",
            success: "bg-green-600 text-white border-green-600",
            warning: "bg-amber-500 text-white border-amber-500",
            danger: "bg-red-600 text-white border-red-600",
            info: "bg-blue-600 text-white border-blue-600"
        };

        return (
            <span className={`${base} ${variants[color] || ""}`}>
                {label}
            </span>
        );
    };

    // Compact, non-interactive indicator for a quote/department that's on_hold.
    // Purely informational — never disables or hides the actions around it.
    const OnHoldBadge = ({ className = "" }) => (
        <TextTooltip text="This quote is on hold">
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none whitespace-nowrap ${className}`}>
                <PauseCircle className="w-2.5 h-2.5 flex-shrink-0" />
                ON HOLD
            </span>
        </TextTooltip>
    );


    const DisplayStatusCards = ({ assignment }) => {
        const DEPT_LIMIT = 10;
        const [showAllDepts, setShowAllDepts] = useState(false);

        const requiredQuotes = (assignment.job_quotes?.filter(q =>
            q.is_required && !q.delivery_count
        ) || []).sort((a, b) => {
            const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
            if (typeOrder !== 0) return typeOrder;
            return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
        });

        const visibleQuotes = showAllDepts ? requiredQuotes : requiredQuotes.slice(0, DEPT_LIMIT);
        const hasMore = requiredQuotes.length > DEPT_LIMIT;

        const estimationStatus = getJobStatusSummary(assignment, 'estimation');
        const designStatus = getJobStatusSummary(assignment, 'design');

        return (
            <div className="flex flex-col gap-3 w-full">
                <div className="flex flex-wrap items-center gap-2">
                    {assignment.job_type === "Estimation" && (
                        <StatusPill
                            label={assignment?.on_hold ? "Estimation On Hold" : estimationStatus}
                            color="neutral"
                        />
                    )}

                    {assignment.job_type === "Design" && (
                        <StatusPill
                            label={designStatus}
                            color="neutral"
                        />
                    )}

                    {assignment.job_type === "Design & Estimation" && (
                        <>
                            <StatusPill
                                label={assignment?.on_hold ? "Est. On Hold" : `Est. ${estimationStatus}`}
                                color={assignment?.on_hold ? "neutral" : getStatusColor(estimationStatus?.toLowerCase().replaceAll(" ", "_"))}
                            />
                            <StatusPill
                                label={`Des. ${designStatus}`}
                                color={getStatusColor(designStatus?.toLowerCase().replaceAll(" ", "_"))}
                            />
                        </>
                    )}
                </div>

                {requiredQuotes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {visibleQuotes.map((quote) => {
                            const isChild = !!quote?.child_unique_id;
                            const isFileOnly = isFileOnlyQuoteType(quote.quote_type);
                            const fileOnlyCompleted = isFileOnlyQuoteCompleted(quote);

                            const pillClass = isFileOnly
                                ? `flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border capitalize ${fileOnlyCompleted
                                    ? "bg-green-100 border-green-200 text-green-800"
                                    : "bg-amber-100 border-amber-200 text-amber-800"
                                }`
                                : `flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-gray-100 border border-gray-200 capitalize ${getStatusTextColor(quote?.design_status)} ${isChild ? 'cursor-pointer hover:bg-gray-200 hover:border-gray-300 transition-colors' : ''}`;

                            const label = isFileOnly
                                ? `${getQuoteLabel(quote.quote_type)} · ${fileOnlyCompleted ? "Files Added" : "Waiting Files"}`
                                : (quote?.child_unique_id || getQuoteLabel(quote?.quote_type));

                            const content = (
                                <>
                                    <span className="text-sm">
                                        {getQuoteIcon(quote?.quote_type)}
                                    </span>
                                    {quote?.on_hold && <OnHoldBadge />}
                                    <span>
                                        {label}
                                    </span>
                                </>
                            );

                            if (isChild && !isFileOnly) {
                                return (
                                    <button
                                        key={quote?.id || quote?.child_unique_id}
                                        type="button"
                                        onClick={() => navigate(`/${companySlug}/assignment/${jobId}/${quote.id}`, {
                                            state: { from: location.pathname }
                                        })}
                                        className={pillClass}
                                    >
                                        {content}
                                    </button>
                                );
                            }

                            return (
                                <div
                                    key={quote?.id || quote?.quote_type}
                                    className={pillClass}
                                >
                                    {content}
                                </div>
                            );
                        })}

                        {hasMore && (
                            <button
                                type="button"
                                onClick={() => setShowAllDepts(prev => !prev)}
                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-dashed border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                            >
                                {showAllDepts
                                    ? 'Show less'
                                    : `+${requiredQuotes.length - DEPT_LIMIT} more`}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const FILE_ICON_MAP = {
        image: {
            extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'],
            icon: <FiImage size={20} color="GRAY" />
        },
        pdf: {
            extensions: ['pdf'],
            icon: <FaRegFilePdf size={20} color="GRAY" />
        },
        word: {
            extensions: ['doc', 'docx'],
            icon: <FaRegFileWord size={20} color="GRAY" />
        },
        excel: {
            extensions: ['xls', 'xlsx', 'csv'],
            icon: <FileSpreadsheet size={20} color="GRAY" />
        },
        audio: {
            extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac'],
            icon: <FileAudio size={20} color="GRAY" />
        },
        video: {
            extensions: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
            icon: <FileVideo size={20} color="GRAY" />
        },
        archive: {
            extensions: ['zip', 'rar', '7z', 'tar', 'gz'],
            icon: <FileArchive size={20} color="GRAY" />
        },
        code: {
            extensions: ['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'py', 'java', 'php'],
            icon: <Code size={20} color="GRAY" />
        },
        text: {
            extensions: ['txt', 'md', 'log'],
            icon: <FiFileText size={20} color="GRAY" />
        }
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        for (const type in FILE_ICON_MAP) {
            if (FILE_ICON_MAP[type].extensions.includes(extension)) {
                return FILE_ICON_MAP[type].icon;
            }
        }

        return <FiFile size={20} color="GRAY" />; // default icon
    };

    const getActionIcon = (action) => {
        const iconMap = {
            'job_created': FiCheckCircle,
            'job_updated': FiEdit3,
            'Job Updated': FiEdit3,
            'job_assigned': FiUser,
            'job_completed': FiCheckCircle,
            'status_changed': FiClock,
            'designer_assigned': FiUser,
            'removed_assignee': FiUser,
            'estimator_assigned': FiDollarSign,
            'production_started': FiPackage,
            'delivery_scheduled': FiTruck,
        };
        return iconMap[action] || FiActivity;
    };

    const getActionColor = (action) => {
        const colorMap = {
            'job_created': 'text-green-600 bg-green-100',
            'job_updated': 'text-blue-600 bg-blue-100',
            'job_assigned': 'text-purple-600 bg-purple-100',
            'job_completed': 'text-emerald-600 bg-emerald-100',
            'status_changed': 'text-amber-600 bg-amber-100',
            'designer_assigned': 'text-indigo-600 bg-indigo-100',
            'estimator_assigned': 'text-teal-600 bg-teal-100',
            'production_started': 'text-orange-600 bg-orange-100',
            'delivery_scheduled': 'text-pink-600 bg-pink-100',
        };
        return colorMap[action] || 'text-gray-600 bg-gray-100';
    };

    const formatDateTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-AU', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const toggleFolder = (fileType) => {
        setOpenFolders(prev => ({
            ...prev,
            [fileType]: !prev[fileType]
        }));
    };

    const toggleQuoteFolder = (fileType, quoteType) => {
        const key = `${fileType}-${quoteType}`;
        setOpenQuoteFolders(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Create a custom-named folder (persisted via a placeholder file record)
    const handleCreateCustomFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        setIsCreatingFolder(true);
        try {
            const { error } = await supabase
                .from('job_files')
                .insert({
                    job_id: assignment.id,
                    filename: '__folder_placeholder__',
                    original_name: name,
                    url: '__folder_placeholder__',
                    uploaded_by_user_id: userProfile.id,
                    uploaded_by_name: userProfile.username || userProfile.full_name,
                    uploaded_by_role: userProfile.role,
                    file_type: 'custom',
                    description: name,
                });
            if (error) throw error;
            setNewFolderName('');
            setShowFolderInput(false);
            fetchAssignment();
            Toast.success(`Folder "${name}" created`, { position: 'bottom-right', autoClose: 2000 });
        } catch (err) {
            console.error('Error creating folder:', err);
            Toast.error(err.message || 'Failed to create folder', { position: 'bottom-right', autoClose: 3000 });
        } finally {
            setIsCreatingFolder(false);
        }
    };

    // Trigger the hidden file input for a specific folder context
    const triggerFileUpload = (fileType, quoteType = null, folderName = null, isCustom = false) => {
        uploadContext.current = { fileType, quoteType, folderName, isCustom };
        if (fileUploadInputRef.current) {
            fileUploadInputRef.current.value = '';
            fileUploadInputRef.current.click();
        }
    };

    // Core upload logic — used by both file input and drag-drop
    const uploadSingleFile = async (file, fileType, quoteType, folderName, isCustom) => {
        const uploadKey = `${fileType}-${quoteType || 'general'}-${folderName || ''}`;
        setFolderUploading(uploadKey);
        try {
            if (isCustom && folderName) {
                const timestamp = Date.now();
                const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const filename = `${companySlug || company?.slug}/${assignment.id}/custom-${timestamp}-${sanitizedFileName}`;
                const { error: storageError } = await supabase.storage
                    .from('job-files')
                    .upload(filename, file, { cacheControl: '3600', upsert: false });
                if (storageError) throw storageError;
                const { error: dbError } = await supabase
                    .from('job_files')
                    .insert({
                        job_id: assignment.id,
                        filename,
                        original_name: file.name,
                        url: filename,
                        uploaded_by_user_id: userProfile.id,
                        uploaded_by_name: userProfile.username || userProfile.full_name,
                        uploaded_by_role: userProfile.role,
                        file_type: 'custom',
                        description: folderName,
                    });
                if (dbError) throw dbError;
            } else {
                await uploadJobFile(
                    assignment.id,
                    file,
                    fileType,
                    quoteType,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug || company?.slug,
                    null
                );

                if (isFileOnlyQuoteType(quoteType)) {
                    await supabase
                        .from("job_quotes")
                        .update({
                            department_file_status: "completed",
                            department_files_completed_at: new Date().toISOString(),
                            department_files_completed_by: userProfile.id,
                            department_files_completed_by_name: userProfile.username || userProfile.full_name,
                        })
                        .eq("job_id", assignment.id)
                        .eq("quote_type", quoteType);
                }
            }
            fetchAssignment();
            Toast.success('File uploaded successfully', { position: 'bottom-right', autoClose: 2000 });
        } catch (err) {
            console.error('Error uploading file:', err);
            Toast.error(err.message || 'Failed to upload file', { position: 'bottom-right', autoClose: 3000 });
        } finally {
            setFolderUploading(null);
        }
    };

    // Handle file selected from hidden input
    const handleFileInputChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const { fileType, quoteType, folderName, isCustom } = uploadContext.current;
        await uploadSingleFile(file, fileType, quoteType, folderName, isCustom);
    };

    const handleUploadDriverFiles = async () => {
        if (driverFilesToUpload.length === 0 || isUploadingDriverFiles) return;

        try {
            setIsUploadingDriverFiles(true);

            for (const item of driverFilesToUpload) {
                const selectedChild = item.childId
                    ? (assignment?.job_quotes || []).find(
                        quote => quote.id === item.childId
                    )
                    : null;

                await uploadJobFile(
                    assignment.id,
                    item.file,
                    "driver",
                    selectedChild?.quote_type || null,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug || company?.slug,
                    selectedChild
                        ? {
                            id: selectedChild.id,
                            child_unique_id: selectedChild.child_unique_id,
                        }
                        : null
                );
            }

            const uploadedCount = driverFilesToUpload.length;

            setDriverFilesToUpload([]);
            setClearDriverFiles(true);

            setTimeout(() => {
                setClearDriverFiles(false);
            }, 100);

            await fetchAssignment();

            Toast.success(
                `${uploadedCount} Driver File${uploadedCount !== 1 ? "s" : ""} uploaded`,
                {
                    position: "bottom-right",
                    autoClose: 2000,
                }
            );
        } catch (error) {
            console.error("Error uploading Driver Files:", error);

            Toast.error(error.message || "Failed to upload Driver Files", {
                position: "bottom-right",
                autoClose: 3000,
            });
        } finally {
            setIsUploadingDriverFiles(false);
        }
    };

    const parseLogDetails = (details) => {
        if (!details) return 'No details';

        // If it's already a string and not JSON, return it
        if (typeof details === 'string') {
            try {
                // Try to parse as JSON
                const parsed = JSON.parse(details);
                return parsed.message || parsed.details || details;
            } catch {
                // Not JSON, return as is
                return details;
            }
        }

        // If it's an object, extract message or details
        if (typeof details === 'object') {
            return details.message || details.details || JSON.stringify(details);
        }

        return String(details);
    };


    const QUOTE_STATUS_COLORS = {
        completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
        in_progress: "bg-amber-100 text-amber-700 border-amber-200",
        waiting_design: "bg-blue-100 text-blue-700 border-blue-200",
        waiting_assignment: "bg-gray-100 text-gray-500 border-gray-200",
        not_required: "bg-slate-50 text-slate-400 border-slate-200",
    };
    // const getQuoteColor = (designStatus) =>
    //     QUOTE_STATUS_COLORS[designStatus] || QUOTE_STATUS_COLORS.waiting_assignment;

    const renderQuoteChips = (job) => {
        const required = job.job_quotes?.filter(q => q.is_required) || [];
        // group by quote_type
        const grouped = required.reduce((acc, q) => {
            if (!acc[q.quote_type]) acc[q.quote_type] = [];
            acc[q.quote_type].push(q);
            return acc;
        }, {});

        const QUOTE_ICONS_STACK = {
            roof: <Home size={14} />,
            wall: <PanelsTopLeft size={14} />,
            floor: <HiOutlineSquare3Stack3D size={14} />,
            timber: <GiWoodBeam size={14} />,
            beams: <GiWoodPile size={14} />,
            flooring_sheets: <SquareStack size={14} />,
        };

        const QUOTE_ORDER = { beams: 0, flooring_sheets: 1, roof: 2, wall: 3, floor: 4, timber: 5 };

        return Object.entries(grouped)
            .sort(([typeA, quotesA], [typeB, quotesB]) => {
                const typeOrder =
                    (QUOTE_ORDER[typeA] ?? 99) - (QUOTE_ORDER[typeB] ?? 99);

                if (typeOrder !== 0) return typeOrder;

                const aChild = quotesA[0]?.child_unique_id || '';
                const bChild = quotesB[0]?.child_unique_id || '';

                return aChild.localeCompare(bChild);
            }).map(([type, quotes]) => {
                const count = quotes.length;

                return (
                    <div key={type} className="flex items-center gap-1 mr-2 mt-1">
                        {/* stacked icons */}
                        <div className="flex -space-x-[10px] bg-transparent">
                            {quotes.map((q, i) => (
                                <span
                                    key={q.id || q.child_unique_id || `${type}-${i}`}
                                    className={`p-1 rounded-full ${getQuoteColor(q.design_status)} bg-zinc-100 drop-shadow-sm border-2 border-white`} // ${getQuoteColor(q.design_status)}
                                    style={{
                                        left: i * 12,
                                        zIndex: count - i   // 👈 first icon gets highest z-index
                                    }}
                                >
                                    {QUOTE_ICONS_STACK[type]}
                                </span>
                            ))}
                        </div>

                        {/* count */}
                        <span className="text-xs text-gray-500">
                            ({count})
                        </span>
                    </div>
                );
            });
    };

    const SUB_STATUS_COLORS = {
        completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        in_progress: "bg-amber-50   text-amber-700   border-amber-200",
        in_estimation: "bg-amber-50   text-amber-700   border-amber-200",
        waiting_design: "bg-blue-50    text-blue-700    border-blue-200",
        waiting_assignment: "bg-gray-100   text-gray-500    border-gray-200",
        not_required: "bg-slate-50   text-slate-400   border-slate-200",
        in_cutting: "bg-orange-50  text-orange-700  border-orange-200",
        cutting_ended: "bg-orange-50  text-orange-700  border-orange-200",
        in_production: "bg-violet-50  text-violet-700  border-violet-200",
        not_started: "bg-gray-100   text-gray-400    border-gray-200",
    };
    const getSubStatusColor = (status) =>
        SUB_STATUS_COLORS[status] || "bg-gray-50 text-gray-500 border-gray-200";

    const renderStatusFields = (assignment, quote) => {
        if (!quote) return null;

        const jobType = assignment.job_type || "";
        const hasEstimation = jobType.includes("Estimation");
        const hasDesign =
            (jobType.includes("Design") && !jobType.includes("Estimation")) ||
            jobType === "Design & Estimation";

        const fields = [];

        if (hasEstimation) {
            const est = quote.estimation_status;
            if (est && est !== "not_required") {
                fields.push({
                    label: "Est.",
                    status: est,
                    text: est.replace(/_/g, " ")
                });
            }
        }

        if (jobType.includes("Design") || jobType === "Timber") {
            const des = quote.design_status;
            if (des && des !== "not_required") {
                fields.push({
                    label: "Design",
                    status: des,
                    text: des.replace(/_/g, " ")
                });
            }

            const prod = quote.production_status;
            if (prod && prod !== "not_required" && prod !== "not_started") {
                fields.push({
                    label: "Prod.",
                    status: prod,
                    text: prod.replace(/_/g, " ")
                });
            }
        }

        if (!fields.length) return null;

        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {fields.map((f, i) => (
                    <span
                        key={i}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${getSubStatusColor(f.status)}`}
                    >
                        <span className="text-[9px] font-bold opacity-60 uppercase">
                            {f.label}
                        </span>
                        {f.text.charAt(0).toUpperCase() + f.text.slice(1)}
                    </span>
                ))}
                <span
                    key={quote?.id + "-invoiced"}
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${quote?.invoiced ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
                >
                    <span className="text-[9px] font-bold opacity-60 uppercase">
                        {"INV"}
                    </span>
                    {quote?.invoiced ? "Invoiced" : "Not Invoiced"}
                </span>
            </div>
        );
    };


    const isSiteMeasureRequired =
        assignment?.site_measure_required && (
            assignment?.site_measure_truss ||
            assignment?.site_measure_wall ||
            assignment?.site_measure_floor
        )


    const safeParse = (value) => {
        if (!value) return null;

        // already an object → return as-is
        if (typeof value === "object") return value;

        // string → try parsing
        if (typeof value === "string") {
            try {
                return JSON.parse(value);
            } catch (e) {
                console.warn("Invalid JSON:", value);
                return null;
            }
        }

        return null;
    };

    const getChangedFields = (log) => {
        if (!log.old_values || !log.new_values) return [];

        try {
            const oldVals = safeParse(log.old_values);
            const newVals = safeParse(log.new_values);


            const keys = log.changed_fields || Object.keys(newVals);

            return keys.map((key) => ({
                field: key,
                oldValue: oldVals?.[key],
                newValue: newVals?.[key],
            }));
        } catch (err) {
            console.error("Failed to parse log values:", err);
            return [];
        }
    };

    const formatValue = (value) => {
        if (Array.isArray(value)) return value.join(", ");
        if (typeof value === "boolean") return value ? "Yes" : "No";
        if (value === null || value === undefined) return "—";
        return String(value);
    };

    const formatAustralianDisplayPhone = (phone) => {
        if (!phone) return '';

        let digits = String(phone).replace(/\D/g, '');

        // Remove country code
        if (digits.startsWith('61')) {
            digits = digits.slice(2);
        }

        // Add local leading zero
        if (!digits.startsWith('0')) {
            digits = `0${digits}`;
        }

        // Format 04XX XXX XXX
        if (digits.length <= 4) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;

        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
    };



    const pricingSettings = {
        send_email_on_estimation_complete: false,
        estimation_complete_email_user_ids: [],
        allow_estimators_finish_estimation_without_pricing: true,
        allow_designers_finish_estimation_without_pricing: true,
        ...(company?.settings?.pricing_settings || {}),
    };

    const ESTIMATOR_PRICING_FIELDS = [
        { key: 'roof', departments: ['roof'] },
        { key: 'labour', departments: ['roof'] },
        { key: 'first_floor', departments: ['floor'] },
        { key: 'second_floor', departments: ['floor'] },
        { key: 'gf_wall', departments: ['wall'] },
        { key: 'first_floor_wall', departments: ['wall'] },
        { key: 'second_floor_wall', departments: ['wall'] },
    ];

    const enabledPricingFields = useMemo(() => {
        return ESTIMATOR_PRICING_FIELDS.filter(field =>
            field.departments.some(department =>
                assignment?.job_quotes?.some(q => q.is_required && q.quote_type === department)
            )
        );
    }, [assignment?.job_quotes]);


    const hasAnyPricingValue = () =>
        Object.values(pricingValues || {}).some(unit =>
            Object.values(unit || {}).some(value => String(value || '').trim() !== '')
        );

    const hasAllRequiredPricingValues = () => {
        if (!enabledPricingFields.length) return true;

        return pricingUnitNumbers.every(unitNumber => {
            const unitValues = pricingValues?.[unitNumber] || {};

            return enabledPricingFields.every(field =>
                String(unitValues?.[field.key] || '').trim() !== ''
            );
        });
    };

    const mustEnterPricingBeforeEstimationComplete = () => {
        return pricingSettings.allow_estimators_finish_estimation_without_pricing === false;
    };

    const isEstimationPricingMissing = () => {
        return mustEnterPricingBeforeEstimationComplete() && !hasAllRequiredPricingValues();
    };

    // Establishes the "was pricing already complete before this session's
    // edits" baseline used by the false -> true transition check in
    // saveEstimationPricingEntries below. Keyed only on assignment?.id so it
    // runs once per assignment load (using pricingValues as it stands right
    // after the fetch — before any of the user's own edits accumulate) and
    // does NOT re-run on every keystroke. hasAllRequiredPricingValues is
    // deliberately excluded from the deps — it isn't memoized, so including
    // it would make this effect recompute the baseline from the user's
    // in-progress edits on every render instead of once from the fetched
    // data, defeating the whole point of the baseline.
    useEffect(() => {
        wasPricingCompleteRef.current = hasAllRequiredPricingValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignment?.id]);

    const formatLogCurrency = (value) => {
        if (value === null || value === undefined || value === '') return null;

        return `$${new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value))}`;
    };

    const createEstimationPricingLogs = async (rows) => {


        const logs = rows.map(row => ({
            job_id: assignment.id,
            company_id: company.id,
            user_id: userProfile.id,
            user_name: userProfile.full_name || userProfile.username,
            user_role: userProfile.role,

            action: 'estimation_pricing_created',

            details: `Estimator pricing entered for Unit ${row.unit_number}`,

            changed_fields: [
                'roof',
                'labour',
                'first_floor',
                'second_floor',
                'gf_wall',
                'first_floor_wall',
                'second_floor_wall',
            ].filter(field => row[field] !== null),

            old_values: {},

            new_values: {
                roof: formatLogCurrency(row.roof),
                labour: formatLogCurrency(row.labour),
                first_floor: formatLogCurrency(row.first_floor),
                second_floor: formatLogCurrency(row.second_floor),
                gf_wall: formatLogCurrency(row.gf_wall),
                first_floor_wall: formatLogCurrency(row.first_floor_wall),
                second_floor_wall: formatLogCurrency(row.second_floor_wall),
            },

            action_origin: 'estimation_pricing',
            timestamp: new Date().toISOString(),
        }));

        const { error } = await supabase
            .from('job_action_logs')
            .insert(logs);

        if (error) {
            console.error('Failed to create estimation pricing logs:', error);
        }
    };

    const saveEstimationPricingEntries = async () => {
        if (!enabledPricingFields.length || !hasAnyPricingValue()) return;

        const userName = userProfile.full_name || userProfile.username;

        const rows = pricingUnitNumbers.map(unitNumber => {
            const values = pricingValues[unitNumber] || {};

            const customPrices = {};
            const customLabels = {};
            const fieldMeta = {};

            enabledPricingFields.forEach(field => {
                const value = values[field.key];

                if (field.is_custom) {
                    customPrices[field.key] = value;
                    customLabels[field.key] = field.label;
                }

                if (String(value || "").trim() !== "") {
                    fieldMeta[field.key] = {
                        updated_by: userProfile.id,
                        updated_by_name: userName,
                        updated_at: new Date().toISOString(),
                        value,
                    };
                }
            });

            return {
                company_id: company.id,
                job_id: assignment.id,
                child_id: null,
                type: 'estimation',
                department: null,
                unit_number: unitNumber,

                roof: values.roof || null,
                labour: values.labour || null,
                first_floor: values.first_floor || null,
                second_floor: values.second_floor || null,
                gf_wall: values.gf_wall || null,
                first_floor_wall: values.first_floor_wall || null,
                second_floor_wall: values.second_floor_wall || null,

                created_by: userProfile.id,
                created_by_name: userName,
                updated_by: userProfile.id,
                updated_by_name: userName,

                quote_revision: 1,
                is_archived: false,
                field_meta: fieldMeta,

                custom_prices: customPrices,
                custom_price_labels: customLabels,
            };
        });

        const { error } = await supabase
            .from('job_pricing_entries')
            .upsert(rows, {
                onConflict: 'job_id,type,unit_number,child_id_key,department_key,quote_revision'
            });

        if (error) throw error;

        await createEstimationPricingLogs(rows);

        const isNowComplete = hasAllRequiredPricingValues();

        if (!wasPricingCompleteRef.current && isNowComplete) {
            const notifyUserIds = company?.settings?.pricing_settings?.estimation_complete_email_user_ids || [];

            // Fetched fresh rather than read off local component state — by
            // the time this "just became fully priced" transition fires, the
            // complete picture may span job_pricing_entries rows saved
            // across earlier calls/units too, not just the `rows` just
            // upserted above.
            (async () => {
                const { data: allEntries, error: entriesError } = await supabase
                    .from('job_pricing_entries')
                    .select('*')
                    .eq('job_id', assignment.id)
                    .eq('is_archived', false);

                if (entriesError) throw entriesError;

                await notifyPricingEntered({
                    job: assignment,
                    company,
                    companySlug,
                    userIds: notifyUserIds,
                    pricingEntries: allEntries || [],
                    // This form always upserts job_pricing_entries at quote_revision: 1
                    // (see saveEstimationPricingEntries above) — it has no revision concept.
                    quoteRevision: 1,
                });
            })().catch(err => console.error('Failed to send pricing entered notification:', err));
        }

        wasPricingCompleteRef.current = isNowComplete;
    };



    const pricingUnitNumbers = useMemo(() => {
        const keys = Object.keys(pricingValues || {}).map(Number).filter(Boolean);
        return keys.length ? keys : [1];
    }, [pricingValues]);

    if (!assignment) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500">Loading assignment details...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="mobile-detail-page job-detail-page px-4 md:px-0">
                {/* Header */}
                <div className="sticky top-0 z-[50] bg-white flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-200 py-3 md:py-4 gap-3 transition-all duration-200" style={isScrolled ? { boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)" } : {}}>
                    <div className="flex items-start gap-2 md:gap-4 w-full">
                        {/* <button
                                onClick={() => navigate(`/${companySlug}/assignment/list`)}
                                className="p-2 md:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                            >
                                <FiArrowLeft className="w-5 h-5" />
                            </button> */}
                        <BackButtonIconOnly className="bg-transparent !text-blue-600 border border-blue-500 hover:!text-white w-10 h-10" />
                        <div className="flex-1">
                            <h1 className="text-lg md:text-xl font-bold text-gray-900 mb-1 break-words items-center content-center flex flex-row">
                                {assignment.unique_id}
                                {userProfile?.role !== "driver" &&
                                    <span className={`mx-2 px-3 mt-[0.2rem] text-sm self-center rounded-full font-medium border bg-gray-200`}>
                                        {assignment?.job_type}
                                    </span>
                                }

                                {customerTypeMeta &&
                                    <div className={`flex flex-row items-center gap-2 mx-2 px-3 mt-[0.2rem] text-sm self-center rounded-full font-medium border shadow-sm ${customerTypeMeta.card}`}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                                            Customer Type
                                        </p>
                                        <p className="text-sm font-bold text-zinc-800">
                                            {customerTypeMeta.label}
                                        </p>
                                    </div>
                                }

                                <span className="text-xs mt-[0.2rem] md:text-sm font-medium opacity-70 ml-2">#{assignment?.short_id}</span>
                            </h1>



                            {assignment?.is_cancelled && (
                                <div className="my-2 w-fit rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                        <FiX className="w-5 h-5 text-red-700" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-red-800">This job is cancelled</p>
                                        <p className="text-xs text-red-700 mt-0.5">
                                            Actions are disabled until this job is restored.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {userProfile?.role !== "driver" &&
                                <div className="flex flex-wrap items-center gap-2">
                                    {/* <StatusBadge status={assignment.main_status} large /> */}
                                    <DisplayStatusCards assignment={assignment} />
                                    {assignment?.job_quotes?.some(q => q.is_required && q.layout_only) && (
                                        <span className="px-2 md:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                                            Layout Only
                                        </span>
                                    )}
                                    {(assignment.urgent_quote || assignment.urgent_design) && (
                                        <span className="px-2 md:px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium border border-red-200">
                                            URGENT
                                        </span>
                                    )}
                                    {/* <span className="px-2 md:px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                                        {assignment.job_type}
                                    </span> */}
                                </div>
                            }
                        </div>
                    </div>

                    {hasPermission(userProfile?.role, ["super_admin", "admin", "estimator", "designer"]) &&
                        <div className="flex gap-2 ml-auto md:ml-0">
                            <CancelledAction>
                                <button
                                    onClick={() => setEditOpen(true)}
                                    className="p-2 md:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                                >
                                    <FiEdit3 className="w-5 h-5" />
                                </button>
                            </CancelledAction>
                        </div>
                    }
                </div>

                <div className="space-y-4 mt-4">
                    {/* Main Content - Customer, Supervisor, etc. */}
                    <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">

                        <div className="flex flex-col gap-2 !h-full">
                            {/* Customer Information */}
                            <InfoCard title="Customer Information" icon={FiUser} color="blue">
                                <DetailRow
                                    label="Customer Name"
                                    value={
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span>{assignment.customer_name || "Not specified"}</span>

                                            {customerTypeMeta && (
                                                <span className={`px-2 rounded-full border text-[10px] font-bold uppercase ${customerTypeMeta.badge}`}>
                                                    {customerTypeMeta.label}
                                                </span>
                                            )}
                                        </div>
                                    }
                                    icon={FiUser}
                                />
                                <DetailRow
                                    label="Phone Number"
                                    value={formatAustralianDisplayPhone(assignment.customer_number)}
                                    icon={FiPhone}
                                    link={assignment.customer_number && `tel:${assignment.customer_number}`}
                                    onCopy={
                                        hasPhoneNumber
                                            ? () => copyToClipboard(assignment.customer_number, "Phone number")
                                            : null
                                    }
                                />
                                <DetailRow
                                    label="Email Address"
                                    value={assignment.customer_mail}
                                    icon={FiMail}
                                    link={assignment.customer_mail && `mailto:${assignment.customer_mail}`}
                                    onCopy={
                                        hasEmail
                                            ? () => copyToClipboard(assignment.customer_mail, "Email")
                                            : null
                                    }
                                />
                                <DetailRow
                                    label="Job Address"
                                    value={assignment?.customer_address}
                                    icon={FiMap}
                                    link={
                                        hasJobAddress
                                            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.customer_address)}`
                                            : null
                                    }
                                    onCopy={
                                        hasJobAddress
                                            ? () => copyToClipboard(assignment.customer_address, "Job address")
                                            : null
                                    }
                                />

                                {(assignment.customer_note || assignment.customer_notes?.some(n => n?.note?.trim())) && (
                                    <DetailRow
                                        label="Customer Note"
                                        value={<CustomerNotesBox assignment={assignment} />}
                                        icon={NotebookPen}
                                    />
                                )}

                                <DropdownSection
                                    title="Supervisor Information"
                                    titleStyle="text-zinc-700 text-xs"
                                    icon={FiUser}
                                    iconStyle="text-zinc-800 !w-4 !h-4"
                                    subtext={``}
                                    subtextStyle="text-xs "
                                    defaultOpen={false}
                                >
                                    <DetailRow label="Supervisor Name" value={assignment.carpenter_supervisor_name} icon={FiUser} />
                                    <DetailRow
                                        label="Phone Number"
                                        value={formatAustralianDisplayPhone(assignment.carpenter_supervisor_number)}
                                        icon={FiPhone}
                                        link={assignment.carpenter_supervisor_number && `tel:${assignment.carpenter_supervisor_number}`}
                                    />
                                </DropdownSection>
                            </InfoCard>

                            {/* Timeline */}
                            <InfoCard title="Timeline" icon={FiCalendar} color="orange" customClass="flex-1">
                                {/* <DetailRow label="Created" value={formatDate(assignment.created_at)} icon={FiCalendar} />
                            <DetailRow label="Last Updated" value={formatDate(assignment.updated_at)} icon={FiClock} /> */}
                                {assignment.job_type !== "Design" &&
                                    <div className="pt-2 mt-2">
                                        {(() => {

                                            let assignedEstimator = assignment?.job_estimators;
                                            let date = null;
                                            let text = "No estimator assigned"

                                            if (assignedEstimator && assignedEstimator?.completed_at === null) text = "Estimation in progress"
                                            if (assignedEstimator && assignedEstimator?.completed_at !== null) date = assignedEstimator?.completed_at;

                                            return (
                                                <DetailRow label="Estimation Completion Date" value={date !== null ? formatDate(date) : text} icon={FiFlag} />
                                            )
                                        })()}
                                    </div>
                                }

                                {/* Booked dates per quote */}
                                {(() => {
                                    const QUOTE_COLORS = {
                                        roof: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-500', label: 'text-blue-700', icon: <Home className="w-3 h-3" /> },
                                        wall: { bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-500', label: 'text-red-700', icon: <PanelsTopLeft className="w-3 h-3" /> },
                                        floor: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500', label: 'text-green-700', icon: <HiOutlineSquare3Stack3D className="w-3 h-3" /> },
                                        timber: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-500', label: 'text-amber-700', icon: <GiWoodBeam className="w-3 h-3" /> },
                                    };
                                    const QUOTE_ORDER = { roof: 0, wall: 1, floor: 2, timber: 3 };

                                    const bookedQuotes = (assignment.job_quotes ?? [])
                                        .filter(q => q.is_required && (q.delivery_count === null || (q.delivery_count ?? 0) === 0))
                                        .filter(q => q.expected_finish_date || q.expected_delivery_date)
                                        .sort((a, b) => {
                                            const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                                            if (typeOrder !== 0) return typeOrder;
                                            return (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                        });

                                    if (bookedQuotes.length === 0) return null;

                                    // Booked dates are 'date' PG columns → already "YYYY-MM-DD" strings,
                                    // matching the holiday map's key format directly.
                                    const getHolidaysForBookedDate = (dateString) => {
                                        if (!dateString) return [];
                                        return holidayMap.get(dateString.slice(0, 10)) || [];
                                    };

                                    return (
                                        <DropdownSection
                                            title="Booked Dates"
                                            titleStyle="text-zinc-700 text-xs"
                                            icon={Calendar}
                                            iconStyle="text-zinc-800 !w-4 !h-4"
                                            subtext={``}
                                            subtextStyle="text-xs"
                                            defaultOpen={true}
                                        >
                                            <div className="border-gray-100">
                                                <div className="space-y-1.5">
                                                    {bookedQuotes.map(q => {
                                                        const C = QUOTE_COLORS[q.quote_type] ?? {
                                                            bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-400', label: 'text-gray-700', icon: <FiCalendar className="w-3 h-3" />
                                                        };
                                                        const finish = q.expected_finish_date;
                                                        const delivery = q.expected_delivery_date;
                                                        const bothSet = finish && delivery;
                                                        const differ = bothSet && finish !== delivery;
                                                        const etaHolidays = getHolidaysForBookedDate(delivery || finish);

                                                        return (
                                                            <div key={q.id} className={`rounded-lg border ${C.border} ${C.bg} px-2.5 py-2 flex items-start gap-2`}>
                                                                {/* Quote type icon */}
                                                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${C.iconBg} text-white`}>
                                                                    {C.icon}
                                                                </div>

                                                                {/* Label + dates */}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`text-[11px] font-semibold capitalize leading-none mb-1 flex items-center gap-1.5 ${C.label}`}>
                                                                        {q?.quote_type?.split("_")?.join(" ") || q.quote_type}
                                                                        {q.child_unique_id && (
                                                                            <span className="ml-1 font-normal text-gray-500">· {q.child_unique_id}</span>
                                                                        )}
                                                                        {q?.on_hold && <OnHoldBadge />}
                                                                    </p>

                                                                    <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                                                        <FiCalendar className="w-3 h-3 flex-shrink-0 text-gray-400" />
                                                                        <span className="text-gray-400">ETA:</span>
                                                                        <span className="font-medium">{formatDateDriver(delivery || finish)}</span>
                                                                    </div>

                                                                    {etaHolidays.length > 0 && (
                                                                        <p className="text-[11px] mt-1 flex items-center gap-1 text-amber-700 font-semibold" title={etaHolidays.join(', ')}>
                                                                            <PartyPopper className="w-3 h-3 flex-shrink-0" />
                                                                            <span className="truncate">
                                                                                {etaHolidays[0]}{etaHolidays.length > 1 ? ` +${etaHolidays.length - 1}` : ''}
                                                                            </span>
                                                                        </p>
                                                                    )}

                                                                    {/* {differ ? (
                                                                        <div className="space-y-0.5">
                                                                            <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                                                                <FiFlag className="w-3 h-3 flex-shrink-0 text-gray-400" />
                                                                                <span className="text-gray-400">Finish:</span>
                                                                                <span className="font-medium">{formatDateDriver(finish)}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                                                                <FiTruck className="w-3 h-3 flex-shrink-0 text-gray-400" />
                                                                                <span className="text-gray-400">Delivery:</span>
                                                                                <span className="font-medium">{formatDateDriver(delivery)}</span>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                                                            <FiCalendar className="w-3 h-3 flex-shrink-0 text-gray-400" />
                                                                            <span className="text-gray-400">ETA:</span>
                                                                            <span className="font-medium">{formatDateDriver(delivery || finish)}</span>
                                                                        </div>
                                                                    )} */}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </DropdownSection>
                                    );
                                })()}

                                <div className="ml-auto w-fit">
                                    <div className="flex flex-wrap items-center justify-end gap-2">
                                        <CancelledAction>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowConversationModal(true);
                                                }}
                                                className="relative flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-[0.4rem] text-xs font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                <FiMessageSquare className="w-4 h-4" />
                                                Open Conversation

                                                {inboundConversationCount > 0 && (
                                                    <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white shadow">
                                                        {inboundConversationCount > 9 ? '9+' : inboundConversationCount}
                                                    </span>
                                                )}
                                            </button>
                                        </CancelledAction>
                                        <JobBookedDatesEmailSender
                                            assignment={assignment}
                                            userProfile={userProfile}
                                            company={company}
                                        />
                                    </div>
                                </div>
                            </InfoCard>

                        </div>

                        {/* Supervisor Information */}
                        <InfoCard title="Job Requirements" icon={FiTool} color="green">
                            <div className="flex flex-col gap-4">
                                {assignment?.job_type?.toLowerCase()?.includes("design") &&
                                    <DetailRow
                                        label="Required Sections"
                                        value={renderQuoteChips(assignment)}
                                        icon={FiCheckCircle}
                                        extraStyle="flex flex-col gap-1"
                                    />
                                }
                                {/* <DetailRow label="Job Type" value={assignment.job_type} icon={FiTool} /> */}
                                {assignment.job_type !== "Timber" && (
                                    <DetailRow
                                        label="Priority Settings"
                                        value={(
                                            <div className="flex flex-col gap-1 mt-2">
                                                {assignment?.job_type !== "Design" &&
                                                    <CheckboxDisplay label="Urgent Quote" isChecked={assignment.urgent_quote} />
                                                }
                                                {assignment?.job_type !== "Estimation" &&
                                                    <CheckboxDisplay label="Urgent Design" isChecked={assignment.urgent_design} />
                                                }
                                            </div>
                                        )}
                                        icon={FiClock}
                                    />
                                )}
                                <DetailRow
                                    label="Unit Job"
                                    value={assignment?.is_unit ? `Yes (${assignment?.unit_count} Unit${assignment?.unit_count > 1 ? "s" : ""})` : `No`}
                                    icon={SquareStack}
                                />
                                <DetailRow
                                    label={
                                        <div className="flex items-center gap-2">
                                            <span>Site Measure</span>
                                            {isSiteMeasureRequired && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500 text-white font-semibold">
                                                    REQUIRED
                                                </span>
                                            )}
                                        </div>
                                    }
                                    value={(
                                        <div className="flex flex-col gap-1 mt-1">
                                            {assignment?.job_quotes?.some(q => q?.quote_type === "roof" && q?.is_required) && (
                                                <CheckboxDisplay
                                                    label="Roof Measure"
                                                    isChecked={assignment?.site_measure_required && assignment?.site_measure_truss}
                                                />
                                            )}

                                            {assignment?.job_quotes?.some(q => q?.quote_type === "wall" && q?.is_required) && (<CheckboxDisplay
                                                label="Wall Measure"
                                                isChecked={assignment?.site_measure_required && assignment?.site_measure_wall}
                                            />
                                            )}

                                            {assignment?.job_quotes?.some(q => q?.quote_type === "floor" && q?.is_required) && (<CheckboxDisplay
                                                label="Floor Measure"
                                                isChecked={assignment?.site_measure_required && assignment?.site_measure_floor}
                                            />
                                            )}
                                        </div>
                                    )}
                                    icon={PencilRuler}
                                    containerStyle={`relative pl-3 py-1 pr-2 rounded transition-all ${isSiteMeasureRequired
                                        ? "bg-green-50 border border-green-200"
                                        : ""
                                        }`}
                                />
                                <DropdownSection
                                    title="Invoice"
                                    titleStyle="text-zinc-700 text-xs"
                                    icon={Receipt}
                                    iconStyle="text-zinc-800 !w-4 !h-4"
                                    subtext={``}
                                    subtextStyle="text-xs"
                                    defaultOpen={true}
                                >
                                    {(() => {
                                        const jobQuotes = (assignment.job_quotes ?? [])
                                            .filter(q => q.is_required)
                                            .sort((a, b) => {
                                                const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                                                if (typeOrder !== 0) return typeOrder;

                                                const childCompare = (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                                if (childCompare !== 0) return childCompare;

                                                return (a.delivery_count || 0) - (b.delivery_count || 0);
                                            });

                                        const groupedJobQuotes = getSortedQuoteTypeEntries(jobQuotes);

                                        // const INVOICE_META = {
                                        //     not_invoiced: {
                                        //         label: "Not Inv",
                                        //         wrap: "bg-red-50 border-red-200",
                                        //         icon: "bg-red-100 text-red-700",
                                        //         badge: "bg-red-100 text-red-700 border-red-200",
                                        //     },
                                        //     invoiced: {
                                        //         label: "Inv",
                                        //         wrap: "bg-blue-50 border-blue-200",
                                        //         icon: "bg-blue-100 text-blue-700",
                                        //         badge: "bg-blue-100 text-blue-700 border-blue-200",
                                        //     },
                                        //     cod: {
                                        //         label: "COD",
                                        //         wrap: "bg-sky-50 border-sky-200",
                                        //         icon: "bg-sky-100 text-sky-700",
                                        //         badge: "bg-sky-100 text-sky-700 border-sky-200",
                                        //     },
                                        //     paid: {
                                        //         label: "Paid",
                                        //         wrap: "bg-green-50 border-green-200",
                                        //         icon: "bg-green-100 text-green-700",
                                        //         badge: "bg-green-100 text-green-700 border-green-200",
                                        //     },
                                        //     deposit: {
                                        //         label: "Deposit",
                                        //         wrap: "bg-amber-50 border-amber-200",
                                        //         icon: "bg-amber-100 text-amber-700",
                                        //         badge: "bg-amber-100 text-amber-700 border-amber-200",
                                        //     },
                                        // };

                                        const getDisplayName = (quote) => quote?.child_unique_id || quote?.quote_type;

                                        if (!jobQuotes.length) {
                                            return (
                                                <div className="text-xs text-gray-400 italic px-1 py-1">
                                                    No invoice items
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="space-y-3">
                                                {groupedJobQuotes.map(group => (
                                                    <DropdownSection
                                                        key={`invoice-group-${group.quoteType}`}
                                                        title={group.label}
                                                        titleStyle="text-zinc-600 text-xs"
                                                        iconStyle="text-zinc-600"
                                                        subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                                        subtextStyle="text-[11px]"
                                                        defaultOpen={false}
                                                    >
                                                        <div className="flex flex-wrap gap-2">
                                                            {group.items.map(quote => {
                                                                const invoiceState = (
                                                                    quote?.xero_invoice_status ||
                                                                    quote?.invoice_status ||
                                                                    quote?.invoice_state ||
                                                                    "not_invoiced"
                                                                )
                                                                    .toString()
                                                                    .toLowerCase()
                                                                    .replaceAll(" ", "_");


                                                                const meta = INVOICE_META[invoiceState] || INVOICE_META.not_invoiced;
                                                                const displayName = getDisplayName(quote);
                                                                const isRedelivery = quote?.delivery_count != null && quote?.delivery_count > 0;

                                                                const icon = getInvoiceSourceIcon(quote, company, 'sm');
                                                                const text = getInvoiceStatusText(quote, company);

                                                                const source = getInvoiceStatusSource(quote, company);
                                                                const canOpenXeroPdf = source === 'xero' && !!quote?.xero_invoice_id;

                                                                return (
                                                                    <CancelledAction>
                                                                        <button
                                                                            type="button"
                                                                            key={`${quote.id}-inv`}
                                                                            className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${meta.wrap}`}
                                                                            onClick={() => canOpenXeroPdf && openXeroInvoicePdf(quote)}
                                                                            disabled={!canOpenXeroPdf}
                                                                            title={canOpenXeroPdf ? 'View Xero invoice PDF' : 'PDF available only for linked Xero invoices'}
                                                                        >
                                                                            <div className={`rounded-md p-1 ${meta.icon}`}>
                                                                                {quote?.quote_type === 'roof' && <Home className="w-3.5 h-3.5" />}
                                                                                {quote?.quote_type === 'wall' && <PanelsTopLeft className="w-3.5 h-3.5" />}
                                                                                {quote?.quote_type === 'floor' && <HiOutlineSquare3Stack3D className="w-3.5 h-3.5" />}
                                                                                {quote?.quote_type === 'timber' && <GiWoodBeam className="w-3.5 h-3.5" />}
                                                                            </div>

                                                                            <span className="text-xs font-semibold text-gray-800 capitalize">
                                                                                {displayName}
                                                                            </span>

                                                                            {isRedelivery && (
                                                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-white/80 border border-gray-200 text-gray-600">
                                                                                    D#{quote.delivery_count}
                                                                                </span>
                                                                            )}
                                                                            <div className={`flex flex-row items-center text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md border ${meta.badge}`}>
                                                                                {icon}
                                                                                <span className={`ml-1`}>
                                                                                    {text}
                                                                                </span>
                                                                            </div>


                                                                        </button>
                                                                    </CancelledAction>
                                                                );
                                                            })}
                                                        </div>
                                                    </DropdownSection>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </DropdownSection>
                            </div>
                        </InfoCard>

                        {/* Job Requirements */}
                        {/* <InfoCard title="Job Requirements" icon={FiLayers} color="purple">
                            {assignment?.job_type?.toLowerCase()?.includes("design") &&
                                <DetailRow
                                    label="Required Sections"
                                    value={renderQuoteChips(assignment)}
                                    icon={FiCheckCircle}
                                />
                            }
                            <DetailRow label="Job Type" value={assignment.job_type} icon={FiTool} />
                            {assignment.job_type !== "Timber" && (
                                <DetailRow
                                    label="Priority Settings"
                                    value={(
                                        <div className="flex flex-col gap-2 mt-2">
                                            {assignment?.job_type !== "Design" &&
                                                <CheckboxDisplay label="Urgent Quote" isChecked={assignment.urgent_quote} />
                                            }
                                            {assignment?.job_type !== "Estimation" &&
                                                <CheckboxDisplay label="Urgent Design" isChecked={assignment.urgent_design} />
                                            }
                                        </div>
                                    )}
                                    icon={FiClock}
                                />
                            )}
                        </InfoCard> */}


                    </div>


                    {/* Driver Delivery Section - Only for assigned driver, showing all deliveries */}
                    {hasPermission(userProfile?.role, ["driver"]) && getDriverAssignedDeliveries().length > 0 && (
                        <div className="space-y-4 md:space-y-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 pb-2">
                                    My Deliveries
                                </h2>
                                <span className="text-xs text-gray-500">
                                    {getDriverAssignedDeliveries().length} delivery assignment{getDriverAssignedDeliveries().length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div className="space-y-4">
                                {getDriverAssignedDeliveries().map(delivery => {
                                    const quoteType = delivery.quote_type;
                                    const deliveryCount = delivery.delivery_count ?? 0;
                                    const ordinalSuffix = deliveryCount === 0 ? '' : deliveryCount === 1 ? '2nd ' : deliveryCount === 2 ? '3rd ' : `${deliveryCount + 1}th `;


                                    // delivery.child_unique_id is job_quotes.id (UUID) — present for new-system rows.
                                    // quote.child_unique_id is the human display ID (NEWR1, NEWR2, …).
                                    const quote = delivery.child_unique_id
                                        ? assignment.job_quotes?.find(q => q.id === delivery.child_unique_id)
                                        : assignment.job_quotes?.find(q =>
                                            q.quote_type === quoteType &&
                                            !q.child_unique_id &&   // don't steal a named-child row
                                            (q.delivery_count === deliveryCount || (deliveryCount === 0 && (q.delivery_count === null || q.delivery_count === 0)))
                                        );
                                    const deliveryPhotos = assignment.job_files?.filter(f => {
                                        if (f.file_type !== 'delivery' || f.quote_type !== quoteType) return false;
                                        // Scope by child delivery: new-system rows match on child_id; old-system rows have no child_id
                                        if (delivery.child_unique_id) {
                                            return f.child_id === delivery.child_unique_id;
                                        }
                                        if (f.child_id) return false; // belongs to a named delivery, not this one
                                        if (deliveryCount === 0) {
                                            return f.delivery_count === 0 || f.delivery_count === null || f.delivery_count === undefined;
                                        }
                                        return f.delivery_count === deliveryCount;
                                    }) || [];

                                    const statusConfig = {
                                        'not_scheduled': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' },
                                        'scheduled': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
                                        'in_transit': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
                                        'delivered': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' }
                                    };

                                    const config = statusConfig[delivery?.delivery_status] || statusConfig['not_scheduled'];

                                    return (
                                        <div key={`${quoteType}-${deliveryCount}`} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                            {/* Header */}
                                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${quoteType === 'roof' ? 'bg-blue-100 text-blue-600' :
                                                            quoteType === 'wall' ? 'bg-red-100 text-red-600' :
                                                                quoteType === 'timber' ? 'bg-amber-100 text-amber-600' :
                                                                    'bg-green-100 text-green-600'
                                                            }`}>
                                                            {quoteType === 'roof' ? <Home className="w-3.5 h-3.5" /> :
                                                                quoteType === 'wall' ? <PanelsTopLeft className="w-3.5 h-3.5" /> :
                                                                    quoteType === 'floor' ? <HiOutlineSquare3Stack3D className="w-3.5 h-3.5" /> :
                                                                        <GiWoodBeam className="w-3.5 h-3.5" />}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-800 capitalize">
                                                                {ordinalSuffix}{quoteType}{quote?.child_unique_id ? ` (${quote?.child_unique_id})` : ""} Delivery
                                                            </h3>
                                                        </div>
                                                    </div>
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium w-fit ${delivery?.delivery_status === 'delivered' ? 'bg-green-100 text-green-700' :
                                                        delivery?.delivery_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                                                            delivery?.delivery_status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {delivery?.delivery_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-4 space-y-3">
                                                {/* Delivery Details */}
                                                <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiCalendar className="w-3.5 h-3.5 text-gray-500" />
                                                            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Schedule</h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <DetailRow
                                                                label="Delivery Date"
                                                                value={formatDateDriver(delivery?.delivery_date || quote?.expected_delivery_date)}
                                                            />
                                                            {/* <DetailRow
                                                                label="Delivery Time"
                                                                value={delivery?.delivery_time || "Not specified"}
                                                            /> */}
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiUser className="w-3.5 h-3.5 text-gray-500" />
                                                            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <DetailRow
                                                                label="Contact Name"
                                                                value={delivery?.delivery_contact_name || assignment.customer_name}
                                                            />
                                                            <DetailRow
                                                                label="Phone"
                                                                value={delivery?.delivery_contact_phone || assignment.customer_number}
                                                                link={`tel:${delivery?.delivery_contact_phone || assignment.customer_number}`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Delivery Address */}
                                                {hasJobAddress && (
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.customer_address)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                                                        >
                                                            <FiMapPin className="w-3 h-3" />
                                                            Open in Maps
                                                        </a>

                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                copyToClipboard(assignment.customer_address);
                                                            }}
                                                            className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1"
                                                        >
                                                            <FiCopy className="w-3 h-3" />
                                                            Copy
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Special Instructions */}
                                                {quote?.delivery_note && (
                                                    <div className="bg-white rounded-lg border border-amber-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiAlertCircle className="w-4 h-4 text-amber-600" />
                                                            <h4 className="font-semibold text-amber-900">Delivery Note</h4>
                                                        </div>
                                                        <p className="text-sm text-red-600 font-semibold">{quote.delivery_note}</p>
                                                    </div>
                                                )}

                                                {/* Special Instructions */}
                                                {delivery?.special_instructions && (
                                                    <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiAlertCircle className="w-4 h-4 text-amber-600" />
                                                            <h4 className="font-semibold text-amber-900">Delivery Instructions</h4>
                                                        </div>
                                                        <p className="text-sm text-amber-800">{delivery.special_instructions}</p>
                                                    </div>
                                                )}

                                                {/* Action Buttons Based on Delivery Status */}
                                                <div className="space-y-4 pt-4 border-t border-gray-200">
                                                    {(!delivery?.delivery_status || delivery?.delivery_status === 'scheduled') ? (
                                                        // Step 1: Confirm Package Receipt
                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                            <div className="flex items-center gap-2.5 mb-3">
                                                                <div className="w-8 h-8 bg-gray-700 rounded-md flex items-center justify-center flex-shrink-0">
                                                                    <FiCheckCircle className="w-4 h-4 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-gray-800">Step 1: Confirm Package</h4>
                                                                    <p className="text-xs text-gray-500">Confirm that you have received the {quoteType} package</p>
                                                                </div>
                                                            </div>
                                                            <CancelledAction>
                                                                <button
                                                                    onClick={() => handleConfirmPackage(quoteType, deliveryCount, delivery.child_unique_id || null)}
                                                                    onTouchEnd={(e) => { e.preventDefault(); handleConfirmPackage(quoteType, deliveryCount, delivery.child_unique_id || null); }}
                                                                    className="w-full py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg transition-all min-h-[44px] [touch-action:manipulation]"
                                                                >
                                                                    I Have the Package
                                                                </button>
                                                            </CancelledAction>
                                                        </div>
                                                    ) : delivery?.delivery_status === 'in_transit' ? (
                                                        // Step 2: Upload Delivery Photos
                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                            <div className="flex items-center gap-2.5 mb-3">
                                                                <div className="w-8 h-8 bg-gray-700 rounded-md flex items-center justify-center flex-shrink-0">
                                                                    <FiUpload className="w-4 h-4 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-gray-800">Step 2: Upload Delivery Photos</h4>
                                                                    <p className="text-xs text-gray-500">Take photos of the delivered {quoteType} package</p>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-4">
                                                                <div>
                                                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                                                        Upload Photos *
                                                                    </label>
                                                                    <DragDropUploader
                                                                        onFilesChange={handleDeliveryPhotosChange(quoteType, deliveryCount, delivery.child_unique_id || null)}
                                                                        clearFiles={clearDeliveryPhotos[`${quoteType}-${deliveryCount}-${delivery.child_unique_id || ''}`]}
                                                                        acceptedTypes=".jpg,.jpeg,.png"
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                                                        Delivery Notes (Optional)
                                                                    </label>
                                                                    <textarea
                                                                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm md:text-base"
                                                                        value={deliveryNotes[`${quoteType}-${deliveryCount}-${delivery.child_unique_id || ''}`] || ''}
                                                                        onChange={(e) => setDeliveryNotes(prev => ({
                                                                            ...prev,
                                                                            [`${quoteType}-${deliveryCount}-${delivery.child_unique_id || ''}`]: e.target.value
                                                                        }))}
                                                                        rows="3"
                                                                        placeholder={`Add any notes about the ${quoteType} delivery...`}
                                                                    />
                                                                </div>

                                                                <CancelledAction>
                                                                    <button
                                                                        onClick={() => handleUploadDeliveryPhotos(quoteType, deliveryCount, delivery.child_unique_id || null)}
                                                                        onTouchEnd={(e) => { e.preventDefault(); handleUploadDeliveryPhotos(quoteType, deliveryCount, delivery.child_unique_id || null); }}
                                                                        disabled={isSubmittingDelivery || !uploadedDeliveryPhotos[`${quoteType}-${deliveryCount}-${delivery.child_unique_id || ''}`]?.length}
                                                                        className="w-full py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px] [touch-action:manipulation]"
                                                                    >
                                                                        {isSubmittingDelivery ? (
                                                                            <>
                                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                                Uploading...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FiUpload className="w-4 h-4" />
                                                                                Upload Photos
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </CancelledAction>

                                                                {/* Show uploaded photos if any */}
                                                                {deliveryPhotos.length > 0 && (
                                                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                                                        <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">
                                                                            Uploaded Photos ({deliveryPhotos.length})
                                                                        </p>
                                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                                                                            {deliveryPhotos.map((photo, index) => {
                                                                                const { data: { publicUrl } } = supabase.storage
                                                                                    .from('delivery-uploads') // Changed from 'job-files'
                                                                                    .getPublicUrl(photo.filename);

                                                                                return (
                                                                                    <div key={index} className="relative aspect-square bg-gray-50 rounded-lg border border-gray-200 overflow-hidden group">
                                                                                        <img
                                                                                            src={publicUrl}
                                                                                            alt={`Delivery photo ${index + 1}`}
                                                                                            className="w-full h-full object-contain p-1"
                                                                                        />
                                                                                        <CancelledAction>
                                                                                            <button
                                                                                                onClick={() => handleDeleteDeliveryPhoto(photo.id, photo.filename)}
                                                                                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                                title="Delete photo"
                                                                                            >
                                                                                                <FiX className="w-3.5 h-3.5" />
                                                                                            </button>
                                                                                        </CancelledAction>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>

                                                                        {/* Step 3: End Delivery */}
                                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                                            <div className="flex items-center gap-2.5 mb-3">
                                                                                <div className="w-7 h-7 bg-gray-700 rounded-md flex items-center justify-center flex-shrink-0">
                                                                                    <FiCheckCircle className="w-3.5 h-3.5 text-white" />
                                                                                </div>
                                                                                <div>
                                                                                    <h4 className="text-sm font-semibold text-gray-800">Step 3: Complete Delivery</h4>
                                                                                    <p className="text-xs text-gray-500">Finalize the {quoteType} delivery</p>
                                                                                </div>
                                                                            </div>
                                                                            <CancelledAction>
                                                                                <button
                                                                                    onClick={() => handleEndDelivery(quoteType, deliveryCount, delivery.child_unique_id || null)}
                                                                                    onTouchEnd={(e) => { e.preventDefault(); handleEndDelivery(quoteType, deliveryCount, delivery.child_unique_id || null); }}
                                                                                    className="w-full py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg transition-all min-h-[44px] [touch-action:manipulation]"
                                                                                >
                                                                                    End Delivery
                                                                                </button>
                                                                            </CancelledAction>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : delivery?.delivery_status === 'delivered' ? (
                                                        // Delivery Completed
                                                        <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                                                            <div className="flex items-center gap-2.5 mb-3">
                                                                <div className="w-7 h-7 bg-emerald-600 rounded-md flex items-center justify-center flex-shrink-0">
                                                                    <FiCheckCircle className="w-3.5 h-3.5 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-emerald-800">Delivery Completed</h4>
                                                                    <p className="text-xs text-emerald-600">This {quoteType} delivery has been successfully completed</p>
                                                                </div>
                                                            </div>

                                                            {/* Show delivery photos */}
                                                            {deliveryPhotos.length > 0 && (
                                                                <div className="space-y-3">
                                                                    <p className="text-xs md:text-sm font-medium text-gray-700">
                                                                        Delivery Photos ({deliveryPhotos.length})
                                                                    </p>
                                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                                        {deliveryPhotos.map((photo, index) => {
                                                                            const { data: { publicUrl } } = supabase.storage
                                                                                .from('delivery-uploads')
                                                                                .getPublicUrl(photo.filename);

                                                                            return (
                                                                                <div key={index} className="relative aspect-square">
                                                                                    <img
                                                                                        src={publicUrl}
                                                                                        alt={`Delivery photo ${index + 1}`}
                                                                                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {delivery?.delivered_at && (
                                                                <p className="text-xs text-gray-600 mt-3">
                                                                    Completed on {formatDate(delivery.delivered_at)}
                                                                </p>
                                                            )}

                                                            {delivery?.delivery_notes && (
                                                                <div className="mt-3 p-3 bg-white rounded border border-green-200">
                                                                    <p className="text-xs font-medium text-gray-700 mb-1">Delivery Notes:</p>
                                                                    <p className="text-xs text-gray-600">{delivery.delivery_notes}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}


                    {/* Notes Section */}
                    {(assignment.job_notes && assignment.job_notes.length > 0) || hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) ? (
                        <DropdownSection
                            title="Notes"
                            titleStyle="text-zinc-800 text-base"
                            icon={NotebookPen}
                            iconStyle="text-zinc-700 self-center"
                            subtext={`${assignment.job_notes?.length || 0} Note${assignment.job_notes?.length === 1 ? "" : "s"}`}
                            subtextStyle="text-xs font-semibold text-zinc-600"
                            customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                            defaultOpen={true}
                        >
                            <div className="space-y-4">
                                {assignment.job_notes && assignment.job_notes.length > 0 && (
                                    <div className="space-y-2">
                                        {assignment.job_notes.map((note) => (
                                            <div key={note.id} className={`p-3 rounded-lg border relative ${note?.is_important ? "bg-yellow-50 border-yellow-600" : "bg-white border-gray-200"}`}>
                                                {note?.is_important &&
                                                    <div className="absolute top-0 right-0 flex flex-row items-center gap-2 bg-yellow-600 text-white px-2 py-1 rounded-bl-lg">
                                                        <BsExclamationTriangle className="w-4 h-4" />
                                                        <span className="uppercase font-semibold text-[12px]">important</span>
                                                    </div>
                                                }
                                                {editingNoteId === note.id ? (
                                                    <EditNoteForm
                                                        note={note}
                                                        onSave={handleSaveEditNote}
                                                        onCancel={cancelEditNote}
                                                        isJobCancelled={isJobCancelled}
                                                    />
                                                ) : (
                                                    <div className="">
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">
                                                            <span className={`font-semibold py-0.5 px-1 mr-2 rounded text-sm ${getNoteCategoryBadgeClasses(note?.category || DEFAULT_NOTE_CATEGORY)}`}>
                                                                {note?.category || DEFAULT_NOTE_CATEGORY}
                                                            </span>
                                                            {note?.child_id &&
                                                                <span className="font-semibold py-0.5 px-1 mr-2 bg-gray-300 text-zinc-700 rounded text-sm">
                                                                    {note?.child_unique_id}
                                                                </span>
                                                            }
                                                            {note.content}
                                                        </p>
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                {renderUserAvatar(note?.added_by_user_id, note?.added_by_name, 'w-8 h-8', `${note.added_by_role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                                                    note.added_by_role === 'designer' ? 'bg-green-100 text-green-800' :
                                                                        'bg-purple-100 text-purple-800'
                                                                    }`)}
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{note.added_by_name}</p>
                                                                    <p className="text-xs text-gray-500 capitalize">{note.added_by_role}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-right">
                                                                    <p className="text-xs text-gray-500">{formatDate(note.added_at)}</p>
                                                                    {note.is_edited && (
                                                                        <p className="text-xs text-gray-400">Edited {formatDate(note.edited_at)}</p>
                                                                    )}
                                                                </div>
                                                                <CancelledAction>
                                                                    <button
                                                                        onClick={() => handleSetImportant(note.id, note.is_important)}
                                                                        className="p-1 ml-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                                        title="Set Important"
                                                                    >
                                                                        <BsExclamationTriangle className="w-4 h-4" />
                                                                    </button>
                                                                </CancelledAction>
                                                                {canUserModifyNote(note) && (
                                                                    <div className="flex gap-1">
                                                                        <CancelledAction>
                                                                            <button
                                                                                onClick={() => startEditNote(note.id)}
                                                                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                                                title="Edit note"
                                                                            >
                                                                                <FiEdit3 className="w-4 h-4" />
                                                                            </button>
                                                                        </CancelledAction>
                                                                        <CancelledAction>
                                                                            <button
                                                                                onClick={() => handleDeleteNote(note.id)}
                                                                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                                                title="Delete note"
                                                                            >
                                                                                <FiX className="w-4 h-4" />
                                                                            </button>
                                                                        </CancelledAction>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Add Note Form */}
                                <AddNoteForm onAdd={handleAddNote} isJobCancelled={isJobCancelled} />

                            </div>
                        </DropdownSection>
                    ) : null}

                    {/* Files & Documents with Folder Accordion Design */}
                    {(
                        jobFiles.length > 0 ||
                        customerConversationFiles.length > 0 ||
                        driverFilesEnabled
                    ) && (
                            <DropdownSection
                                title="Files & Documents"
                                titleStyle="text-zinc-800 text-base"
                                icon={FiFileText}
                                iconStyle="text-zinc-700 self-center"
                                subtext={``}
                                subtextStyle="text-xs"
                                customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                                defaultOpen={true}
                            >
                                {/* <InfoCard title="Files & Documents" icon={FiFileText} color="purple"> */}
                                <div className="space-y-3">
                                    {/* Header: Download All + Create Folder */}
                                    <div className="flex flex-wrap justify-between items-center mb-4 pb-3 border-b border-gray-200 gap-2">
                                        <div className="job-file-actions flex flex-row gap-2">
                                            <JobFilesEmailSender
                                                assignment={assignment}
                                                userProfile={userProfile}
                                                company={company}
                                            />
                                            <CancelledAction allow>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowConversationModal(true);
                                                    }}
                                                    className="relative flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50"
                                                >
                                                    <FiMessageSquare className="w-4 h-4" />
                                                    Customer Conversation

                                                    {inboundConversationCount > 0 && (
                                                        <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white shadow">
                                                            {inboundConversationCount > 9 ? '9+' : inboundConversationCount}
                                                        </span>
                                                    )}
                                                </button>
                                            </CancelledAction>
                                            {/* Download All Files button */}
                                            {(() => {
                                                const realFiles = [
                                                    ...jobFiles.filter(f => f.filename !== '__folder_placeholder__'),
                                                    ...customerConversationFiles,
                                                ];
                                                return realFiles.length > 0 && (
                                                    <CancelledAction allow>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadAllFilesWithFolders(realFiles, `${assignment.unique_id}_${assignment?.customer_address?.length > 0 ? assignment?.customer_address : "all-files"}.zip`)}
                                                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-xs font-medium"
                                                        >
                                                            {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                            {isDownloading ? `Downloading ${realFiles.length} files` : `Download All Files (${realFiles.length})`}
                                                        </button>
                                                    </CancelledAction>
                                                );
                                            })()}
                                        </div>

                                        {/* Create Folder button / input */}
                                        {!showFolderInput ? (
                                            <CancelledAction>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowFolderInput(true)}
                                                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium"
                                                >
                                                    <FiFolderPlus className="w-4 h-4" />
                                                    Create Folder
                                                </button>
                                            </CancelledAction>
                                        ) : (
                                            <div className="flex items-center gap-2 flex-1 min-w-0 max-w-xs">
                                                <input
                                                    type="text"
                                                    value={newFolderName}
                                                    onChange={e => setNewFolderName(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleCreateCustomFolder();
                                                        if (e.key === 'Escape') { setShowFolderInput(false); setNewFolderName(''); }
                                                    }}
                                                    placeholder="Folder name..."
                                                    autoFocus
                                                    className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none min-w-0"
                                                />
                                                <CancelledAction>
                                                    <button
                                                        type="button"
                                                        onClick={handleCreateCustomFolder}
                                                        disabled={isCreatingFolder || !newFolderName.trim()}
                                                        className="px-2 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-1"
                                                    >
                                                        {isCreatingFolder ? <Loader className="w-3 h-3 animate-spin" /> : 'Create'}
                                                    </button>
                                                </CancelledAction>
                                                <CancelledAction>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setShowFolderInput(false); setNewFolderName(''); }}
                                                        className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                                                    >
                                                        <FiX className="w-3.5 h-3.5" />
                                                    </button>
                                                </CancelledAction>
                                            </div>
                                        )}
                                    </div>

                                    <FileSearchBar
                                        value={fileSearch}
                                        onChange={setFileSearch}
                                    />

                                    {hasFileSearch &&
                                        filteredJobFiles.length === 0 &&
                                        filteredCustomerConversationFiles.length === 0 && (
                                            <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg bg-gray-50">
                                                <p className="text-sm font-semibold text-gray-700">No files found</p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Try another file name, folder, type, uploader, or department.
                                                </p>
                                            </div>
                                        )}

                                    {/* Standard file type accordions */}
                                    {['initial', 'design', 'estimation', 'timber', 'delivery'].map(fileType => {
                                        const filesOfType = filteredJobFiles.filter(file => file.file_type === fileType);
                                        if (filesOfType.length === 0) return null;

                                        const isOpen = openFolders[fileType] || false;

                                        return (
                                            <div key={fileType} className="border border-gray-200 rounded-lg overflow-hidden">
                                                {/* Folder Header — div wrapper prevents nested-button invalid HTML */}
                                                <div className="flex items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                                                    {/* Toggle button (flex-1 takes most of the row) */}
                                                    <CancelledAction allow>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleFolder(fileType)}
                                                            className="flex-1 flex items-center justify-between p-4 text-left"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="text-xl text-gray-500">
                                                                    {isOpen ? <FiFolderMinus /> : <FiFolder />}
                                                                </div>
                                                                <div className="text-left">
                                                                    <h4 className="font-semibold text-gray-700 capitalize text-sm">
                                                                        {fileType === "initial" ? "Job" : fileType} Files
                                                                    </h4>
                                                                    <p className="text-xs text-gray-500">
                                                                        {filesOfType.length} file{filesOfType.length !== 1 ? 's' : ''} • {(() => {
                                                                            const quoteTypes = [...new Set(filesOfType.map(f => f.quote_type || 'general'))];
                                                                            return quoteTypes.length > 1 ? `${quoteTypes.length} categories` : '1 category';
                                                                        })()}
                                                                    </p>
                                                                </div>
                                                                <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
                                                            </div>
                                                            <div className={`text-gray-400 transition-transform duration-200 mr-2 ${isOpen ? 'rotate-180' : ''}`}>
                                                                <FiChevronDown className="w-5 h-5" />
                                                            </div>
                                                        </button>
                                                    </CancelledAction>

                                                    {/* Download button — sibling, not nested inside toggle */}
                                                    <CancelledAction allow>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadAllFilesWithFolders(filesOfType, `${assignment.unique_id}_${fileType}_files.zip`)}
                                                            className="p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                            title={`Download all ${fileType} files`}
                                                        >
                                                            {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                        </button>
                                                    </CancelledAction>
                                                </div>

                                                {/* Folder Content - Expandable */}
                                                {isOpen && (
                                                    <div className="bg-white border-t border-gray-200">
                                                        <div className="p-4 space-y-3">
                                                            {/* Group by Quote Type - Nested Accordions */}
                                                            {['beams', 'flooring_sheets', 'roof', 'wall', 'floor', 'timber', 'general'].map(quoteType => {
                                                                const quoteFiles = filesOfType.filter(file =>
                                                                    (file.quote_type || 'general') === quoteType
                                                                );
                                                                if (quoteFiles.length === 0) return null;

                                                                const quoteKey = `${fileType}-${quoteType}`;
                                                                const isQuoteOpen = openQuoteFolders[quoteKey] || false;
                                                                const uploadKey = `${fileType}-${quoteType}-`;

                                                                // Only show quote folders if there are multiple quote types OR if it's not general
                                                                const shouldShowQuoteFolder = quoteType !== 'general' || ['roof', 'wall', 'floor', 'timber'].some(type =>
                                                                    filesOfType.some(f => (f.quote_type || 'general') === type)
                                                                );

                                                                if (!shouldShowQuoteFolder) {
                                                                    // Show files directly (no sub-folder) + add file button
                                                                    return (
                                                                        <div key={quoteType} className="space-y-2">
                                                                            {quoteFiles.map((file, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                                >
                                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                        <div className="flex-shrink-0">
                                                                                            {getFileIcon(file.filename)}
                                                                                        </div>
                                                                                        <div className="min-w-0 flex-1">
                                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                {file.original_name}
                                                                                            </p>
                                                                                            <p className="text-xs text-gray-500">
                                                                                                Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                                        <CancelledAction allow>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => seeFile(file, filteredJobFiles)}
                                                                                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                                title="View file"
                                                                                            >
                                                                                                <FiEye className="w-4 h-4" />
                                                                                            </button>
                                                                                        </CancelledAction>
                                                                                        <CancelledAction allow>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => downloadFile(file)}
                                                                                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                                                title="Download file"
                                                                                            >
                                                                                                <FiDownload className="w-4 h-4" />
                                                                                            </button>
                                                                                        </CancelledAction>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                            {/* Add a new file to this folder */}
                                                                            <CancelledAction>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => triggerFileUpload(fileType, null)}
                                                                                    disabled={folderUploading === uploadKey}
                                                                                    onDragOver={e => { e.preventDefault(); setDragOverFolder(uploadKey); }}
                                                                                    onDragLeave={() => setDragOverFolder(null)}
                                                                                    onDrop={async e => { e.preventDefault(); setDragOverFolder(null); for (const file of Array.from(e.dataTransfer.files)) { await uploadSingleFile(file, fileType, null, null, false); } }}
                                                                                    className={`w-full flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg text-xs transition-colors disabled:opacity-50 ${dragOverFolder === uploadKey ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50'}`}
                                                                                >
                                                                                    {folderUploading === uploadKey
                                                                                        ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                                                                        : dragOverFolder === uploadKey
                                                                                            ? <><FiUpload className="w-3.5 h-3.5" /> Drop to upload</>
                                                                                            : <><FiPlus className="w-3.5 h-3.5" /> Add a new file</>
                                                                                    }
                                                                                </button>
                                                                            </CancelledAction>
                                                                        </div>
                                                                    );
                                                                }

                                                                return (
                                                                    <div key={quoteType} className="border border-gray-300 rounded-lg overflow-hidden ml-4">
                                                                        {/* Quote Folder Header — sibling buttons, no nesting */}
                                                                        <div className="flex items-center bg-gray-100 hover:bg-gray-200 transition-colors">
                                                                            <CancelledAction allow>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => toggleQuoteFolder(fileType, quoteType)}
                                                                                    className="flex-1 flex items-center justify-between p-3 text-left"
                                                                                >
                                                                                    <div className="flex items-center gap-2">
                                                                                        <div className={`text-lg ${isQuoteOpen ? 'text-gray-500' :
                                                                                            quoteType === 'roof' ? 'text-blue-400' :
                                                                                                quoteType === 'wall' ? 'text-red-400' :
                                                                                                    quoteType === 'floor' ? 'text-green-400' :
                                                                                                        quoteType === 'timber' ? 'text-amber-400' :
                                                                                                            'text-gray-400'
                                                                                            }`}>
                                                                                            {isQuoteOpen ? <FiFolderMinus /> : <FiFolder />}
                                                                                        </div>
                                                                                        <div className="text-left">
                                                                                            <span className="text-sm font-semibold text-gray-700 capitalize">
                                                                                                {quoteType === 'general' ? 'General Files' : `${quoteType?.split("_")?.join(" ")} Files`}
                                                                                            </span>
                                                                                            <p className="text-xs text-gray-500">
                                                                                                {quoteFiles.length} file{quoteFiles.length !== 1 ? 's' : ''}
                                                                                            </p>
                                                                                        </div>
                                                                                        <div className={`w-2 h-2 rounded-full ${quoteType === 'roof' ? 'bg-blue-400' :
                                                                                            quoteType === 'wall' ? 'bg-red-400' :
                                                                                                quoteType === 'floor' ? 'bg-green-400' :
                                                                                                    quoteType === 'timber' ? 'bg-amber-400' :
                                                                                                        'bg-gray-400'
                                                                                            }`}></div>
                                                                                    </div>
                                                                                    <div className={`text-gray-400 transition-transform duration-200 mr-1 ${isQuoteOpen ? 'rotate-180' : ''}`}>
                                                                                        <FiChevronDown className="w-4 h-4" />
                                                                                    </div>
                                                                                </button>
                                                                            </CancelledAction>
                                                                            {/* Download button — sibling */}
                                                                            <CancelledAction allow>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => downloadAllFilesWithFolders(quoteFiles, `${assignment.unique_id}_${fileType}_${quoteType}_files.zip`)}
                                                                                    className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                                    title={`Download all ${quoteType} files`}
                                                                                >
                                                                                    {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                                                </button>
                                                                            </CancelledAction>
                                                                        </div>

                                                                        {/* Quote Folder Content */}
                                                                        {isQuoteOpen && (
                                                                            <div className="bg-white border-t border-gray-300">
                                                                                <div className="p-3 space-y-2">
                                                                                    {quoteFiles.map((file, index) => (
                                                                                        <div
                                                                                            key={index}
                                                                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                                        >
                                                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                                <div className="flex-shrink-0">
                                                                                                    {getFileIcon(file.filename)}
                                                                                                </div>
                                                                                                <div className="min-w-0 flex-1">
                                                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                        {file.original_name}
                                                                                                    </p>
                                                                                                    <p className="text-xs text-gray-500">
                                                                                                        Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                                    </p>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                                                <CancelledAction allow>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => seeFile(file, filteredJobFiles)}
                                                                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                                        title="View file"
                                                                                                    >
                                                                                                        <FiEye className="w-4 h-4" />
                                                                                                    </button>
                                                                                                </CancelledAction>
                                                                                                <CancelledAction allow>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => downloadFile(file)}
                                                                                                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                                                        title="Download file"
                                                                                                    >
                                                                                                        <FiDownload className="w-4 h-4" />
                                                                                                    </button>
                                                                                                </CancelledAction>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}

                                                                                    {/* Add a new file to this department */}
                                                                                    <CancelledAction>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => triggerFileUpload(fileType, quoteType)}
                                                                                            disabled={folderUploading === uploadKey}
                                                                                            onDragOver={e => {
                                                                                                if (isTouchDevice) return;
                                                                                                e.preventDefault();
                                                                                                setDragOverFolder(uploadKey);
                                                                                            }}
                                                                                            onDragLeave={() => setDragOverFolder(null)}
                                                                                            onDrop={async e => {
                                                                                                if (isTouchDevice) return;
                                                                                                e.preventDefault();
                                                                                                setDragOverFolder(null);
                                                                                                for (const file of Array.from(e.dataTransfer.files)) {
                                                                                                    await uploadSingleFile(file, fileType, quoteType, null, false);
                                                                                                }
                                                                                            }}
                                                                                            className={`w-full flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg text-xs transition-colors disabled:opacity-50 ${dragOverFolder === uploadKey ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50'}`}
                                                                                        >
                                                                                            {folderUploading === uploadKey
                                                                                                ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                                                                                : dragOverFolder === uploadKey
                                                                                                    ? <><FiUpload className="w-3.5 h-3.5" /> Drop to upload</>
                                                                                                    : <><FiPlus className="w-3.5 h-3.5" /> Add a new file</>
                                                                                            }
                                                                                        </button>
                                                                                    </CancelledAction>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {filteredCustomerConversationFiles.length > 0 && (() => {
                                        const fileType = 'customer-uploads';
                                        const isOpen = openFolders[fileType] || false;

                                        return (
                                            <div key={fileType} className="border border-sky-200 rounded-lg overflow-hidden">
                                                <div className="flex items-center bg-sky-50 hover:bg-sky-100 transition-colors">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleFolder(fileType)}
                                                        className="flex-1 flex items-center justify-between p-4 text-left"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-xl text-sky-500">
                                                                {isOpen ? <FiFolderMinus /> : <FiFolder />}
                                                            </div>
                                                            <div className="text-left">
                                                                <h4 className="font-semibold text-sky-700 text-sm">
                                                                    Customer Uploads
                                                                </h4>
                                                                <p className="text-xs text-sky-500">
                                                                    {filteredCustomerConversationFiles.length} file{filteredCustomerConversationFiles.length !== 1 ? 's' : ''} • Conversation attachments
                                                                </p>
                                                            </div>
                                                            <div className="w-2.5 h-2.5 rounded-full bg-sky-400"></div>
                                                        </div>
                                                        <div className={`text-sky-400 transition-transform duration-200 mr-2 ${isOpen ? 'rotate-180' : ''}`}>
                                                            <FiChevronDown className="w-5 h-5" />
                                                        </div>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => downloadAllFilesWithFolders(filteredCustomerConversationFiles, `${assignment.unique_id}_customer_uploads.zip`)}
                                                        className="p-3 text-sky-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                        title="Download all customer uploads"
                                                    >
                                                        {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                    </button>
                                                </div>

                                                {isOpen && (
                                                    <div className="bg-white border-t border-sky-200">
                                                        <div className="p-4 space-y-2">
                                                            {filteredCustomerConversationFiles.map((file, index) => (
                                                                <div
                                                                    key={file.id || index}
                                                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                >
                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                        <div className="flex-shrink-0">
                                                                            {getFileIcon(file.filename)}
                                                                        </div>
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                {file.original_name}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">
                                                                                Uploaded by {file.sent_by_name || 'Customer'} ({file.sent_by_role || 'customer'}) • {formatDate(file.uploaded_at || file.created_at)}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => seeFile(file, filteredCustomerConversationFiles)}
                                                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                            title="View file"
                                                                        >
                                                                            <FiEye className="w-4 h-4" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => downloadFile(file)}
                                                                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                            title="Download file"
                                                                        >
                                                                            <FiDownload className="w-4 h-4" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Custom folder accordions */}
                                    {(() => {
                                        const customFiles = filteredJobFiles.filter(f => f.file_type === 'custom');
                                        const folderNames = [...new Set(customFiles.map(f => f.description).filter(Boolean))];
                                        return folderNames.map(folderName => {
                                            const folderFiles = customFiles.filter(f => f.description === folderName && f.filename !== '__folder_placeholder__');
                                            const isOpen = openFolders[`custom-${folderName}`] || false;
                                            const uploadKey = `custom-${folderName}-`;

                                            const restrictedFolders = ["Quotes & Invoices", "Layouts & Comps"];

                                            const defaultRestrictedFolderUploadRoles = {
                                                "Quotes & Invoices": ["admin", "super_admin"],
                                                "Layouts & Comps": ["admin", "super_admin"],
                                            };

                                            const restrictedFolderUploadRoles =
                                                company?.settings?.restricted_folder_upload_roles ||
                                                defaultRestrictedFolderUploadRoles;

                                            const isRestrictedFolder = restrictedFolders.includes(folderName);

                                            const allowedRolesForFolder =
                                                restrictedFolderUploadRoles[folderName] ||
                                                defaultRestrictedFolderUploadRoles[folderName] ||
                                                [];

                                            const shouldBlockFileUpload =
                                                isRestrictedFolder &&
                                                !allowedRolesForFolder.includes(userProfile?.role);

                                            return (
                                                <div key={`custom-${folderName}`} className="border border-indigo-200 rounded-lg overflow-hidden">
                                                    {/* Custom Folder Header */}
                                                    <div className="flex items-center bg-indigo-50 hover:bg-indigo-100 transition-colors">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleFolder(`custom-${folderName}`)}
                                                            className="flex-1 flex items-center justify-between p-4 text-left"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="text-xl text-indigo-500">
                                                                    {isOpen ? <FiFolderMinus /> : <FiFolder />}
                                                                </div>
                                                                <div className="text-left">
                                                                    <h4 className="font-semibold text-indigo-700 text-sm">{folderName}</h4>
                                                                    <p className="text-xs text-indigo-400">
                                                                        {folderFiles.length} file{folderFiles.length !== 1 ? 's' : ''} • Custom folder
                                                                    </p>
                                                                </div>
                                                                <div className="w-2.5 h-2.5 rounded-full bg-indigo-400"></div>
                                                            </div>
                                                            <div className={`text-indigo-400 transition-transform duration-200 mr-2 ${isOpen ? 'rotate-180' : ''}`}>
                                                                <FiChevronDown className="w-5 h-5" />
                                                            </div>
                                                        </button>

                                                        {folderFiles.length > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => downloadAllFilesWithFolders(folderFiles, `${assignment.unique_id}_${folderName}_files.zip`)}
                                                                className="p-3 text-indigo-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                title={`Download all files in ${folderName}`}
                                                            >
                                                                {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Custom Folder Content */}
                                                    {isOpen && (
                                                        <div className="bg-white border-t border-indigo-200">
                                                            <div className="p-4 space-y-2">
                                                                {folderFiles.length === 0 ? (
                                                                    <p className="text-xs text-gray-400 text-center py-2">No files yet — upload one below</p>
                                                                ) : (
                                                                    folderFiles.map((file, index) => (
                                                                        <div
                                                                            key={index}
                                                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                        >
                                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                <div className="flex-shrink-0">
                                                                                    {getFileIcon(file.filename)}
                                                                                </div>
                                                                                <div className="min-w-0 flex-1">
                                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                                        {file.original_name}
                                                                                    </p>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => seeFile(file, folderFiles)}
                                                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                    title="View file"
                                                                                >
                                                                                    <FiEye className="w-4 h-4" />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => downloadFile(file)}
                                                                                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                                    title="Download file"
                                                                                >
                                                                                    <FiDownload className="w-4 h-4" />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                                {shouldBlockFileUpload ? (
                                                                    <div className="w-full flex items-center justify-center p-2.5 rounded-lg text-xs border border-dashed border-gray-300 text-gray-400 bg-gray-50">
                                                                        Not enough permission to add files
                                                                    </div>
                                                                ) : (
                                                                    <CancelledAction>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => triggerFileUpload('custom', null, folderName, true)}
                                                                            disabled={folderUploading === uploadKey}
                                                                            onDragOver={e => { e.preventDefault(); setDragOverFolder(uploadKey); }}
                                                                            onDragLeave={() => setDragOverFolder(null)}
                                                                            onDrop={async e => {
                                                                                e.preventDefault();
                                                                                setDragOverFolder(null);
                                                                                for (const file of Array.from(e.dataTransfer.files)) {
                                                                                    await uploadSingleFile(file, 'custom', null, folderName, true);
                                                                                }
                                                                            }}
                                                                            className={`w-full flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg text-xs transition-colors disabled:opacity-50 ${dragOverFolder === uploadKey
                                                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                                                : 'border-indigo-300 text-indigo-500 hover:text-indigo-700 hover:border-indigo-500 hover:bg-indigo-50'
                                                                                }`}
                                                                        >
                                                                            {folderUploading === uploadKey
                                                                                ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                                                                : dragOverFolder === uploadKey
                                                                                    ? <><FiUpload className="w-3.5 h-3.5" /> Drop to upload</>
                                                                                    : <><FiPlus className="w-3.5 h-3.5" /> Add a new file</>
                                                                            }
                                                                        </button>
                                                                    </CancelledAction>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                {shouldShowDriverFiles && (
                                    <div className="rounded-xl !mt-3 border border-gray-200 bg-white overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenFolders(prev => ({
                                                    ...prev,
                                                    driver_files: !prev.driver_files,
                                                }))
                                            }
                                            className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-lg bg-gray-800 text-white flex items-center justify-center shrink-0">
                                                    <FiTruck className="w-4 h-4" />
                                                </div>

                                                <div className="text-left min-w-0">
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        Driver Files
                                                    </p>

                                                    <p className="text-xs text-gray-500">
                                                        Add files for the assigned driver to view.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-xs text-gray-500">
                                                    {driverFiles.length} file{driverFiles.length !== 1 ? "s" : ""}
                                                </span>

                                                <FiChevronDown
                                                    className={`w-4 h-4 text-gray-500 transition-transform ${openFolders.driver_files ? "rotate-180" : ""
                                                        }`}
                                                />
                                            </div>
                                        </button>

                                        {openFolders.driver_files && (
                                            <div className="p-4 space-y-4 border-t border-gray-200">
                                                {driverFilesEnabled && (
                                                    <div
                                                        className={`rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-3 ${isJobCancelled ? "opacity-50" : ""
                                                            }`}
                                                    >
                                                        <DragDropUploader
                                                            onFilesChange={handleDriverFilesChange}
                                                            clearFiles={clearDriverFiles}
                                                        />

                                                        {driverFilesToUpload.length > 0 && (
                                                            <div className="space-y-2">
                                                                {driverFilesToUpload.map((item, index) => (
                                                                    <div
                                                                        key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}
                                                                        className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-gray-200 bg-white p-3"
                                                                    >
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="text-sm font-medium text-gray-800 truncate">
                                                                                {item.file.name}
                                                                            </p>

                                                                            <p className="text-xs text-gray-500">
                                                                                Select which job child can access this file.
                                                                            </p>
                                                                        </div>

                                                                        <select
                                                                            value={item.childId}
                                                                            onChange={event =>
                                                                                handleDriverFileChildChange(
                                                                                    index,
                                                                                    event.target.value
                                                                                )
                                                                            }
                                                                            className="w-full sm:w-56 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                                                        >
                                                                            <option value="">None — all assigned drivers</option>

                                                                            {driverFileChildOptions.map(option => (
                                                                                <option
                                                                                    key={option.id}
                                                                                    value={option.id}
                                                                                >
                                                                                    {option.childUniqueId}
                                                                                    {option.quoteType
                                                                                        ? ` — ${getQuoteLabel(option.quoteType)}`
                                                                                        : ""}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        <div className="flex items-center justify-end">
                                                            <button
                                                                type="button"
                                                                onClick={handleUploadDriverFiles}
                                                                disabled={
                                                                    isJobCancelled ||
                                                                    isUploadingDriverFiles ||
                                                                    driverFilesToUpload.length === 0
                                                                }
                                                                title={isJobCancelled ? "This job is cancelled" : undefined}
                                                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {isUploadingDriverFiles ? (
                                                                    <Loader className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <FiUpload className="w-4 h-4" />
                                                                )}

                                                                {isUploadingDriverFiles
                                                                    ? "Uploading..."
                                                                    : `Upload Driver Files${driverFilesToUpload.length > 0
                                                                        ? ` (${driverFilesToUpload.length})`
                                                                        : ""
                                                                    }`}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {!driverFilesEnabled && driverFiles.length > 0 && (
                                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                                                        <p className="text-xs text-amber-700">
                                                            Driver Files uploads are disabled in company settings.
                                                            Existing files remain available.
                                                        </p>
                                                    </div>
                                                )}

                                                {filteredDriverFiles.length > 0 ? (
                                                    <div className="grid grid-cols-1 gap-3">
                                                        {filteredDriverFiles.map(file => (
                                                            <button
                                                                key={file.id}
                                                                type="button"
                                                                onClick={() => seeFile(file, filteredDriverFiles)}
                                                                className="group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-gray-300 hover:shadow-sm transition"
                                                            >
                                                                <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
                                                                    {getFileIcon(file.original_name || file.filename)}
                                                                </div>

                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                                        {file.original_name || file.filename}
                                                                    </p>

                                                                    <p className="text-xs text-gray-500 truncate">
                                                                        Uploaded by {file.uploaded_by_name || "Unknown"}
                                                                    </p>

                                                                    {file.child_unique_id && (
                                                                        <p className="text-xs font-medium text-blue-600 truncate mt-0.5">
                                                                            Child: {file.child_unique_id}
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                <FiEye className="w-4 h-4 text-gray-400 ml-auto shrink-0" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="rounded-lg border border-dashed border-gray-300 py-6 text-center">
                                                        <FiTruck className="w-6 h-6 text-gray-300 mx-auto mb-2" />

                                                        <p className="text-sm text-gray-500">
                                                            {hasFileSearch && driverFiles.length > 0
                                                                ? "No Driver Files match your search."
                                                                : "No Driver Files uploaded yet."}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* </InfoCard> */}
                            </DropdownSection>
                        )}

                    <DropdownSection
                        title="Pricing"
                        titleStyle="text-zinc-800 text-base"
                        icon={FiDollarSign}
                        iconStyle="text-zinc-700 self-center"
                        subtext="Estimator prices and designer department prices"
                        subtextStyle="text-xs"
                        customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                        defaultOpen={true}
                    >
                        <PricingReviewDropdown
                            assignment={assignment}
                            company={company}
                            userProfile={userProfile}
                            onSaved={fetchAssignment}
                            companySlug={companySlug}
                        />
                    </DropdownSection>

                    {/* Files & Documents with Folder Accordion Design */}

                    {/* Assigned Users Section */}
                    <DropdownSection
                        title="Assigned Users"
                        titleStyle="text-zinc-800 text-base"
                        icon={Users}
                        iconStyle="text-zinc-700 self-center"
                        subtext={``}
                        subtextStyle="text-xs font-semibold text-zinc-500"
                        customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                        defaultOpen={true}
                    >
                        <div className="space-y-3">
                            <div className="flex flex-col gap-3">
                                {/* 1. Job Creator Information */}
                                <DropdownSection
                                    title="Job Creator"
                                    titleStyle="text-zinc-700 text-sm"
                                    icon={UserCog}
                                    iconStyle="text-zinc-800"
                                    subtext={``}
                                    subtextStyle="text-xs "
                                    defaultOpen={false}
                                >
                                    <DetailRow
                                        label="Created By"
                                        value={assignment.created_by_name || "Unknown"}
                                        icon={FiUser}
                                    />
                                    <DetailRow
                                        label="Role"
                                        value="Admin"
                                        icon={FiTool}
                                    />
                                    <DetailRow
                                        label="Created On"
                                        value={formatDate(assignment.created_at)}
                                        icon={FiCalendar}
                                    />
                                </DropdownSection>

                                {/* 3. Assigned Estimator */}
                                {(assignment.job_type === 'Estimation' || assignment.job_type === 'Design & Estimation') && userProfile?.role !== "driver" && (
                                    <DropdownSection
                                        title="Assigned Estimator"
                                        titleStyle="text-zinc-700 text-sm"
                                        icon={FiDollarSign}
                                        iconStyle="text-zinc-800"
                                        subtext={``}
                                        subtextStyle="text-xs "
                                        defaultOpen={false}
                                    >
                                        <div className="space-y-4">
                                            {assignment.job_estimators?.user_id ? (
                                                <div className="bg-green-50 p-3 md:p-4 rounded-lg">
                                                    <div className="flex items-center justify-between mb-2 gap-2">
                                                        <span className="text-xs md:text-sm font-medium text-green-900">Estimator</span>
                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                            <CancelledAction>
                                                                <button
                                                                    onClick={() => handleRemoveEstimator()}
                                                                    className="text-xs text-red-600 hover:text-red-800 font-medium min-h-[44px] px-2"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </CancelledAction>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {renderUserAvatar(assignment.job_estimators?.user_id, assignment.job_estimators?.name, 'w-7 h-7', 'bg-green-500')}
                                                        <div>
                                                            <span className="text-xs md:text-sm font-medium text-gray-900">
                                                                {assignment.job_estimators?.full_name?.trim()
                                                                    ? assignment.job_estimators.full_name
                                                                    : assignment.job_estimators?.name}
                                                            </span>
                                                            {assignment.job_estimators?.is_completed && (
                                                                <div className="flex items-center gap-1 mt-1">
                                                                    <FiCheckCircle className="w-3 h-3 text-green-500" />
                                                                    <span className="text-xs text-green-600">Completed</span>
                                                                </div>
                                                            )}
                                                            {assignment.job_estimators?.is_completed && (
                                                                <div className="flex items-center gap-1 mt-1">
                                                                    <span className="text-xs text-green-600">Completed by {assignment.job_estimators?.completed_by_name || assignment.job_estimators?.name}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <DetailRow
                                                        label="Assigned On"
                                                        value={formatDate(assignment.job_estimators?.assigned_at)}
                                                        icon={FiCalendar}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    <p className="text-xs md:text-sm text-gray-600">No estimator assigned</p>

                                                    {(hasPermission(userProfile?.role, ["super_admin", "admin"]) || checkPermissions(userProfile?.permissions, ["ASSIGN_ESTIMATOR"])) && (

                                                        <CancelledAction>
                                                            <p>{checkPermissions(userProfile?.permissions, ["ASSIGN_ESTIMATOR"])}</p>
                                                            <button
                                                                onClick={() => openAssignEstimatorModal()}
                                                                className="w-full py-3 md:py-2 px-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium min-h-[44px]"
                                                            >
                                                                Assign Estimator
                                                            </button>
                                                        </CancelledAction>
                                                    )}

                                                    {hasPermission(userProfile?.role, ["estimator", "designer"]) && (
                                                        <CancelledAction>
                                                            <button
                                                                onClick={() => handleSelfAssignEstimator()}
                                                                className="w-full py-3 md:py-2 px-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium min-h-[44px]"
                                                            >
                                                                Assign Self as Estimator
                                                            </button>
                                                        </CancelledAction>
                                                    )}

                                                    {hasPermission(userProfile?.role, ["designer"]) &&
                                                        !checkPermissions(userProfile?.permissions, ["ASSIGN_ESTIMATOR"]) && (
                                                            <p className="text-xs text-gray-500 italic">
                                                                Contact admin to assign estimator
                                                            </p>
                                                        )}

                                                </div>
                                            )}
                                        </div>
                                    </DropdownSection>
                                )}

                                {/* 2. Assigned Designers */}
                                {(assignment.job_type === 'Design' || assignment.job_type === 'Design & Estimation') && userProfile?.role !== "driver" && (
                                    <>
                                        {(() => {
                                            const designerQuotes = (assignment.job_quotes ?? [])
                                                .filter(q =>
                                                    q.is_required &&
                                                    !isFileOnlyQuoteType(q.quote_type) &&
                                                    (q.delivery_count === null || (q.delivery_count ?? 0) === 0)
                                                )
                                                .sort((a, b) => {
                                                    const ORDER = { roof: 0, wall: 1, floor: 2, timber: 3 };
                                                    const typeOrder = (ORDER[a.quote_type] ?? 99) - (ORDER[b.quote_type] ?? 99);
                                                    if (typeOrder !== 0) return typeOrder;
                                                    return (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                                });
                                            const n = designerQuotes.length;
                                            const assignedCount = designerQuotes.filter(dep => {
                                                const depDesigners = (assignment.job_designers ?? []).filter(d => {
                                                    if (d.quote_type !== dep.quote_type) return false;
                                                    if (d.child_job_id) return d.child_job_id === dep.id;
                                                    return !dep.child_unique_id;
                                                });

                                                return depDesigners.length > 0;
                                            }).length;
                                            const gridCols =
                                                n <= 1 ? 'grid-cols-1' :
                                                    n === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                                                        n === 3 ? 'grid-cols-1 sm:grid-cols-3' :
                                                            'grid-cols-2 lg:grid-cols-4';
                                            return (
                                                <DropdownSection
                                                    title="Assigned Designers"
                                                    titleStyle="text-zinc-700 text-sm"
                                                    icon={FiEdit3}
                                                    iconStyle="text-zinc-800"
                                                    subtext={`${n} Jobs - ${assignedCount}/${n} assigned`}
                                                    subtextStyle="text-xs "
                                                    defaultOpen={false}
                                                >
                                                    <div className={`grid gap-2 ${gridCols}`}>
                                                        {designerQuotes.map(dep => {
                                                            const quoteType = dep.quote_type;
                                                            const childName = dep.child_unique_id;

                                                            // 3-case designer filter: new-style by child_job_id, old-style by null child_job_id
                                                            const depDesigners = (assignment.job_designers ?? []).filter(d => {
                                                                if (d.quote_type !== quoteType) return false;
                                                                if (d.child_job_id) return d.child_job_id === dep.id;
                                                                return !dep.child_unique_id; // old-style: show only in non-child panel
                                                            });

                                                            const C = {
                                                                roof: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-500', avatarBg: 'bg-blue-500', divider: 'divide-blue-100', icon: <Home className="w-3 h-3" /> },
                                                                wall: { bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-500', avatarBg: 'bg-red-500', divider: 'divide-red-100', icon: <PanelsTopLeft className="w-3 h-3" /> },
                                                                floor: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500', avatarBg: 'bg-green-500', divider: 'divide-green-100', icon: <HiOutlineSquare3Stack3D className="w-3 h-3" /> },
                                                                timber: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-500', avatarBg: 'bg-amber-500', divider: 'divide-amber-100', icon: <GiWoodBeam className="w-3 h-3" /> },
                                                            }[quoteType] ?? { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-400', avatarBg: 'bg-gray-400', divider: 'divide-gray-100', icon: <FiUser className="w-3 h-3" /> };

                                                            const canAssign = hasPermission(userProfile?.role, ["super_admin", "admin"]) || checkPermissions(userProfile?.permissions, ["ASSIGN_DESIGNERS"]);
                                                            const isSelfAssigned = (assignment.job_designers ?? []).some(d => {
                                                                if (d.user_id !== userProfile.id || d.quote_type !== quoteType) return false;
                                                                if (d.child_job_id) return d.child_job_id === dep.id;
                                                                return !dep.child_unique_id;
                                                            });
                                                            const canSelfAssign = (hasPermission(userProfile?.role, ["designer"]) || checkPermissions(userProfile?.permissions, ["ASSIGN_DESIGNER_SELF"])) && !isSelfAssigned;

                                                            return (
                                                                <div key={dep.id || quoteType} className={`rounded-xl border ${C.border} overflow-hidden`}>

                                                                    {/* ── Quote header ── */}
                                                                    <div className={`flex items-center justify-between px-3 py-2 ${C.bg} border-b ${C.border}`}>
                                                                        <div
                                                                            className={`flex items-center gap-2 ${childName && "cursor-pointer hover:underline underline-offset-2 hover:!text-blue-500"}`}
                                                                            onClick={() => {
                                                                                if (!childName) return;
                                                                                navigate(`./${dep?.id}`, {
                                                                                    state: { from: location.pathname }
                                                                                })
                                                                            }}
                                                                            disabled={!childName}
                                                                        >
                                                                            <div className={`w-5 h-5 rounded flex items-center justify-center ${C.iconBg} text-white flex-shrink-0`}>
                                                                                {C.icon}
                                                                            </div>
                                                                            <span className="text-xs font-semibold text-gray-700 capitalize">
                                                                                {quoteType}
                                                                                {childName && <span className="ml-1.5 font-normal text-gray-500">· {childName}</span>}
                                                                            </span>
                                                                            {depDesigners.length > 0 && (
                                                                                <span className="text-[10px] text-gray-400 font-medium">
                                                                                    {depDesigners.length} {depDesigners.length === 1 ? 'designer' : 'designers'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center gap-2">
                                                                            {/* design_status badge — same source as overview header pills */}
                                                                            {dep.design_status && dep.design_status !== 'not_required' && (
                                                                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${dep.design_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                                                                    dep.design_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                                                        dep.design_status === 'waiting_assignment' ? 'bg-amber-100 text-amber-700' :
                                                                                            'bg-gray-100 text-gray-500'
                                                                                    }`}>
                                                                                    {dep.design_status.replace(/_/g, ' ')}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* ── Designer rows ── */}
                                                                    {depDesigners.length === 0 ? (
                                                                        <div className="px-3 py-2.5 bg-white">
                                                                            {canAssign && depDesigners.length <= 0 && (
                                                                                <CancelledAction>
                                                                                    <button
                                                                                        onClick={() => openAssignDesignerModal(dep.quote_type, dep.id, dep?.child_unique_id || null)}
                                                                                        className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                                                                    >
                                                                                        <FiEdit3 className="w-3 h-3" />
                                                                                        Assign Designer
                                                                                    </button>
                                                                                </CancelledAction>
                                                                            )}
                                                                            {canSelfAssign && (
                                                                                <CancelledAction>
                                                                                    <button
                                                                                        onClick={() => handleSelfAssignDesigner(dep.quote_type, dep.id, dep?.child_unique_id || null)}
                                                                                        disabled={isAssigningDesigner}
                                                                                        className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                    >
                                                                                        {isAssigningDesigner ? 'Assigning...' : 'Assign Yourself'}
                                                                                    </button>
                                                                                </CancelledAction>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className={`divide-y ${C.divider} bg-white`}>
                                                                            {depDesigners.map(designer => (
                                                                                <div key={designer.id} className="flex items-center justify-between px-3 py-2.5 gap-2">
                                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                                        {renderUserAvatar(designer.user_id, designer.name, 'w-7 h-7', C.avatarBg)}
                                                                                        <div className="min-w-0">
                                                                                            <p className="text-xs font-semibold text-gray-800 truncate leading-tight">
                                                                                                {designer.name}
                                                                                            </p>
                                                                                            {designer.is_completed && (
                                                                                                <p className="text-[10px] text-gray-400 leading-tight truncate">
                                                                                                    by {designer.completed_by_name || designer.name}
                                                                                                </p>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                                        {/* <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${designer.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                                                    {designer.is_completed ? 'finished' : 'in progress'}
                                                                                </span> */}
                                                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                            <CancelledAction>
                                                                                                <button
                                                                                                    onClick={() => openRemoveDesignerModal(dep.quote_type, designer.user_id, dep?.id || null, designer.name, designer.is_completed, dep?.child_unique_id || null)}
                                                                                                    className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                                                                    title="Remove designer"
                                                                                                >
                                                                                                    <FiX className="w-3.5 h-3.5" />
                                                                                                </button>
                                                                                            </CancelledAction>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </DropdownSection>

                                            );
                                        })()}
                                    </>
                                )}

                                {userProfile?.role !== "driver" && (
                                    <>
                                        {(() => {
                                            const fileOnlyQuotes = (assignment.job_quotes ?? [])
                                                .filter(q =>
                                                    q.is_required &&
                                                    isFileOnlyQuoteType(q.quote_type) &&
                                                    (q.delivery_count === null || (q.delivery_count ?? 0) === 0)
                                                )
                                                .sort((a, b) => {
                                                    const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                                                    if (typeOrder !== 0) return typeOrder;
                                                    return (a.child_unique_id ?? "").localeCompare(b.child_unique_id ?? "");
                                                });

                                            if (fileOnlyQuotes.length === 0) return null;

                                            return (
                                                <DropdownSection
                                                    title="Additional Department Files"
                                                    titleStyle="text-zinc-700 text-sm"
                                                    icon={FiFolderPlus}
                                                    iconStyle="text-zinc-800"
                                                    subtext={`${fileOnlyQuotes.length} file-only department${fileOnlyQuotes.length === 1 ? "" : "s"}`}
                                                    subtextStyle="text-xs"
                                                    defaultOpen={false}
                                                >
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                                        {fileOnlyQuotes.map(quote => {
                                                            const color = getQuoteColor(quote.quote_type);

                                                            return (
                                                                <FileOnlyDepartmentSection
                                                                    key={quote.id}
                                                                    quote={quote}
                                                                    label={getQuoteLabel(quote.quote_type)}
                                                                    icon={getQuoteIcon(quote.quote_type, "w-4 h-4")}
                                                                    color={color}
                                                                    clearFiles={clearPdfs}
                                                                    isSubmitting={isSubmittingCompletion}
                                                                    disabled={isJobCancelled}
                                                                    existingFiles={getFilesForQuoteType(quote.quote_type)}
                                                                    onFilesChange={handleAdditionalDepartmentFilesChange}
                                                                    onComplete={handleCompleteFileOnlyDepartment}
                                                                    onSeeFile={seeFile}
                                                                    onDownloadFile={downloadFile}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </DropdownSection>
                                            );
                                        })()}
                                    </>
                                )}


                                {/* 4. Assigned Drivers — per child quote, supports multi-quote system */}
                                {assignment?.job_type !== "Estimation" && userProfile?.role !== "driver" &&
                                    <>
                                        {(() => {

                                            const deliveryMatchesQuote = (delivery, quote) => {
                                                if (!delivery || !quote) return false;
                                                if (delivery.quote_type !== quote.quote_type) return false;

                                                const deliveryCount = delivery.delivery_count ?? 0;
                                                const quoteDeliveryCount = quote.delivery_count ?? 0;

                                                if (deliveryCount !== quoteDeliveryCount) return false;

                                                const quoteId = quote.id;
                                                const quoteDisplayId = quote.child_unique_id;
                                                const quoteChildId = quote.child_id;

                                                // Best/new-system match
                                                if (delivery.child_id && delivery.child_id === quoteId) return true;

                                                // Your mixed current data support
                                                if (delivery.child_unique_id && delivery.child_unique_id === quoteId) return true;
                                                if (delivery.child_unique_id && delivery.child_unique_id === quoteDisplayId) return true;
                                                if (delivery.child_id && delivery.child_id === quoteChildId) return true;

                                                // Old system fallback only when neither side has child linkage
                                                if (
                                                    !quote.child_unique_id &&
                                                    !quote.child_id &&
                                                    !delivery.child_unique_id &&
                                                    !delivery.child_id
                                                ) {
                                                    return true;
                                                }

                                                return false;
                                            };

                                            const driverQuotes = (assignment.job_quotes ?? [])
                                                .filter(q => q.is_required && (q.delivery_count === null || (q.delivery_count ?? 0) === 0))
                                                .sort((a, b) => {
                                                    const ORDER = QUOTE_ORDER;
                                                    const typeOrder = (ORDER[a.quote_type] ?? 99) - (ORDER[b.quote_type] ?? 99);
                                                    if (typeOrder !== 0) return typeOrder;
                                                    return (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                                });
                                            const n = driverQuotes.length;
                                            const assignedCount = driverQuotes.filter(quote => {
                                                const deliveries = (assignment.job_deliveries ?? []).filter(d =>
                                                    deliveryMatchesQuote(d, quote)
                                                );

                                                return deliveries.some(d => d.assigned_driver_id);
                                            }).length;
                                            const gridCols =
                                                n <= 1 ? 'grid-cols-1' :
                                                    n === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                                                        n === 3 ? 'grid-cols-1 sm:grid-cols-3' :
                                                            'grid-cols-2 lg:grid-cols-4';
                                            return (
                                                <DropdownSection
                                                    title="Assigned Drivers"
                                                    titleStyle="text-zinc-700 text-sm"
                                                    icon={FiTruck}
                                                    iconStyle="text-zinc-800"
                                                    subtext={`${n} Deliveries - ${assignedCount}/${n} assigned`}
                                                    subtextStyle="text-xs "
                                                    defaultOpen={false}
                                                >
                                                    <div className={`grid gap-2 ${gridCols}`}>
                                                        {driverQuotes.map(quote => {
                                                            const quoteType = quote.quote_type;
                                                            const childName = quote.child_unique_id;
                                                            const childId = quote.child_id;
                                                            const quoteId = quote.id;

                                                            // Colour config per type
                                                            const C = {
                                                                ...getQuoteColor(quoteType),
                                                                icon: getQuoteIcon(quoteType, "w-3 h-3"),
                                                            };

                                                            // Match deliveries to this specific child quote
                                                            // job_deliveries.child_unique_id stores the job_quotes.id UUID (not the display string).
                                                            // — new deliveries: match d.child_unique_id (UUID) against quoteId
                                                            // — old deliveries (no child_unique_id): only show under quotes that also have no child name
                                                            const deliveries = (assignment.job_deliveries ?? [])
                                                                .filter(d => deliveryMatchesQuote(d, quote))
                                                                .sort((a, b) => (a.delivery_count ?? 0) - (b.delivery_count ?? 0));

                                                            const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];
                                                            const STATUS_STYLES = {
                                                                delivered: 'bg-emerald-100 text-emerald-700',
                                                                in_transit: 'bg-blue-100 text-blue-700',
                                                                scheduled: 'bg-amber-100 text-amber-700',
                                                            };

                                                            return (
                                                                <div key={quoteId || quoteType} className={`rounded-xl border ${C.border} overflow-hidden`}>

                                                                    {/* ── Quote header ── */}
                                                                    <div className={`flex items-center justify-between px-3 py-2 ${C.bg} border-b ${C.border}`}>
                                                                        <div className="flex items-center gap-2">
                                                                            <div className={`w-5 h-5 rounded flex items-center justify-center ${C.iconBg} text-white flex-shrink-0`}>
                                                                                {C.icon}
                                                                            </div>
                                                                            <div className="flex flex-col">
                                                                                <span className="text-xs font-semibold text-gray-700 capitalize">
                                                                                    {quoteType?.split("_").join(" ")}
                                                                                    {childName && (
                                                                                        <span className="ml-1.5 font-normal text-gray-400">· {childName}</span>
                                                                                    )}
                                                                                </span>
                                                                                {quote?.expected_delivery_date && (
                                                                                    <span className="text-[10px] font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 flex items-center gap-1 w-fit">
                                                                                        <FiCalendar className="w-3 h-3" />
                                                                                        {formatDateDriver(quote.expected_delivery_date)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {deliveries.length > 0 && (
                                                                                <span className="text-[10px] text-gray-400 font-medium">
                                                                                    {deliveries.length} {deliveries.length === 1 ? 'delivery' : 'deliveries'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {/* Assign button shown in header only when there are no deliveries yet */}
                                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && deliveries.length === 0 && (
                                                                            <CancelledAction>
                                                                                <button
                                                                                    onClick={() => openAssignDriverModal(quoteType, 0, childName ? quoteId : null, childName)}
                                                                                    className="flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-800 transition-colors"
                                                                                >
                                                                                    <FiTruck className="w-3 h-3" /> Assign
                                                                                </button>
                                                                            </CancelledAction>
                                                                        )}
                                                                    </div>

                                                                    {/* ── Delivery rows ── */}
                                                                    {deliveries.length === 0 ? (
                                                                        <div className="px-3 py-2.5 bg-white">
                                                                            <p className="text-xs text-gray-400">No deliveries scheduled yet</p>
                                                                        </div>
                                                                    ) : (
                                                                        <div className={`divide-y ${C.divider} bg-white`}>
                                                                            {deliveries.map((delivery, index) => {
                                                                                const deliveryCount = delivery.delivery_count ?? 0;
                                                                                const ordinal = ORDINALS[deliveryCount] ?? `${deliveryCount + 1}th`;
                                                                                const isCurrentDriver = userProfile?.role === 'driver' && delivery?.assigned_driver_id === userProfile.id;
                                                                                const statusStyle = STATUS_STYLES[delivery?.delivery_status] ?? 'bg-gray-100 text-gray-500';

                                                                                // Find matching job_quote row for units/notes
                                                                                const quoteCurrent = deliveryCount === 0 ? quote
                                                                                    : assignment.job_quotes?.find(q =>
                                                                                        q.quote_type === quoteType &&
                                                                                        (q.delivery_count ?? 0) === deliveryCount &&
                                                                                        (!childName || q.child_unique_id === childName)
                                                                                    );

                                                                                return (
                                                                                    <div key={delivery.id || `${quoteType}-${deliveryCount}-${delivery.child_id || delivery.child_unique_id || index}`} className="px-3 py-2.5">
                                                                                        {/* Delivery label — only shown when multiple deliveries */}
                                                                                        {deliveries.length > 1 && (
                                                                                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                                                                                                {ordinal} Delivery
                                                                                            </p>
                                                                                        )}

                                                                                        {delivery?.assigned_driver_id ? (
                                                                                            <>
                                                                                                {/* Driver row */}
                                                                                                <div className="flex items-center justify-between gap-2">
                                                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                                                        <div className="w-7 h-7 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                                                                            {delivery.assigned_driver_name?.charAt(0) || 'D'}
                                                                                                        </div>
                                                                                                        <div className="min-w-0">
                                                                                                            <p className="text-xs font-semibold text-gray-800 truncate leading-tight">
                                                                                                                {delivery.assigned_driver_name || 'Unknown'}
                                                                                                                {isCurrentDriver && <span className="ml-1 text-teal-500 font-normal">(You)</span>}
                                                                                                            </p>
                                                                                                            {delivery?.delivery_date ? (
                                                                                                                <p className="text-[10px] text-gray-400 truncate leading-tight">
                                                                                                                    {formatDate(delivery.delivery_date)}{delivery.delivery_time && ` · ${delivery.delivery_time}`}
                                                                                                                </p>
                                                                                                            ) : (
                                                                                                                <p className="text-[10px] text-gray-400 leading-tight">
                                                                                                                    Assigned {formatDate(delivery.assigned_at)}
                                                                                                                </p>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusStyle}`}>
                                                                                                            {delivery?.delivery_status?.replace(/_/g, ' ') || 'pending'}
                                                                                                        </span>
                                                                                                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                                            <CancelledAction>
                                                                                                                <button
                                                                                                                    onClick={() => handleRemoveDriver(quoteType, deliveryCount, childName ? quoteId : null)}
                                                                                                                    className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                                                                                    title="Remove driver"
                                                                                                                >
                                                                                                                    <FiX className="w-3.5 h-3.5" />
                                                                                                                </button>
                                                                                                            </CancelledAction>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>

                                                                                                {/* Units */}
                                                                                                {quoteCurrent?.selected_units?.length > 0 && (
                                                                                                    <div className="flex flex-wrap gap-1 mt-1.5 ml-9">
                                                                                                        {quoteCurrent.selected_units.map(u => (
                                                                                                            <span key={u} className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                                                                                                Unit {u}
                                                                                                            </span>
                                                                                                        ))}
                                                                                                    </div>
                                                                                                )}

                                                                                                {/* Note */}
                                                                                                {quoteCurrent?.delivery_note?.length > 0 && (
                                                                                                    <div className="flex items-start gap-1 mt-1.5 ml-9">
                                                                                                        <FiPenTool className="w-3 h-3 text-red-400 flex-shrink-0 mt-px rotate-180" />
                                                                                                        <p className="text-[10px] text-red-600 leading-tight">{quoteCurrent.delivery_note}</p>
                                                                                                    </div>
                                                                                                )}
                                                                                            </>
                                                                                        ) : (
                                                                                            /* No driver yet */
                                                                                            <div className="flex items-center justify-between">
                                                                                                <p className="text-xs text-gray-400">No driver assigned</p>
                                                                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                                    <CancelledAction>
                                                                                                        <button
                                                                                                            onClick={() => openAssignDriverModal(quoteType, deliveryCount, childName ? quoteId : null, childName)}
                                                                                                            className="flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-800 transition-colors"
                                                                                                        >
                                                                                                            <FiTruck className="w-3 h-3" /> Assign
                                                                                                        </button>
                                                                                                    </CancelledAction>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </DropdownSection>
                                            );
                                        })()}
                                    </>
                                }
                            </div>


                        </div>
                    </DropdownSection>

                    {!hasPermission(userProfile?.role, ["driver"]) &&
                        <DropdownSection
                            title="Departments"
                            titleStyle="text-zinc-800 text-base"
                            icon={Info}
                            iconStyle="text-zinc-700 self-center"
                            subtext={``}
                            subtextStyle="text-xs font-semibold text-zinc-500"
                            customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                            defaultOpen={true}
                        >
                            <>
                                {/* Job Specifications */}
                                {assignment.job_quotes?.some(q => q.is_required && ['roof', 'wall', 'floor'].includes(q.quote_type)) && (
                                    <div className="space-y-4">

                                        {/* Roof Specifications */}
                                        {assignment.job_quotes?.find(q => q.quote_type === 'roof' && q.is_required) && (
                                            <DropdownSection
                                                title={<span className="inline-flex items-center gap-1.5">Roof Department{assignment.job_quotes?.find(q => q.quote_type === 'roof' && q.is_required)?.on_hold && <OnHoldBadge />}</span>}
                                                titleStyle="text-zinc-700 text-sm"
                                                icon={FiHome}
                                                iconStyle="text-zinc-800"
                                                subtext={``}
                                                subtextStyle="text-xs "
                                                defaultOpen={false}
                                            >
                                                <DetailRow
                                                    label="Roof Type"
                                                    value={assignment.roof_type}
                                                />
                                                <DetailRow
                                                    label="Roof Truss Centres"
                                                    value={assignment.roof_truss_centres}
                                                />
                                                <DetailRow
                                                    label="Roof Measure"
                                                    value={
                                                        <CheckboxDisplay
                                                            label={`${assignment.site_measure_truss ? "Roof measure required" : "Roof measure not required"}`}
                                                            isChecked={assignment.site_measure_truss}
                                                        />
                                                    }

                                                />
                                            </DropdownSection>
                                        )}

                                        {/* Wall Specifications */}
                                        {assignment.job_quotes?.find(q => q.quote_type === 'wall' && q.is_required) && (
                                            <DropdownSection
                                                title={<span className="inline-flex items-center gap-1.5">Wall Department{assignment.job_quotes?.find(q => q.quote_type === 'wall' && q.is_required)?.on_hold && <OnHoldBadge />}</span>}
                                                titleStyle="text-zinc-700 text-sm"
                                                icon={PanelsTopLeft}
                                                iconStyle="text-zinc-800"
                                                subtext={``}
                                                subtextStyle="text-xs "
                                                defaultOpen={false}
                                            >

                                                <DetailRow
                                                    label="Building Type"
                                                    value={assignment.is_single_storey ? "Single Storey" : "Double Storey"}
                                                />

                                                {assignment.is_single_storey ? (
                                                    // Single Storey - Get from job_floor_specs where floor_level = 'single_storey' or 'ground_floor'
                                                    assignment.job_floor_specs?.filter(spec =>
                                                        spec.floor_level === 'single' || spec.floor_level === 'ground'
                                                    ).map((spec, index) => (
                                                        <div key={index} className="space-y-2 border-l-4 border-gray-200 pl-4">
                                                            <p className="text-sm font-medium text-gray-900">Single Storey Details</p>
                                                            <DetailRow
                                                                label="Load Bearing Wall Stud Sizes"
                                                                value={spec.load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Sizes"
                                                                value={spec.non_load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Centres"
                                                                value={spec.non_load_bearing_wall_stud_centres}
                                                            />
                                                            <DetailRow
                                                                label="Wall Height"
                                                                value={spec.wall_height}
                                                            />
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="space-y-4">
                                                        {/* Ground Floor */}
                                                        {assignment.job_floor_specs?.filter(spec => spec.floor_level === 'ground').map((spec, index) => (
                                                            <div key={index} className="space-y-2 border-l-4 border-blue-200 pl-4">
                                                                <p className="text-sm font-medium text-blue-900">Ground Floor</p>
                                                                <DetailRow
                                                                    label="Load Bearing Wall Stud Sizes"
                                                                    value={spec.load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Sizes"
                                                                    value={spec.non_load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Centres"
                                                                    value={spec.non_load_bearing_wall_stud_centres}
                                                                />
                                                                <DetailRow
                                                                    label="Wall Height"
                                                                    value={spec.wall_height}
                                                                />
                                                            </div>
                                                        ))}

                                                        {/* First Floor */}
                                                        {assignment.job_floor_specs?.filter(spec => spec.floor_level === 'first').map((spec, index) => (
                                                            <div key={index} className="space-y-2 border-l-4 border-green-200 pl-4">
                                                                <p className="text-sm font-medium text-green-900">First Floor</p>
                                                                <DetailRow
                                                                    label="Load Bearing Wall Stud Sizes"
                                                                    value={spec.load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Sizes"
                                                                    value={spec.non_load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Centres"
                                                                    value={spec.non_load_bearing_wall_stud_centres}
                                                                />
                                                                <DetailRow
                                                                    label="Wall Height"
                                                                    value={spec.wall_height}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                            </DropdownSection>
                                        )}

                                        {/* Floor Specifications */}
                                        {assignment.job_quotes?.find(q => q.quote_type === 'floor' && q.is_required) &&
                                            assignment.job_floors && assignment.job_floors.length > 0 && (
                                                <DropdownSection
                                                    title={<span className="inline-flex items-center gap-1.5">Floor Department{assignment.job_quotes?.find(q => q.quote_type === 'floor' && q.is_required)?.on_hold && <OnHoldBadge />}</span>}
                                                    titleStyle="text-zinc-700 text-sm"
                                                    icon={HiOutlineSquare3Stack3D}
                                                    iconStyle="text-zinc-800"
                                                    subtext={``}
                                                    subtextStyle="text-xs "
                                                    defaultOpen={false}
                                                >
                                                    <div className="space-y-4">
                                                        {assignment.job_floors.map((floor) => (
                                                            <div key={floor.id} className="border-l-4 border-green-200 pl-4 space-y-2">
                                                                <p className="text-sm font-medium text-green-900">
                                                                    Floor {floor.floor_number}
                                                                </p>
                                                                <DetailRow
                                                                    label="Floor Truss Size"
                                                                    value={floor.floor_truss_size}
                                                                />
                                                                <DetailRow
                                                                    label="Flooring Type"
                                                                    value={floor.flooring}
                                                                />
                                                                <CheckboxDisplay
                                                                    label="Balcony Included"
                                                                    isChecked={floor.balcony_included}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </DropdownSection>
                                            )}

                                        {/* Site Measure */}
                                        {assignment.site_measure_required && (
                                            <InfoCard title="Site Measure Requirements" icon={FiMapPin} color="yellow">
                                                <div className="flex flex-wrap gap-3">
                                                    <CheckboxDisplay
                                                        label="Wall Measurement"
                                                        isChecked={assignment.site_measure_wall}
                                                    />
                                                    <CheckboxDisplay
                                                        label="Floor Measurement"
                                                        isChecked={assignment.site_measure_floor}
                                                    />
                                                    <CheckboxDisplay
                                                        label="Truss Measurement"
                                                        isChecked={assignment.site_measure_truss}
                                                    />
                                                </div>
                                            </InfoCard>
                                        )}
                                    </div>
                                )}
                            </>
                        </DropdownSection>
                    }


                    {/* Design Management Section */}
                    {(assignment.job_type === "Design" || assignment.job_type === "Design & Estimation") && (!assignment?.is_parent || !assignment?.is_unit) &&
                        hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (
                            <DropdownSection
                                title="Design Status & Actions"
                                titleStyle="text-zinc-800 text-base"
                                icon={FiDollarSign}
                                iconStyle="text-zinc-700 self-center"
                                subtext={`${assignment.job_quotes?.filter(q =>
                                    q.is_required && !isFileOnlyQuoteType(q.quote_type)
                                ).length} sections required`}
                                subtextStyle="text-xs font-semibold text-zinc-600"
                                customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                                defaultOpen={true}
                            >
                                <div className="space-y-4">
                                    {/* <div className="flex items-center justify-between gap-2">
                                        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 pb-2 flex-1">
                                            Design Status & Actions
                                        </h2>
                                        <div className="text-xs text-gray-500 flex-shrink-0">
                                            {assignment.job_quotes?.filter(q => q.is_required).length} sections required
                                        </div>
                                    </div> */}

                                    <div className="grid gap-4">
                                        {assignment.job_quotes?.filter(q =>
                                            !isFileOnlyQuoteType(q.quote_type) &&
                                            (q.is_required || q.delivery_count === null)
                                        )
                                            .sort((a, b) => {
                                                const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                                                if (typeOrder !== 0) return typeOrder;
                                                return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
                                            })
                                            .map(quote => {
                                                if (quote.delivery_count ?? 0) return;
                                                const quoteType = quote.quote_type;
                                                // 3-case designer match (mirrors schedule.js logic):
                                                // 1. d.child_job_id set  → new-style: match by UUID
                                                // 2. d.child_job_id null + regular quote (no child_unique_id) → old-style fallback
                                                // 3. d.child_job_id null + named child quote → skip (would duplicate across siblings)
                                                const designers = assignment.job_designers?.filter(d => {
                                                    if (d.quote_type !== quoteType) return false;
                                                    if (d.child_job_id) return d.child_job_id === quote.id;
                                                    return !quote.child_unique_id;
                                                });
                                                const userDesigner = designers?.find(d => d.user_id === userProfile.id);

                                                const canActAsDesigner =
                                                    userProfile?.role === "designer" ||
                                                    userProfile?.permissions?.includes("ASSIGN_DESIGNERS");

                                                const isUserAssigned = canActAsDesigner && userDesigner;
                                                // Use quote.design_status as the authoritative source per child
                                                const isDesignCompleted = quote.design_status === 'completed';
                                                const isQuoteRequired = quote.is_required || false;

                                                if (!isQuoteRequired) return;

                                                return (
                                                    <DropdownSection
                                                        key={quote?.child_unique_id || quoteType}
                                                        title={<span className="inline-flex items-center gap-1.5 capitalize">{`${quote?.child_unique_id !== null ? quote?.child_unique_id : ""} ${quoteType} Design`}{quote?.on_hold && <OnHoldBadge />}</span>}
                                                        titleStyle={`text-zinc-700 text-sm capitalize`}
                                                        icon={quoteType === 'roof' ? Home :
                                                            quoteType === 'wall' ? PanelsTopLeft :
                                                                quoteType === 'floor' ? HiOutlineSquare3Stack3D :
                                                                    GiWoodBeam}
                                                        iconStyle={`text-zinc-700 ${quoteType === 'roof' ? '!text-blue-600' :
                                                            quoteType === 'wall' ? '!text-red-600' :
                                                                '!text-green-600'
                                                            }`}
                                                        subtext={`${quote.design_status === "not_required" ? "WAITING ASSIGNMENT" : quote.design_status?.replace('_', ' ').toUpperCase() || 'PENDING'}`}
                                                        subtextStyle={`px-2 py-0.5 rounded text-xs font-medium ${quote.design_status === 'completed' ? 'bg-emerald-100 !text-emerald-700' :
                                                            quote.design_status === 'in_progress' ? 'bg-amber-100 !text-amber-700' :
                                                                'bg-gray-100 !text-gray-700'
                                                            }`}
                                                        defaultOpen={hasPermission(userProfile?.role, ["designer"])}
                                                    >
                                                        <div key={quote?.child_unique_id || quoteType} className="rounded-lg border border-gray-200 bg-white overflow-hidden transition-all hover:shadow-sm">
                                                            {/* Header */}
                                                            {/* <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                                <div className="flex items-center justify-between"> */}
                                                            {/* <div className="flex items-center gap-2.5">
                                                                        <div className={`w-6 h-6 rounded-md flex items-center justify-center ${quoteType === 'roof' ? 'bg-blue-100 text-blue-600' :
                                                                            quoteType === 'wall' ? 'bg-red-100 text-red-600' :
                                                                                'bg-green-100 text-green-600'
                                                                            }`}>
                                                                            {quoteType === 'roof' ? <Home className="w-3.5 h-3.5" /> :
                                                                                quoteType === 'wall' ? <PanelsTopLeft className="w-3.5 h-3.5" /> :
                                                                                    quoteType === 'floor' ? <HiOutlineSquare3Stack3D className="w-3.5 h-3.5" /> :
                                                                                        <GiWoodBeam className="w-3.5 h-3.5" />}
                                                                        </div>
                                                                        <div>
                                                                            <h3 className="text-sm font-semibold text-gray-800 uppercase">
                                                                                {quote?.child_unique_id && quote?.child_unique_id} {quoteType} Design
                                                                            </h3>
                                                                        </div>
                                                                    </div> */}
                                                            {/* <div className="flex items-center gap-2">
                                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${quote.design_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                                                            quote.design_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                                                'bg-amber-100 text-amber-700'
                                                                            }`}>
                                                                            {quote.design_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                                        </span>
                                                                    </div> */}
                                                            {/* </div>
                                                            </div> */}

                                                            {/* Content */}
                                                            <div className="p-4 space-y-4">
                                                                {/* Assigned Designers */}
                                                                <div>
                                                                    <div className="flex items-center gap-2 mb-3">
                                                                        <FiUser className="w-4 h-4 text-gray-400" />
                                                                        <span className="text-sm font-medium text-gray-700">
                                                                            {designers?.length > 0 ? "Assigned Designers" : "No Designer Assigned"}
                                                                        </span>
                                                                    </div>

                                                                    {designers && designers.length > 0 ? (
                                                                        <div className="space-y-2">
                                                                            {designers.map((designer) => (
                                                                                <div key={designer.id} className="flex flex-col space-y-3">
                                                                                    <div key={designer.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                                                        <div className="flex items-center gap-3">
                                                                                            {renderUserAvatar(designer.user_id, designer.name, 'w-8 h-8', 'bg-gradient-to-br from-gray-400 to-gray-600', 'text-sm')}
                                                                                            <div>
                                                                                                <p className="font-medium text-gray-900">{designer.name}</p>
                                                                                                <p className="text-xs text-gray-500">
                                                                                                    Assigned {formatDate(designer.assigned_at)}
                                                                                                </p>
                                                                                            </div>

                                                                                        </div>
                                                                                        <div className="flex items-center gap-2">
                                                                                            {designer.is_completed ? (
                                                                                                <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                                                                                    <FiCheckCircle className="w-3 h-3" />
                                                                                                    Completed
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                                                                                    <FiClock className="w-3 h-3" />
                                                                                                    In Progress
                                                                                                </div>
                                                                                            )}
                                                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                                <CancelledAction>
                                                                                                    <button
                                                                                                        onClick={() => openRemoveDesignerModal(quoteType, designer.user_id, designer.child_job_id || null, designer.name, designer.is_completed, quote?.child_unique_id || null)}
                                                                                                        className="text-red-500 hover:text-red-700"
                                                                                                        title="Remove designer"
                                                                                                    >
                                                                                                        <FiX className="w-5 h-5" />
                                                                                                    </button>
                                                                                                </CancelledAction>
                                                                                            )}
                                                                                        </div>

                                                                                    </div>
                                                                                    <div className="flex flex-col">
                                                                                        <div className="flex items-center gap-2 mb-3">
                                                                                            <FiPenTool className="rotate-180 w-4 h-4 text-gray-400" />
                                                                                            <p className="text-sm font-medium text-gray-700">Finishing Note</p>
                                                                                        </div>

                                                                                        <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm" >
                                                                                            <p className={`text-sm ${designer?.finishing_note?.length <= 0 ? "text-gray-500 normal-case" : "text-green-700 italic"}`}>
                                                                                                {designer?.finishing_note || "Not specified any notes."}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>

                                                                                    {designer?.is_completed &&
                                                                                        <div className="flex flex-col">
                                                                                            <div className="flex items-center gap-2 mb-3">
                                                                                                <FiCheckCircle className="w-4 h-4 text-green-400" />
                                                                                                <p className="text-sm font-medium text-green-700">Completed by {designer?.completed_by_name || designer?.name}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    }
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-200">
                                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                <CancelledAction>
                                                                                    <button
                                                                                        onClick={() => openAssignDesignerModal(quoteType, quote.id, quote?.child_unique_id || null)}
                                                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium w-full h-full py-4"
                                                                                    >
                                                                                        Assign Designer
                                                                                    </button>
                                                                                </CancelledAction>
                                                                            )}
                                                                            {hasPermission(userProfile?.role, ["designer"]) && (
                                                                                <CancelledAction>
                                                                                    <button
                                                                                        onClick={() => handleSelfAssignDesigner(quoteType, quote.id, quote?.child_unique_id || null)}
                                                                                        disabled={isAssigningDesigner}
                                                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium w-full h-full py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                    >
                                                                                        {isAssigningDesigner ? 'Assigning...' : 'Assign Self'}
                                                                                    </button>
                                                                                </CancelledAction>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Designer Action Panel - Only for assigned designers who haven't completed */}
                                                                {(isUserAssigned || hasPermission(userProfile?.role, ["super_admin", "admin"]))
                                                                    && !isDesignCompleted && (
                                                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                                            <div className="flex items-center gap-2 mb-3">
                                                                                <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                                                                                    <FiTool className="w-3 h-3 text-white" />
                                                                                </div>
                                                                                <h4 className="font-semibold text-gray-800">Complete Your Design</h4>
                                                                            </div>

                                                                            <div className="space-y-4">
                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                        Upload Design Files *
                                                                                    </label>
                                                                                    <div className="bg-white p-4 rounded-lg border-gray-200 hover:border-green-300 transition-colors">
                                                                                        <DragDropUploader
                                                                                            onFilesChange={handlePdfsChange}
                                                                                            clearFiles={clearPdfs}
                                                                                        />
                                                                                    </div>
                                                                                </div>

                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                        Completion Notes
                                                                                    </label>
                                                                                    <textarea
                                                                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                                        value={completionNotes[quoteType] || ''}
                                                                                        onChange={(e) => setCompletionNotes(prev => ({
                                                                                            ...prev,
                                                                                            [quoteType]: e.target.value
                                                                                        }))}
                                                                                        rows="3"
                                                                                        placeholder={`Add any notes about the completed ${quoteType} design work...`}
                                                                                    />
                                                                                </div>

                                                                                <div>
                                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                                        Delivery Note (Optional)
                                                                                    </label>
                                                                                    <textarea
                                                                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                                        value={quoteDeliveryNotes[quoteType] || ''}
                                                                                        onChange={(e) => setQuoteDeliveryNotes(prev => ({
                                                                                            ...prev,
                                                                                            [quoteType]: e.target.value
                                                                                        }))}
                                                                                        rows="2"
                                                                                        placeholder={`Add delivery instructions or notes for the ${quoteType} delivery...`}
                                                                                    />
                                                                                </div>

                                                                                <div className="flex flex-col sm:flex-row sm:justify-end pt-2 gap-2">
                                                                                    {isUserAssigned &&
                                                                                        <CancelledAction>
                                                                                            <button
                                                                                                onClick={() => handleRemoveSelfAssignment("designer", quoteType)}
                                                                                                className="px-6 py-3 bg-gradient-to-r from-red-700 to-red-800 text-white font-medium rounded-lg hover:from-red-800 hover:to-red-900 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                            >
                                                                                                <FiDelete className="w-4 h-4" />
                                                                                                Unassign yourself
                                                                                            </button>
                                                                                        </CancelledAction>
                                                                                    }

                                                                                    <CancelledAction>
                                                                                        <button
                                                                                            onClick={() => handleCompleteDesign(quoteType, designers, quote?.id)}
                                                                                            disabled={
                                                                                                isSubmittingCompletion ||
                                                                                                !uploadedFiles?.pdfFiles?.length
                                                                                                // isEstimationPricingMissing()
                                                                                            }
                                                                                            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        >
                                                                                            {isSubmittingCompletion ? (
                                                                                                <>
                                                                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                                                    Submitting...
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <FiCheckCircle className="w-4 h-4" />
                                                                                                    Complete Design
                                                                                                </>
                                                                                            )}
                                                                                        </button>
                                                                                    </CancelledAction>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </DropdownSection>
                                                );
                                            })}
                                    </div>
                                </div>
                            </DropdownSection>
                        )}

                    {/* ================= ESTIMATION MANAGEMENT SECTION ================= */}
                    {(assignment.job_type === "Estimation" || assignment.job_type === "Design & Estimation") &&
                        hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (() => {

                            // ================= HELPER VARIABLES =================
                            const estimator = assignment.job_estimators;

                            const isEstimatorAssigned = estimator?.user_id === userProfile.id;
                            const isAdmin = hasPermission(userProfile?.role, ["super_admin", "admin"]);

                            // Source of truth for completion status
                            const isEstimationCompleted =
                                estimator?.is_completed === true ||
                                assignment.job_quotes?.estimation_status === "completed";


                            const isAdminOrSuperAdmin = hasPermission(userProfile?.role, ["admin", "super_admin"]);

                            const isAssignedEstimator =
                                assignment.job_estimators?.user_id === userProfile?.id;

                            const estimatorExists = !!assignment.job_estimators;

                            const canCompleteEstimation =
                                !assignment.job_estimators?.is_completed &&
                                (
                                    isAssignedEstimator ||
                                    isAdminOrSuperAdmin
                                );

                            return (
                                <DropdownSection
                                    title="Estimation Status & Actions"
                                    titleStyle="text-zinc-800 text-base"
                                    icon={FiDollarSign}
                                    iconStyle="text-zinc-700 self-center"
                                    subtext={``}
                                    subtextStyle=""
                                    customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                                    defaultOpen={hasPermission(userProfile?.role, ["estimator"])}
                                >
                                    <div className="space-y-4">
                                        {/* <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 pb-2">
                                            Estimation Status & Actions
                                        </h2> */}
                                        {(assignment?.job_type?.toLowerCase()?.includes("estimation")) &&
                                            <>
                                                {/* Quote Sent Status - Only visible to Super Admins and Admins */}
                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${assignment.quote_sent
                                                                    ? 'bg-green-100 text-green-600'
                                                                    : 'bg-gray-100 text-gray-400'
                                                                    }`}>
                                                                    <FiSend className="w-4 h-4" />
                                                                </div>
                                                                <div>
                                                                    <h3 className="text-sm font-semibold text-gray-800">Quote Sent Status</h3>
                                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                                        {assignment.quote_sent
                                                                            ? 'Quote has been sent to the customer'
                                                                            : 'Quote has not been sent yet'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <CancelledAction>
                                                                <button
                                                                    onClick={handleToggleQuoteSent}
                                                                    disabled={isTogglingQuoteSent}
                                                                    className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 w-full sm:w-auto justify-center ${assignment.quote_sent
                                                                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                                >
                                                                    {isTogglingQuoteSent ? (
                                                                        <>
                                                                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                                            <span>Updating...</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {assignment.quote_sent ? (
                                                                                <>
                                                                                    <FiX className="w-4 h-4" />
                                                                                    <span>Mark as Not Sent</span>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <FiCheck className="w-4 h-4" />
                                                                                    <span>Mark as Sent</span>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </CancelledAction>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        }

                                        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-6 h-6 rounded-md flex items-center justify-center bg-gray-200 text-gray-600">
                                                            <FiDollarSign className="w-3.5 h-3.5" />
                                                        </div>
                                                        <h3 className="text-sm font-semibold text-gray-700">Estimation</h3>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-4 space-y-4">

                                                {/* ================= ASSIGNED ESTIMATOR ================= */}
                                                {estimator?.user_id ? (
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiUser className="w-4 h-4 text-gray-400" />
                                                            <span className="text-sm font-medium text-gray-700">Assigned Estimator</span>
                                                        </div>
                                                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                            <div className="flex items-center gap-3">
                                                                {renderUserAvatar(estimator.user_id, estimator.name, 'w-8 h-8', 'bg-gray-600', 'text-sm')}
                                                                <div>
                                                                    <p className="font-medium text-gray-900">{estimator.name}</p>
                                                                    <p className="text-xs text-gray-500">
                                                                        Assigned {formatDate(estimator.assigned_at)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {isEstimationCompleted ? (
                                                                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                                                        <FiCheckCircle className="w-3 h-3" />
                                                                        Completed
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                                                        <FiClock className="w-3 h-3" />
                                                                        In Progress
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {isEstimationCompleted &&
                                                            <div className="flex flex-col mt-3">
                                                                <div className="flex items-center gap-2 mb-3">
                                                                    <FiCheckCircle className="w-4 h-4 text-green-400" />
                                                                    <p className="text-sm font-medium text-green-700">Completed by {estimator?.completed_by_name || estimator?.name}</p>
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-200">
                                                        {isAdmin && (
                                                            <CancelledAction>
                                                                <button
                                                                    onClick={openAssignEstimatorModal}
                                                                    className="text-xs text-purple-600 hover:text-purple-800 font-medium w-full h-full py-4"
                                                                >
                                                                    Assign Estimator
                                                                </button>
                                                            </CancelledAction>
                                                        )}
                                                        {hasPermission(userProfile?.role, ["estimator", "designer"]) && !isAdmin && (
                                                            <CancelledAction>
                                                                <button
                                                                    onClick={handleSelfAssignEstimator}
                                                                    className="text-xs text-purple-600 hover:text-purple-800 font-medium w-full h-full py-4"
                                                                >
                                                                    Assign Self
                                                                </button>
                                                            </CancelledAction>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Estimator Action Panel */}
                                                {canCompleteEstimation && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                                                                <FiDollarSign className="w-3 h-3 text-white" />
                                                            </div>
                                                            <h4 className="font-semibold text-gray-800">
                                                                Complete Estimation
                                                                {isAdminOrSuperAdmin && !isAssignedEstimator && (
                                                                    <span className="ml-2 text-xs text-gray-500">(Admin Override)</span>
                                                                )}
                                                            </h4>
                                                        </div>

                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Upload Estimation Files *
                                                                </label>
                                                                <div className="bg-white p-4 rounded-lg border-gray-200 hover:border-purple-300 transition-colors">
                                                                    <DragDropUploader
                                                                        onFilesChange={handlePdfsChange}
                                                                        clearFiles={clearPdfs}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    Completion Notes
                                                                </label>
                                                                <textarea
                                                                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                    value={completionNotes.estimation || ''}
                                                                    onChange={(e) =>
                                                                        setCompletionNotes(prev => ({
                                                                            ...prev,
                                                                            estimation: e.target.value
                                                                        }))
                                                                    }
                                                                    rows="3"
                                                                    placeholder="Add any notes about the completed estimation..."
                                                                />
                                                            </div>

                                                            <EstimatorPricingForm
                                                                assignment={assignment}
                                                                company={company}
                                                                pricingValues={pricingValues}
                                                                setPricingValues={setPricingValues}
                                                                required={mustEnterPricingBeforeEstimationComplete()}
                                                            />

                                                            <div className="flex flex-col sm:flex-row sm:justify-end pt-2 gap-2">
                                                                {/* Only assigned estimator can unassign themselves */}
                                                                {isAssignedEstimator && (
                                                                    <CancelledAction>
                                                                        <button
                                                                            onClick={() => handleRemoveSelfAssignment("estimator", null)}
                                                                            className="px-6 py-3 bg-gradient-to-r from-red-700 to-red-800 text-white font-medium rounded-lg hover:from-red-800 hover:to-red-900 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                                                                        >
                                                                            <FiDelete className="w-4 h-4" />
                                                                            Unassign yourself
                                                                        </button>
                                                                    </CancelledAction>
                                                                )}

                                                                <CancelledAction>
                                                                    <button
                                                                        onClick={() => handleCompleteEstimation(estimator)}
                                                                        disabled={
                                                                            isSubmittingCompletion ||
                                                                            !uploadedFiles?.pdfFiles?.length ||
                                                                            isEstimationPricingMissing()
                                                                        }
                                                                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        {isSubmittingCompletion ? (
                                                                            <>
                                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                                Submitting...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FiCheckCircle className="w-4 h-4" />
                                                                                Complete Estimation
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </CancelledAction>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                </DropdownSection>
                            );
                        })()}




                    {/* New Delivery Section */}
                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex flex-row items-start gap-3">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors bg-gray-100 flex-shrink-0`}>
                                    <FiTruck className="w-4 h-4 text-gray-500" />
                                </div>
                                <div className="flex flex-col">
                                    <h3 className="text-sm font-semibold text-gray-800 !mb-0">Create New Delivery</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">Create new delivery record for unit jobs or extra content that needs to be shipped.</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3 mt-4">
                                {['roof', 'wall', 'floor'].map(quoteType => {
                                    const quote = assignment.job_quotes?.find(q => q.quote_type === quoteType);
                                    const isRequired = quote?.is_required;
                                    const isDelivered = quote?.delivery_status === 'delivered';

                                    return (
                                        <CancelledAction>
                                            <button
                                                key={quoteType}
                                                onClick={() => handleOpenNewDeliveryModal(quoteType)}
                                                disabled={!isRequired}
                                                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 capitalize ${!isRequired
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : isDelivered
                                                        ? 'bg-green-100 text-green-700 border-2 border-green-300 hover:bg-green-200'
                                                        : 'bg-blue-100 text-blue-700 border-2 border-blue-300 hover:bg-blue-200'
                                                    }`}
                                            >
                                                {quoteType === 'roof' && <Home className="w-4 h-4" />}
                                                {quoteType === 'wall' && <PanelsTopLeft className="w-4 h-4" />}
                                                {quoteType === 'floor' && <HiOutlineSquare3Stack3D className="w-4 h-4" />}
                                                {quoteType === 'timber' && <GiWoodBeam className="w-4 h-4" />}
                                                <span>{quoteType}</span>
                                                {isDelivered && <FiCheckCircle className="w-4 h-4" />}
                                            </button>
                                        </CancelledAction>
                                    );
                                })}
                            </div>
                        </div>
                    )}



                    {/* {!hasPermission(userProfile?.role, ["driver"]) &&
                        <> */}
                    {/* Additional Information */}
                    {/* <div className={`grid gap-4 grid-cols-1`}> */}

                    {/* Additional Details */}
                    {/* <InfoCard title="Additional Information" icon={FiAlertCircle} color="gray">
                                    {assignment.jobType !== "Timber" &&
                                        <CheckboxDisplay
                                            label="Unit Project"
                                            isChecked={assignment?.is_unit}
                                        />
                                    }
                                    {assignment?.is_unit && (
                                        <DetailRow
                                            label="Number of Units"
                                            value={assignment?.unit_count}
                                        />
                                    )}

                                    {(!assignment.jobInformation?.isUnit && assignment.jobType == "Timber") && "No additional information"}
                                </InfoCard> */}

                    {/* {assignment?.job_type !== "Estimation" &&
                                    <InfoCard title="Invoice Status" icon={FiAlertCircle} color="gray">
                                        {
                                            assignment?.job_quotes?.map(quote => {
                                                if (!quote?.is_required) return;

                                                return (
                                                    <div key={quote?.child_unique_id || quote?.quote_type}>
                                                        <CheckboxDisplay
                                                            label={`${quote?.child_unique_id ? `${quote?.quote_type?.toUpperCase()} - ${quote?.child_unique_id}` : quote?.quote_type?.toUpperCase()}`}
                                                            isChecked={quote?.invoiced}
                                                        />
                                                    </div>
                                                )
                                            })
                                        }

                                    </InfoCard>
                                } */}
                    {/* </div>
                        </>
                    } */}



                    {/* JOB CHILDS */}
                    {/* {assignment?.job_type !== "Estimation" && assignment?.selected_departments?.length > 0 && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-2">
                                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex-1">Child Jobs</h2>
                                <div className="text-sm text-gray-500 flex-shrink-0">
                                    {assignment.job_quotes?.filter(q => q.is_required).length} required
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                {assignment.job_quotes.filter(dep => dep?.is_required === true).map(filteredDep => {
                                    const designers = assignment.job_designers?.filter(d =>
                                        filteredDep?.child_unique_id
                                            ? d.child_job_id === filteredDep.id
                                            : d.quote_type === filteredDep.quote_type
                                    ) || [];

                                    const typeColors = {
                                        roof: { bg: '', border: 'border-gray-200', icon: 'text-blue-600', iconBg: 'bg-blue-100' },
                                        wall: { bg: '', border: 'border-gray-200', icon: 'text-red-600', iconBg: 'bg-red-100' },
                                        floor: { bg: '', border: 'border-gray-200', icon: 'text-green-600', iconBg: 'bg-green-100' },
                                    };
                                    const c = typeColors[filteredDep.quote_type] || { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', iconBg: 'bg-gray-100' };

                                    return (
                                        <div
                                            key={filteredDep?.child_unique_id || filteredDep?.quote_type}
                                            onClick={() => navigate(`./${filteredDep?.id}`)}
                                            className={`group relative flex flex-col gap-1.5 rounded-lg border-2 ${c.border} ${c.bg} p-2 cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5`}
                                        >
                                            {filteredDep?.on_hold && (
                                                <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none">
                                                    ON HOLD
                                                </span>
                                            )}

                                            {/* Header row */}
                    {/* <div className="flex items-center gap-1.5">
                                                <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${c.iconBg} ${c.icon}`}>
                                                    {getQuoteIcon(filteredDep?.quote_type)}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-gray-900 text-xs leading-tight truncate">
                                                        {filteredDep?.child_unique_id}
                                                    </p>
                                                    <p className={`text-[10px] font-semibold capitalize ${c.icon} leading-none`}>
                                                        {filteredDep?.quote_type}
                                                    </p>
                                                </div>
                                                <FiArrowLeft className="w-3 h-3 text-gray-300 group-hover:text-gray-500 rotate-180 transition-colors flex-shrink-0" />
                                            </div> */}

                    {/* Status badges */}
                    {/* <div>{renderStatusFields(assignment, filteredDep)}</div> */}

                    {/* Footer */}
                    {/* <div className="flex flex-col gap-1.5 pt-1 border-t border-gray-200/70">
                                                {designers.length > 0 ? (
                                                    <div className="flex items-center gap-1 min-w-0">
                                                        <div className="flex -space-x-1">
                                                            {designers.slice(0, 2).map((d, i) => (
                                                                <div key={i} className="w-4 h-4 rounded-full bg-gray-400 text-white text-[8px] font-bold flex items-center justify-center ring-1 ring-white flex-shrink-0">
                                                                    {d.name?.charAt(0)}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <span className="text-[10px] text-gray-500 truncate leading-none">
                                                            {designers.map(d => d.name?.split(' ')[0]).join(', ')}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400 italic leading-none">No designer</span>
                                                )} */}

                    {/* {(filteredDep?.expected_finish_date || filteredDep?.expected_delivery_date) && (
                                                    <span className="text-[10px] font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 flex items-center gap-1 w-fit">
                                                        <FiCalendar className="w-3 h-3" />
                                                        {formatDateDriver(filteredDep?.expected_finish_date || filteredDep?.expected_delivery_date)}
                                                    </span>
                                                )}
                                            </div>
                                        </div> */}
                    {/* );
                                })}
                            </div>
                        </div> */}
                    {/* )}  */}







                    {/* Designer Assignment Modal */}
                    {showDesignerModal && (
                        <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                            <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4 gap-2">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">
                                        Assign Designer for {selectedQuoteType?.charAt(0).toUpperCase() + selectedQuoteType?.slice(1)}{selectedChildQuoteName ? ` (${selectedChildQuoteName})` : ''}
                                    </h3>
                                    <CancelledAction>
                                        <button
                                            onClick={() => { setShowDesignerModal(false); setSelectedChildQuoteId(null); setSelectedChildQuoteName(null); }}
                                            className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                        >
                                            <FiX className="w-5 h-5" />
                                        </button>
                                    </CancelledAction>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                            Select Designer
                                        </label>
                                        {(() => {
                                            const qt = selectedQuoteType?.toLowerCase();
                                            const matched = availableDesigners.filter(d => d.department?.toLowerCase() === qt);
                                            const noDept = availableDesigners.filter(d => !d.department);
                                            const qtLabel = selectedQuoteType
                                                ? selectedQuoteType.charAt(0).toUpperCase() + selectedQuoteType.slice(1)
                                                : 'Quote';
                                            const renderOpt = d => (
                                                <option key={d.id} value={d.id}>
                                                    {d.full_name || d.username}
                                                </option>
                                            );
                                            return (
                                                <select
                                                    value={selectedDesignerId}
                                                    onChange={e => setSelectedDesignerId(e.target.value)}
                                                    className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                                                >
                                                    <option value="">Choose a designer…</option>
                                                    {matched.length > 0 && (
                                                        <optgroup label={`${qtLabel} designers`}>
                                                            {matched.map(renderOpt)}
                                                        </optgroup>
                                                    )}
                                                    {noDept.length > 0 && (
                                                        <optgroup label="No department">
                                                            {noDept.map(renderOpt)}
                                                        </optgroup>
                                                    )}
                                                    {matched.length === 0 && noDept.length === 0 && (
                                                        <option disabled value="">No designers available</option>
                                                    )}
                                                </select>
                                            );
                                        })()}
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        <CancelledAction>
                                            <button
                                                onClick={() => handleAssignDesigner()}
                                                disabled={!selectedDesignerId || isAssigningDesigner}
                                                className="flex-1 py-3 md:py-2 px-4 bg-blue-500 text-white text-sm md:text-base rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                            >
                                                {isAssigningDesigner ? 'Assigning...' : 'Assign Designer'}
                                            </button>
                                        </CancelledAction>
                                        <CancelledAction>
                                            <button
                                                onClick={() => { setShowDesignerModal(false); setSelectedChildQuoteId(null); setSelectedChildQuoteName(null); }}
                                                className="flex-1 py-3 md:py-2 px-4 bg-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-400 transition-colors min-h-[44px]"
                                            >
                                                Cancel
                                            </button>
                                        </CancelledAction>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Estimator Assignment Modal */}
                    {showEstimatorModal && (
                        <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                            <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4 gap-2">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">Assign Estimator</h3>
                                    <CancelledAction>
                                        <button
                                            onClick={() => setShowEstimatorModal(false)}
                                            className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                        >
                                            <FiX className="w-5 h-5" />
                                        </button>
                                    </CancelledAction>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                            Select Estimator
                                        </label>
                                        <select
                                            value={selectedEstimatorId}
                                            onChange={(e) => setSelectedEstimatorId(e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                                        >
                                            <option value="">Choose an estimator...</option>
                                            {availableEstimators.map(estimator => (
                                                <option key={estimator.id} value={estimator.id}>
                                                    {estimator.username || estimator.full_name} ({estimator.role})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        <CancelledAction>
                                            <button
                                                onClick={handleAssignEstimator}
                                                disabled={!selectedEstimatorId}
                                                className="flex-1 py-3 md:py-2 px-4 bg-green-500 text-white text-sm md:text-base rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                            >
                                                Assign Estimator
                                            </button>
                                        </CancelledAction>
                                        <CancelledAction>
                                            <button
                                                onClick={() => setShowEstimatorModal(false)}
                                                className="flex-1 py-3 md:py-2 px-4 bg-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-400 transition-colors min-h-[44px]"
                                            >
                                                Cancel
                                            </button>
                                        </CancelledAction>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Driver Assignment Modal */}
                    {showDriverModal && (
                        <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                            <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4 gap-2">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">
                                        Assign Driver for {selectedQuoteTypeForDriver?.charAt(0).toUpperCase() + selectedQuoteTypeForDriver?.slice(1)}{selectedChildQuoteNameForDriver ? ` (${selectedChildQuoteNameForDriver})` : ''} Delivery
                                    </h3>
                                    <CancelledAction>
                                        <button
                                            onClick={() => { setShowDriverModal(false); setSelectedQuoteTypeForDriver(null); setSelectedChildQuoteIdForDriver(null); setSelectedChildQuoteNameForDriver(null); }}
                                            className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                        >
                                            <FiX className="w-5 h-5" />
                                        </button>
                                    </CancelledAction>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                            Select Driver
                                        </label>
                                        <select
                                            value={selectedDriverId}
                                            onChange={(e) => setSelectedDriverId(e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                                        >
                                            <option value="">Choose a driver...</option>
                                            {availableDrivers.map(driver => (
                                                <option key={driver.id} value={`${driver.id}_${driver.username || driver.full_name}`}>
                                                    {driver.username || driver.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        <CancelledAction>
                                            <button
                                                onClick={handleAssignDriver}
                                                disabled={!selectedDriverId}
                                                className="flex-1 py-3 md:py-2 px-4 bg-teal-500 text-white text-sm md:text-base rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                            >
                                                Assign Driver
                                            </button>
                                        </CancelledAction>
                                        <CancelledAction>
                                            <button
                                                onClick={() => { setShowDriverModal(false); setSelectedQuoteTypeForDriver(null); setSelectedDriverId(''); setSelectedChildQuoteIdForDriver(null); setSelectedChildQuoteNameForDriver(null); }}
                                                className="flex-1 py-3 md:py-2 px-4 bg-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-400 transition-colors min-h-[44px]"
                                            >
                                                Cancel
                                            </button>
                                        </CancelledAction>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!hasPermission(userProfile?.role, ["driver"]) && (
                        <ActivityLogsTable
                            logs={actionLogs}
                            showLogs={showLogs}
                            onToggle={() => setShowLogs(prev => !prev)}
                        />
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-200">
                        {hasPermission(userProfile?.role, ["super_admin", "admin", "estimator", "designer"]) && (
                            <CancelledAction>
                                <button
                                    onClick={() => setEditOpen(true)}
                                    className="flex-1 sm:flex-none px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-center"
                                >
                                    Edit Assignment
                                </button>
                            </CancelledAction>
                        )}
                        <button
                            onClick={() => navigate(`/${companySlug}/assignment/list`, {
                                state: { from: location.pathname }
                            })}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-center"
                        >
                            Back to List
                        </button>
                    </div>

                    {/* Delete Job Button - Only for Superadmin and Admin */}
                    {hasPermission(userProfile?.role, ['super_admin', 'admin']) && (
                        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-red-800">Danger Zone</h4>
                                    <p className="text-xs text-red-600 mt-1">
                                        Cancel or permanently delete this job
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                    {assignment?.is_cancelled ? (
                                        <button
                                            onClick={handleRestoreCancelledJob}
                                            disabled={isCancellingJob}
                                            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <FiCheck className="w-4 h-4" />
                                            {isCancellingJob ? 'Restoring...' : 'Restore Job'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setShowCancelModal(true)}
                                            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium w-full sm:w-auto"
                                        >
                                            <FiX className="w-4 h-4" />
                                            Cancel Job
                                        </button>
                                    )}

                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium w-full sm:w-auto"
                                    >
                                        <FiTrash2 className="w-4 h-4" />
                                        Delete Job
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div >

            {/* Hidden file input for folder uploads */}
            <input
                ref={fileUploadInputRef}
                type="file"
                className="hidden"
                onChange={handleFileInputChange}
            />

            {/* File Viewer Modal */}
            {
                fileViewerOpen && selectedFile && (
                    <FileViewer
                        file={selectedFile}
                        filename={viewerFiles[selectedFileIndex].filename}
                        originalName={viewerFiles[selectedFileIndex].original_name}
                        fileType={viewerFiles[selectedFileIndex].file_type}
                        files={viewerFiles}
                        currentIndex={selectedFileIndex}
                        onNavigate={(newIndex) => {
                            setSelectedFileIndex(newIndex);
                            setSelectedFile(viewerFiles[newIndex]);
                        }}
                        onClose={() => setFileViewerOpen(false)}
                        jobId={assignment.id}
                        company={companySlug}
                    />
                )
            }



            {/* New Delivery Modal */}
            {showNewDeliveryModal && newDeliveryQuoteType && (
                <div className="mobile-dialog-shell fixed top-0 inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[80vh] overflow-hidden">
                        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Create New Delivery - {assignment.unique_id}
                                </h2>
                                <p className="text-sm text-gray-600 capitalize mt-1">
                                    {newDeliveryQuoteType} Quote
                                </p>
                            </div>
                            <CancelledAction>
                                <button
                                    onClick={handleCloseNewDeliveryModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </CancelledAction>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            {/* Delivery Status Info */}
                            {(() => {
                                const quote = assignment.job_quotes?.find(q => q.quote_type === newDeliveryQuoteType);
                                const deliveryStatus = quote?.delivery_status;

                                return (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${deliveryStatus === 'delivered' ? 'bg-green-100 text-green-600' :
                                                deliveryStatus === 'in_transit' ? 'bg-blue-100 text-blue-600' :
                                                    'bg-gray-100 text-gray-400'
                                                }`}>
                                                <FiTruck className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Current Status</p>
                                                <p className="text-sm text-gray-600 capitalize">
                                                    {deliveryStatus ? deliveryStatus.replace('_', ' ') : 'Not Scheduled'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Delivery Note */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Delivery Note
                                </label>
                                <textarea
                                    value={newDeliveryNote}
                                    onChange={(e) => setNewDeliveryNote(e.target.value)}
                                    placeholder="Enter delivery note..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    rows={4}
                                />
                            </div>

                            {/* Delivery Time Slot */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Delivery Time Slot
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {['1st', '2nd', '3rd', '4th', '5th'].map(slot => (
                                        <CancelledAction>
                                            <button
                                                key={slot}
                                                onClick={() => setNewDeliveryTimeSlot(slot)}
                                                className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${newDeliveryTimeSlot === slot
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                            >
                                                {slot}
                                            </button>
                                        </CancelledAction>
                                    ))}
                                    {newDeliveryTimeSlot && (
                                        <CancelledAction>
                                            <button
                                                onClick={() => setNewDeliveryTimeSlot(null)}
                                                className="px-4 py-3 rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 text-red-700 font-medium transition-all"
                                            >
                                                Clear
                                            </button>
                                        </CancelledAction>
                                    )}
                                </div>
                            </div>

                            {/* Scheduled For Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Scheduled For
                                </label>
                                <input
                                    type="date"
                                    value={newDeliveryScheduledDate}
                                    onChange={(e) => setNewDeliveryScheduledDate(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Unit Selection - Only for unit type assignments */}
                            {assignment.is_unit && assignment.unit_count > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Select Units ({newDeliverySelectedUnits.length} selected)
                                    </label>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                        {Array.from({ length: assignment.unit_count }, (_, i) => i + 1).map(unitNumber => (
                                            <button
                                                key={unitNumber}
                                                onClick={() => handleToggleUnit(unitNumber)}
                                                className={`px-3 py-2 rounded-lg border-2 transition-all font-medium ${newDeliverySelectedUnits.includes(unitNumber)
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                            >
                                                {unitNumber}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 flex-shrink-0 rounded-b-xl">
                            <button
                                onClick={handleCloseNewDeliveryModal}
                                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium w-full sm:w-auto text-center"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitNewDelivery}
                                className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium w-full sm:w-auto text-center"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Job Confirmation Modal */}
            {showCancelModal && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                                    <FiX className="w-5 h-5 text-orange-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Cancel Job</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowCancelModal(false);
                                    setCancelConfirmText('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <p className="text-sm text-orange-800 font-medium">⚠️ This will mark the job as cancelled.</p>
                                <p className="text-xs text-orange-600 mt-2">
                                    The job will not be deleted. It will stay in the system with cancelled status.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-orange-600">{assignment?.unique_id}</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={cancelConfirmText}
                                    onChange={(e) => setCancelConfirmText(e.target.value)}
                                    placeholder="Enter job unique ID"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowCancelModal(false);
                                        setCancelConfirmText('');
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleCancelJob}
                                    disabled={cancelConfirmText !== assignment?.unique_id || isCancellingJob}
                                    className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isCancellingJob ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Cancelling...
                                        </>
                                    ) : (
                                        <>
                                            <FiX className="w-4 h-4" />
                                            Cancel Job
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Job Confirmation Modal */}
            {showDeleteModal && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                    <FiTrash2 className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Delete Job</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteConfirmText('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-sm text-red-800 font-medium">⚠️ This action is irreversible!</p>
                                <p className="text-xs text-red-600 mt-2">
                                    This will permanently delete the job and all associated data including notes, files, and delivery information.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-red-600">{assignment?.unique_id}</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="Enter job unique ID"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setDeleteConfirmText('');
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteJob}
                                    disabled={deleteConfirmText !== assignment?.unique_id || isDeletingJob}
                                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isDeletingJob ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <FiTrash2 className="w-4 h-4" />
                                            Delete Permanently
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Designer Modal — liveIsCompleted driven by quote's design_status so subscription updates apply */}
            {(() => {
                // Match the specific job_quotes row — by id (new jobs) or quote_type (old jobs)
                const liveQuote = removeDesignerModal.open
                    ? (assignment?.job_quotes ?? []).find(q =>
                        removeDesignerModal.childQuoteId != null
                            ? q.id === removeDesignerModal.childQuoteId
                            : (removeDesignerModal.quoteType != null
                                ? q.quote_type === removeDesignerModal.quoteType
                                : q.quote_type == null)
                    )
                    : null;
                const liveIsCompleted = liveQuote
                    ? liveQuote.design_status === 'completed'
                    : removeDesignerModal.isCompleted;

                return (
                    <Modal
                        isOpen={removeDesignerModal.open}
                        onClose={() => setRemoveDesignerModal(prev => ({ ...prev, open: false }))}
                        title="Remove Designer"
                        size="sm"
                    >
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            <span className="font-semibold text-gray-900 dark:text-white">{removeDesignerModal.designerName}</span> will
                            be removed from the{' '}
                            <span className="capitalize font-semibold">{removeDesignerModal.quoteType}</span>
                            {removeDesignerModal.childQuoteName && (
                                <> · <span className="font-semibold">{removeDesignerModal.childQuoteName}</span></>
                            )}{' '}
                            quote.
                        </p>

                        {liveIsCompleted && (
                            <label className="flex items-start gap-3 mb-5 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={removeDesignerModal.keepCompleted}
                                    onChange={e => setRemoveDesignerModal(prev => ({ ...prev, keepCompleted: e.target.checked }))}
                                    className="mt-0.5 w-4 h-4 accent-teal-600 flex-shrink-0"
                                />
                                <div>
                                    <span className="text-sm font-medium text-gray-800 dark:text-white">Keep completed state</span>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Uncheck to reset the design status back to "waiting assignment".
                                    </p>
                                </div>
                            </label>
                        )}

                        <div className="flex gap-2">
                            <button
                                onClick={() => setRemoveDesignerModal(prev => ({ ...prev, open: false }))}
                                className="flex-1 px-4 py-2 border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700 text-sm font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemoveDesigner}
                                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                Remove
                            </button>
                        </div>
                    </Modal>
                );
            })()}

            {editOpen && (
                <EditAssignment
                    modalJobId={jobId}
                    modalCompanySlug={companySlug}
                    onClose={(saved) => {
                        setEditOpen(false);
                        if (saved) fetchAssignment();
                    }}
                />
            )}

            <XeroInvoicePdfModal
                open={xeroPdfModal.open}
                onClose={closeXeroPdfModal}
                title={xeroPdfModal.title}
                pdfUrl={xeroPdfModal.pdfUrl}
                loading={xeroPdfModal.loading}
                error={xeroPdfModal.error}
            />

            <AssignmentConversation
                mode="modal"
                isOpen={showConversationModal}
                onClose={() => setShowConversationModal(false)}
                assignment={assignment}
                company={company}
            />

            {/* Confirm Remove Driver Modal */}
            <Modal
                isOpen={confirmRemoveDriverModal.open}
                onClose={() => setConfirmRemoveDriverModal(prev => ({ ...prev, open: false }))}
                title="Remove Driver"
                size="sm"
            >
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
                    Are you sure you want to remove this driver from the delivery?
                </p>
                <div className="flex gap-2">
                    <button
                        onClick={() => setConfirmRemoveDriverModal(prev => ({ ...prev, open: false }))}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700 text-sm font-medium transition-colors [touch-action:manipulation]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleRemoveDriverConfirmed}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors [touch-action:manipulation]"
                    >
                        Remove
                    </button>
                </div>
            </Modal>

            {/* Confirm End Delivery Modal */}
            <Modal
                isOpen={confirmEndDeliveryModal.open}
                onClose={() => setConfirmEndDeliveryModal(prev => ({ ...prev, open: false }))}
                title="Complete Delivery"
                size="sm"
            >
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
                    Are you sure you want to mark this delivery as completed? This action cannot be undone.
                </p>
                <div className="flex gap-2">
                    <button
                        onClick={() => setConfirmEndDeliveryModal(prev => ({ ...prev, open: false }))}
                        onTouchEnd={(e) => { e.preventDefault(); setConfirmEndDeliveryModal(prev => ({ ...prev, open: false })) }}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700 text-sm font-medium transition-colors [touch-action:manipulation]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleEndDeliveryConfirmed}
                        onTouchEnd={(e) => { e.preventDefault(); handleEndDeliveryConfirmed(); }}
                        className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-sm font-medium transition-colors [touch-action:manipulation]"
                    >
                        Complete Delivery
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default SingleAssignment;
